import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2, ChevronDown, Clock3, FileSearch, FolderTree, LayoutGrid, PanelRightOpen, ShieldAlert, X } from 'lucide-react';
import styled from 'styled-components';
import BaseCard from '../../../../components/Card';
import {
  applyPlannerRun,
  fetchPlannerAttentionCounts,
  fetchPlannerAttentionEvents,
  fetchPlannerAttentionItems,
  fetchPlannerProgramRecurringSeries,
  fetchPlannerProgramRuns,
  fetchPlannerProgramSelections,
  fetchPlannerPrograms,
  fetchPlannerMonthlyUsage,
  fetchExistingClientPlannerRunHistory,
  fetchOfferingPlaybooks,
  fetchPlannerRunComparisonSummary,
  fetchPlannerRun,
  fetchPlannerRunApplyHistory,
  fetchPlannerRunLatestApply,
  fetchPlannerRunReviewHistory,
  fetchPlannerRunSelections,
  fetchPlannerTriggerEvents,
  fetchPlannerTriggers,
  fetchPlanningMeetingSources,
  fetchOnboardingPlannerRunHistory,
  reviewPlannerRun,
  runExistingClientPlannerDryRun,
  runOnboardingPlannerDryRun,
  updatePlannerAttentionItem,
  type AgenticPlanningAttentionCount,
  type AgenticPlanningAttentionEvent,
  type AgenticPlanningAttentionItem,
  type AgenticPlanningApplyExecution,
  type AgenticPlanningProgramSelections,
  type AgenticPlanningProgramSummary,
  type AgenticPlanningProgramType,
  type AgenticPlanningProgramStatus,
  type AgenticPlanningMonthlyUsageSummary,
  type AgenticPlanningRecurringSeriesSummary,
  type AgenticPlanningRunComparisonBaseline,
  type AgenticPlanningRunComparisonSummary,
  type AgenticPlanningDryRunResult,
  type AgenticPlanningRunReviewEvent,
  type AgenticPlanningRunSummary,
  type AgenticPlanningTrigger,
  type AgenticPlanningTriggerEvent,
  type AgenticOfferingPlaybook,
  type AgenticSourceArtifact,
  type AgenticValidationResult,
  type AgenticWriteOperation,
  type AgenticDraftReviewAction,
  type OrganizationAIAgentKickoffSource,
  type OrganizationAIAgentRecurringNoteSource,
  type OrganizationServiceTabCode,
} from './organizationAIAgentApi';
import {
  SERVICE_LABELS,
  buildRunVersionMap,
  buildTeamLabelMap,
  filterByServiceTab,
  formatAttentionItemTypeLabel,
  formatAttentionStatusLabel,
  formatApplyStatusLabel,
  formatDraftReviewStatusLabel,
  formatProgramStatusLabel,
  formatProgramTypeLabel,
  formatTeamLabel,
  formatTeamList,
  getProgramScopeLabel,
  formatPlanClassificationLabel,
  formatPlanningModeLabel,
  getImmediateScopeCounts,
  getApplyEligibilityState,
  getAttentionItemTone,
  getNewerRecurringNoteCountForRun,
  getPlanningHeaderState,
  getPlanningConfidenceState,
  getPlanOutcomeSummary,
  getProgramExecutionSummary,
  getRunSummaryApplyReadiness,
  getRunTriggerSummary,
  getRunPrimaryProgramId,
  getSpecialistInfluenceSummary,
  humanizeComparisonCopy,
  groupProjectsForDisplay,
  getPlanRunDisplayTitle,
  summarizeAttentionCounts,
  toneForProgramStatus,
  toneForApplyStatus,
  toneForDraftReviewStatus,
  serviceCodeMatchesTab,
} from './organizationAIAgentViewModel';

type Props = {
  orgId: string;
  orgName: string;
  ownerEmail: string | null;
  primaryClientUid: string | null;
  serviceTab: OrganizationServiceTabCode;
  hasActiveServiceContract: boolean;
  serviceContract: {
    id: string;
    serviceCode: string | null;
    serviceName: string | null;
    offerings: Array<{
      serviceContractId: string;
      serviceCode: string | null;
      serviceName: string | null;
      offeringCode: string;
      offeringName: string;
    }>;
  } | null;
  projectCount: number;
  onOpenMeeting?: (meetingId: string) => void;
};

type PlansViewMode = 'list' | 'detail';
type DrawerTabId = 'trace' | 'inputs' | 'context' | 'provenance' | 'taxonomy' | 'diagnostics';
type ProgramTypeFilter = 'all' | AgenticPlanningProgramType;
type ProgramStatusFilter = 'all' | AgenticPlanningProgramStatus;
type ProgramSortOrder = 'updated' | 'oldest' | 'most_versions' | 'most_attention';
type InputExplorerSectionKey = 'summary' | 'meetings' | 'artifacts' | 'scope';
type Tone = 'neutral' | 'good' | 'warn' | 'danger' | 'info';
type DashboardMetricData = {
  label: string;
  value: number | null;
  valueLabel?: string;
  meta: string;
  delta?: number | null;
};
type HeroActivityPoint = {
  key: string;
  label: string;
  fullLabel: string;
  totalRuns: number;
  draftRuns: number;
  publishedRuns: number;
};

type ProgramCardData = {
  program: AgenticPlanningProgramSummary;
  latestRun: AgenticPlanningRunSummary | null;
  latestApprovedRun: AgenticPlanningRunSummary | null;
  currentDraftRun: AgenticPlanningRunSummary | null;
  latestApply: AgenticPlanningApplyExecution | null;
  attentionCount: number;
  newerRecurringNoteCount: number;
  versionCount: number;
};

type SelectedVersionPhaseKey =
  | 'overview'
  | 'inputs'
  | 'discovery'
  | 'roadmap'
  | 'review_apply';

type VersionMenuOption = {
  value: string;
  label: string;
  meta?: string | null;
};

type PlanningGroupKey =
  | 'projects'
  | 'milestones'
  | 'taxonomy_kbase'
  | 'collaborations';

type PlanningExplorerGroup = {
  key: PlanningGroupKey;
  title: string;
  summary: string;
};

type RoadmapProjectSummary = {
  projectKey: string;
  order: number;
  teamLabel: string;
  collaboratorLabel: string | null;
  milestoneCount: number;
  deliverableCount: number;
  ticketCount: number;
  routeLabel: string | null;
};

const PLANNING_EXECUTION_ACTIONS_BY_GROUP: Record<PlanningGroupKey, AgenticWriteOperation['action'][]> = {
  projects: ['create_project', 'update_project', 'backlog_addition'],
  milestones: ['create_milestone', 'create_ticket', 'update_ticket'],
  taxonomy_kbase: ['create_subcategory', 'create_kbase_process'],
  collaborations: [],
};

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(224, 161, 94, 0.1), transparent 34%),
    linear-gradient(180deg, rgba(9, 12, 17, 0.98), rgba(7, 10, 14, 0.96));
`;

const Header = styled.div`
  padding: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  gap: 14px;
`;

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeaderRow = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) minmax(420px, 520px);
  align-items: stretch;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderAside = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const HeroChartSection = styled.div`
  display: grid;
  gap: 8px;
  align-content: start;
  min-width: 0;
`;

const HeroChartTopline = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const HeroChartControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const HeroChartLabel = styled.div`
  color: #dfe6f0;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: -0.01em;
`;

const HeroChartRange = styled.div`
  color: #7f8a9a;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroChartToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const HeroChartToggleButton = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255,255,255,0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.1)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#f3d2ad' : '#8f9aae')};
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 0.58rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 160ms ease, color 160ms ease, background 160ms ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.28);
    color: #f5f7fb;
  }

  &:disabled {
    cursor: default;
    opacity: 1;
  }
`;

const HeroChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  align-items: center;
`;

const HeroChartLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #8f9aae;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroChartLegendSwatch = styled.span<{ $color: string; $line?: boolean }>`
  display: inline-block;
  width: ${({ $line }) => ($line ? '14px' : '8px')};
  height: ${({ $line }) => ($line ? '2px' : '8px')};
  border-radius: ${({ $line }) => ($line ? '999px' : '3px')};
  background: ${({ $color }) => $color};
  opacity: 0.95;
`;

const HeroChartFrame = styled.div`
  width: 100%;
  min-height: 92px;
`;

const HeroChartEmpty = styled.div`
  min-height: 92px;
  display: grid;
  place-items: center;
  color: #8f9aae;
  font-size: 0.74rem;
  font-weight: 700;
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  color: #f5f7fb;
  font-size: clamp(1.35rem, 2vw, 1.9rem);
  font-weight: 900;
  letter-spacing: -0.02em;
`;

const HeaderMetaLine = styled.div`
  color: #8f9aae;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 700;
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled(BaseCard)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: visible;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const Grid = styled.div<{ $cols?: string }>`
  display: grid;
  gap: 12px;
  grid-template-columns: ${({ $cols = 'repeat(3, minmax(0, 1fr))' }) => $cols};

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
  padding: 12px;
  display: grid;
  gap: 8px;
`;

const HeroAttentionList = styled.div`
  display: grid;
  gap: 8px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  align-content: start;
`;

const HeroAttentionItemCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  min-width: 0;
`;

const HeroAttentionItemButton = styled.button`
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  color: inherit;
  border-radius: 10px;
  padding: 10px 12px;
  display: grid;
  gap: 4px;
  cursor: pointer;

  &:hover {
    background: rgba(224, 161, 94, 0.08);
  }
`;

const HeroAttentionItemTitle = styled.div`
  color: #eef2f8;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 900;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const HeroAttentionItemDescription = styled.div`
  color: #9aa5b6;
  font-size: 11px;
  line-height: 1.4;
  font-weight: 700;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ExplorerShell = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(190px, 220px) minmax(0, 1fr);
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const ExplorerList = styled.div`
  display: grid;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
`;

const ExplorerListButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  text-align: left;
  display: grid;
  gap: 4px;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255,255,255,0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.08)' : 'transparent')};
  padding: 10px 12px;
  color: inherit;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.22);
    background: rgba(255, 255, 255, 0.03);
    transform: translateY(-1px);
  }
`;

const ExplorerListLabel = styled.div`
  color: #f5f7fb;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.4;
`;

const ExplorerListMeta = styled.div`
  color: #8f9aae;
  font-size: 11px;
  line-height: 1.5;
  font-weight: 700;
`;

const ExplorerDetail = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const ExplorerDetailScroll = styled.div`
  display: grid;
  gap: 12px;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;
`;

const ExplorerDivider = styled.div`
  border-top: 1px dashed rgba(255,255,255,.12);
  padding-top: 12px;
`;

const RoadmapShell = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const RoadmapSidebar = styled.div`
  display: grid;
  gap: 10px;
  align-content: start;
  min-width: 0;
`;

const RoadmapSidebarTitle = styled.div`
  color: #eef2f8;
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const RoadmapSidebarHint = styled.div`
  color: #8f9aae;
  font-size: 0.74rem;
  line-height: 1.5;
  font-weight: 700;
`;

const RoadmapProjectRail = styled.div`
  display: grid;
  gap: 6px;
  max-height: 620px;
  overflow-y: auto;
  padding-right: 4px;
`;

const RoadmapProjectButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.32)' : 'rgba(255,255,255,0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.08)' : 'rgba(255,255,255,0.015)')};
  color: inherit;
  text-align: left;
  padding: 10px 12px;
  border-radius: 12px;
  display: grid;
  gap: 4px;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.24);
    background: rgba(255,255,255,0.03);
  }
`;

const RoadmapProjectOrderBadge = styled.div<{ $active?: boolean }>`
  width: fit-content;
  min-width: 26px;
  min-height: 26px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.4)' : 'rgba(255,255,255,0.1)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.18)' : 'rgba(255,255,255,0.04)')};
  color: ${({ $active }) => ($active ? '#fff2dd' : '#d5dbe5')};
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const RoadmapProjectTitle = styled.div`
  color: #f5f7fb;
  font-size: 0.8rem;
  line-height: 1.4;
  font-weight: 900;
`;

const RoadmapProjectTeam = styled.div`
  color: #8f9aae;
  font-size: 0.72rem;
  line-height: 1.45;
  font-weight: 700;
`;

const RoadmapMain = styled.div`
  display: grid;
  gap: 16px;
  min-width: 0;
`;

const RoadmapTopline = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const RoadmapHeaderBlock = styled.div`
  display: grid;
  gap: 6px;
  min-width: 0;
  flex: 1 1 420px;
`;

const RoadmapHeadline = styled.div`
  color: #f5f7fb;
  font-size: clamp(1.2rem, 2vw, 1.55rem);
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: -0.02em;
`;

const RoadmapSubtitle = styled.div`
  color: #cfd6e2;
  font-size: 0.9rem;
  line-height: 1.65;
  font-weight: 700;
  max-width: 900px;
`;

const RoadmapOverviewButton = styled.button`
  width: fit-content;
  border: 0;
  padding: 0;
  background: transparent;
  color: #8bbcff;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: #c9ddff;
  }
`;

const RoadmapMetaGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const RoadmapMetaCell = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const RoadmapMetaLabel = styled.div`
  color: #8f9aae;
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const RoadmapMetaValue = styled.div`
  color: #eef2f8;
  font-size: 0.82rem;
  line-height: 1.55;
  font-weight: 800;
`;

const RoadmapDivider = styled.div`
  border-top: 1px solid rgba(255,255,255,0.08);
`;

const RoadmapSectionBlock = styled.section`
  display: grid;
  gap: 10px;
`;

const RoadmapSectionTitle = styled.div`
  color: #f5f7fb;
  font-size: 0.88rem;
  line-height: 1.4;
  font-weight: 900;
`;

const RoadmapSectionBody = styled.div`
  color: #cfd6e2;
  font-size: 0.88rem;
  line-height: 1.7;
  font-weight: 700;
`;

const RoadmapNoteList = styled.div`
  display: grid;
  gap: 8px;
`;

const RoadmapNoteItem = styled.div`
  border-left: 2px solid rgba(224, 161, 94, 0.38);
  padding-left: 12px;
  color: #d8dee8;
  font-size: 0.83rem;
  line-height: 1.65;
  font-weight: 700;
`;

const RoadmapMilestoneList = styled.div`
  display: grid;
  gap: 14px;
`;

const RoadmapMilestoneBlock = styled.div`
  display: grid;
  gap: 8px;
  padding-left: 14px;
  border-left: 2px solid rgba(255,255,255,0.08);
`;

const RoadmapMilestoneTitle = styled.div`
  color: #eef2f8;
  font-size: 0.86rem;
  line-height: 1.45;
  font-weight: 900;
`;

const RoadmapMilestoneOutcome = styled.div`
  color: #cfd6e2;
  font-size: 0.82rem;
  line-height: 1.6;
  font-weight: 700;
`;

const RoadmapSublist = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
  color: #d0d7e2;
  font-size: 0.8rem;
  line-height: 1.6;
  font-weight: 700;
`;

const RoadmapTicketList = styled.div`
  display: grid;
  gap: 0;
`;

const RoadmapTicketItem = styled.div`
  display: grid;
  gap: 4px;
  padding: 12px 0;
  border-top: 1px dashed rgba(255,255,255,0.1);

  &:first-child {
    border-top: 0;
    padding-top: 0;
  }
`;

const RoadmapTicketTitle = styled.div`
  color: #f5f7fb;
  font-size: 0.84rem;
  line-height: 1.45;
  font-weight: 900;
`;

const RoadmapTicketMeta = styled.div`
  color: #8f9aae;
  font-size: 0.74rem;
  line-height: 1.55;
  font-weight: 700;
`;

const RoadmapMetric = styled.div`
  display: grid;
  gap: 8px;
  justify-items: center;
  align-content: start;
  min-width: 120px;
`;

const RoadmapMetricRing = styled.div<{ $percent: number; $muted?: boolean }>`
  width: 104px;
  height: 104px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background:
    ${({ $percent, $muted }) => ($muted
      ? 'rgba(255,255,255,0.05)'
      : `conic-gradient(rgba(224,161,94,0.95) 0deg ${Math.max(0, Math.min(360, $percent * 3.6))}deg, rgba(255,255,255,0.08) ${Math.max(0, Math.min(360, $percent * 3.6))}deg 360deg)`)};
  border: 1px solid rgba(255,255,255,0.08);
`;

const RoadmapMetricRingInner = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 999px;
  background: rgba(9, 12, 17, 0.98);
  border: 1px solid rgba(255,255,255,0.08);
  display: grid;
  place-items: center;
  gap: 2px;
`;

const RoadmapMetricValue = styled.div`
  color: #f5f7fb;
  font-size: 1.05rem;
  font-weight: 900;
  line-height: 1;
`;

const RoadmapMetricSubvalue = styled.div`
  color: #8f9aae;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const RoadmapMetricLabel = styled.div`
  color: #eef2f8;
  font-size: 0.74rem;
  line-height: 1.45;
  font-weight: 800;
  text-align: center;
`;

const RoadmapMetricNote = styled.div`
  color: #8f9aae;
  font-size: 0.68rem;
  line-height: 1.5;
  font-weight: 700;
  text-align: center;
  max-width: 180px;
`;

const ScrollSurface = styled.div`
  max-height: 240px;
  overflow-y: auto;
  padding-right: 4px;
`;

const AccordionToggle = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
  display: grid;
  gap: 10px;
  cursor: pointer;
  text-align: left;
`;

const Eyebrow = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#7dd7ab'
        : $tone === 'warn'
          ? '#f0bf7c'
          : $tone === 'danger'
            ? '#ff9d9d'
            : $tone === 'info'
              ? '#8bbcff'
              : '#c8d0dc'};
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const MetaItem = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

const UsageMetricsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(5, minmax(0, 1fr));

  @media (max-width: 1320px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const UsageMetricItem = styled(MetaItem)`
  justify-items: start;
`;

const MetaLabel = styled.div`
  color: #8f9aae;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const MetaValue = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#f5f7fb'
        : $tone === 'warn'
          ? '#f6d6aa'
          : $tone === 'danger'
            ? '#ffb4b4'
            : $tone === 'info'
              ? '#d9e8ff'
              : '#d0d7e2'};
  font-size: 0.82rem;
  line-height: 1.5;
  font-weight: 800;
`;

const TextList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
  color: #d0d7e2;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 700;
`;

const TextListItem = styled.li`
  margin: 0;
`;

const EmptyText = styled.div`
  color: #9aa5b6;
  font-size: 12px;
  font-weight: 700;
`;

const TextArea = styled.textarea`
  min-height: 88px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.03);
  color: #eef2f8;
  padding: 12px;
  resize: vertical;
  font: inherit;

  &::placeholder {
    color: #7f8a9d;
  }
`;

const Button = styled.button<{ $primary?: boolean }>`
  min-height: 42px;
  padding: 0 14px;
  width: fit-content;
  border-radius: 12px;
  border: 1px solid ${({ $primary }) => ($primary ? 'rgba(224, 161, 94, 0.45)' : 'rgba(255,255,255,.14)')};
  background: ${({ $primary }) => ($primary ? 'rgba(224, 161, 94, 0.15)' : 'rgba(255,255,255,.04)')};
  color: #eef2f8;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  gap: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
  }
`;
const HeaderInlineActionButton = styled(Button)`
  min-height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #e0a15e;
  background: rgba(255,255,255,.03);
  color: #e8eaed;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
  gap: 6px;

  &:hover:not(:disabled) {
    border-color: #f0c490;
    background: rgba(224,161,94,.08);
  }
`;

const ProgramStarterBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(3, 6, 10, 0.72);
  backdrop-filter: blur(14px);
  z-index: 1200;
`;

const ProgramStarterShell = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1201;
  display: grid;
  place-items: center;
  padding: 16px;
  pointer-events: none;
`;

const ProgramStarterDialog = styled(motion.div)`
  width: min(920px, calc(100vw - 32px));
  max-height: min(720px, calc(100vh - 32px));
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  background: rgba(10, 13, 18, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  overflow: hidden;
  pointer-events: auto;
`;

const ProgramStarterHeader = styled.div`
  padding: 18px 18px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ProgramStarterBody = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ProgramStarterRail = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
  display: grid;
  gap: 8px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 760px) {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const ProgramStarterOfferingButton = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.35)' : 'rgba(255,255,255,.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.12)' : 'rgba(255,255,255,.02)')};
  color: #eef2f8;
  display: grid;
  gap: 4px;
  text-align: left;
  cursor: pointer;
`;

const ProgramStarterDetail = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 16px;
`;

const ProgramStarterFooter = styled.div`
  padding: 14px 18px 18px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const PlansToolbar = styled.div`
  display: grid;
  gap: 10px;
`;

const PlansControlGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(220px, 1.8fr) repeat(3, minmax(0, 1fr));

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const PlansField = styled.label`
  display: grid;
  gap: 6px;
`;

const PlansFieldLabel = styled.span`
  color: #8f9aae;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const PlansInput = styled.input`
  min-height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: #eef2f8;
  padding: 0 12px;
  font-size: 0.82rem;
  font-weight: 700;

  &::placeholder {
    color: #7f8999;
  }

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, 0.5);
    background: rgba(255, 255, 255, 0.045);
  }
`;

const PlansSelect = styled.select`
  min-height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: #eef2f8;
  padding: 0 12px;
  font-size: 0.82rem;
  font-weight: 700;
  color-scheme: dark;

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, 0.5);
    background: rgba(255, 255, 255, 0.045);
  }
`;

const VersionMenuButton = styled.button<{ $open?: boolean }>`
  width: 100%;
  min-height: 40px;
  border-radius: 12px;
  border: 1px solid ${({ $open }) => ($open ? 'rgba(224, 161, 94, 0.5)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${({ $open }) => ($open ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.03)')};
  color: #eef2f8;
  padding: 0 12px;
  font-size: 0.82rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.56);
    background: rgba(255, 255, 255, 0.05);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.72;
  }
`;

const VersionMenuButtonText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VersionMenuBackdrop = styled.button`
  position: fixed;
  inset: 0;
  z-index: 1148;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: default;
`;

const VersionMenuPopover = styled(motion.div)`
  position: fixed;
  z-index: 1149;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.98), rgba(18, 20, 25, 0.98));
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
  color-scheme: dark;
`;

const VersionMenuOptionButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'transparent')};
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.1)' : 'transparent')};
  color: inherit;
  padding: 10px 12px;
  display: grid;
  gap: 4px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const VersionMenuOptionLabel = styled.div`
  color: #f5f7fb;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 900;
`;

const VersionMenuOptionMeta = styled.div`
  color: #9aa5b6;
  font-size: 10px;
  line-height: 1.45;
  font-weight: 800;
`;

const InitiativeCardsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;

  @media (max-width: 1220px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const InitiativeCardButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: 14px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224,161,94,.36)' : 'rgba(255,255,255,.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224,161,94,.10)' : 'rgba(255,255,255,.025)')};
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  min-height: 100%;
  cursor: pointer;
  color: inherit;
`;

const InitiativeCardHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: flex-start;
`;

const InitiativeCardHeaderText = styled.div`
  display: grid;
  gap: 6px;
  min-width: 0;
`;

const InitiativeCardMetaLine = styled.div`
  color: #8f9aae;
  font-size: 11px;
  line-height: 1.5;
  font-weight: 800;
`;

const InitiativeCardTitle = styled.div`
  color: #f5f7fb;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InitiativeCardStatusStack = styled.div`
  display: grid;
  gap: 3px;
  justify-items: end;
  text-align: right;
  min-width: max-content;
  align-self: start;
`;

const InitiativeCardPrimaryStatus = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#f5f7fb'
        : $tone === 'warn'
          ? '#f6d6aa'
          : $tone === 'danger'
            ? '#ffb4b4'
            : $tone === 'info'
              ? '#d9e8ff'
              : '#d0d7e2'};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const InitiativeCardSecondaryStatus = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#7dd7ab'
        : $tone === 'warn'
          ? '#f0bf7c'
          : $tone === 'danger'
            ? '#ff9d9d'
            : $tone === 'info'
              ? '#8bbcff'
              : '#8f9aae'};
  font-size: 10px;
  font-weight: 800;
  line-height: 1.4;
`;

const InitiativeCardStatusMeta = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#7dd7ab'
        : $tone === 'warn'
          ? '#f0bf7c'
          : $tone === 'danger'
            ? '#ff9d9d'
            : $tone === 'info'
              ? '#8bbcff'
              : '#9aa5b6'};
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
`;

const DashboardSection = styled.div`
  display: grid;
  gap: 16px;
  overflow: visible;
`;

const DashboardControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const DashboardTopGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.9fr);
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const DashboardColumnStack = styled.div`
  display: grid;
  gap: 14px;
  min-width: 0;
`;

const DashboardSummaryBlock = styled.div`
  display: grid;
  gap: 10px;
  min-width: 0;
`;

const DashboardTitle = styled.div`
  color: #f5f7fb;
  font-size: 20px;
  line-height: 1.35;
  font-weight: 900;
  letter-spacing: -0.02em;
`;

const DashboardNarrative = styled.div`
  color: #d8e0ea;
  font-size: 13px;
  line-height: 1.7;
  font-weight: 700;
`;

const DashboardMetaStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
`;

const DashboardMetaText = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#7dd7ab'
        : $tone === 'warn'
          ? '#f0bf7c'
          : $tone === 'danger'
            ? '#ff9d9d'
            : $tone === 'info'
              ? '#8bbcff'
              : '#9aa5b6'};
  font-size: 11px;
  line-height: 1.5;
  font-weight: 800;
`;

const DashboardStatusRail = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DashboardStatusPanel = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(7, 10, 14, 0.28);
  padding: 12px;
  display: grid;
  gap: 8px;
`;

const DashboardStatusGroup = styled.div`
  display: grid;
  gap: 2px;
`;

const DashboardStatusLabel = styled.div`
  color: #8f9aae;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const DashboardStatusValue = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#f5f7fb'
        : $tone === 'warn'
          ? '#f6d6aa'
          : $tone === 'danger'
            ? '#ffb4b4'
            : $tone === 'info'
              ? '#d9e8ff'
              : '#d0d7e2'};
  font-size: 14px;
  line-height: 1.45;
  font-weight: 900;
`;

const DashboardStatusSubvalue = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#7dd7ab'
        : $tone === 'warn'
          ? '#f0bf7c'
          : $tone === 'danger'
            ? '#ff9d9d'
            : $tone === 'info'
              ? '#8bbcff'
              : '#9aa5b6'};
  font-size: 11px;
  line-height: 1.45;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const DashboardStatusNote = styled.div<{ $tone?: Tone }>`
  color:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? '#7dd7ab'
        : $tone === 'warn'
          ? '#f0bf7c'
          : $tone === 'danger'
            ? '#ff9d9d'
            : $tone === 'info'
              ? '#8bbcff'
              : '#9aa5b6'};
  font-size: 11px;
  line-height: 1.5;
  font-weight: 800;
`;

const OverviewMetricGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const DashboardMetricCard = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  padding: 12px;
  display: grid;
  align-content: start;
  gap: 6px;
  min-height: 104px;
  height: 100%;
`;

const DashboardMetricTopline = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const DashboardMetricValue = styled.div`
  color: #f5f7fb;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 900;
`;

const DashboardMetricMeta = styled.div`
  color: #9aa5b6;
  font-size: 11px;
  line-height: 1.5;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DashboardMetricDelta = styled.div<{ $direction: 'increase' | 'decrease' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color:
    ${({ $direction }) =>
      $direction === 'increase'
        ? '#658963'
        : $direction === 'decrease'
          ? '#a64c44'
          : '#8f9aae'};
  font-size: 10px;
  line-height: 1.4;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const DashboardMetricDeltaBar = styled.span<{ $direction: 'increase' | 'decrease' | 'neutral' }>`
  display: inline-block;
  width: 16px;
  height: 3px;
  border-radius: 999px;
  background:
    ${({ $direction }) =>
      $direction === 'increase'
        ? '#658963'
        : $direction === 'decrease'
          ? '#a64c44'
        : 'rgba(255,255,255,0.16)'};
`;

const SimpleChipGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SimpleDashedChip = styled.div`
  border-radius: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.22);
  background: transparent;
  padding: 9px 10px;
  color: #d8e0ea;
  font-size: 11px;
  line-height: 1.45;
  font-weight: 800;
  min-width: 0;
`;

const PhaseStepperSection = styled.div`
  display: grid;
  gap: 12px;
`;

const PhaseStepperList = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(5, minmax(0, 1fr));

  @media (max-width: 1080px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 680px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

const PhaseStepButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  text-align: left;
  display: grid;
  gap: 6px;
  border-radius: 14px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.36)' : 'rgba(255, 255, 255, 0.08)')};
  background:
    ${({ $active }) =>
      $active
        ? 'linear-gradient(180deg, rgba(224, 161, 94, 0.14), rgba(224, 161, 94, 0.06))'
        : 'rgba(255, 255, 255, 0.02)'};
  color: inherit;
  padding: 12px;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.22);
    background: rgba(255, 255, 255, 0.035);
    transform: translateY(-1px);
  }
`;

const PhaseStepIndex = styled.div<{ $active?: boolean }>`
  color: ${({ $active }) => ($active ? '#f0bf7c' : '#7f8a9a')};
  font-size: 0.6rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const PhaseStepLabel = styled.div`
  color: #f5f7fb;
  font-size: 0.88rem;
  line-height: 1.35;
  font-weight: 900;
`;

const PhaseStepSummary = styled.div<{ $active?: boolean }>`
  color: ${({ $active }) => ($active ? '#d8e0ea' : '#9aa5b6')};
  font-size: 0.72rem;
  line-height: 1.55;
  font-weight: 800;
`;

const DrawerBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  z-index: 1200;
`;

const Drawer = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  height: 100dvh;
  @supports not (height: 100dvh) {
    height: 100vh;
  }
  width: clamp(340px, 92vw, 620px);
  z-index: 1201;
  background: linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96));
  color: #e8eaed;
  border-left: 1px solid rgba(255,255,255,.08);
  box-shadow: -18px 0 48px rgba(0,0,0,.45);
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
  overflow: hidden;
  color-scheme: dark;
`;

const DrawerHeader = styled.div`
  padding: 12px;
  display: grid;
  gap: 10px;
  border-bottom: 1px solid rgba(255,255,255,.08);
`;

const DrawerHeaderBar = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  padding-right: 46px;
`;

const DrawerTitle = styled.div`
  color: #f5f7fb;
  font-size: 16px;
  font-weight: 900;
`;

const DrawerSubtitle = styled.div`
  color: #9aa5b6;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 700;
`;

const DrawerMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  color: #cbd2e1;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.4;
`;

const DrawerCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
  color: #cbd2e1;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,.08);
  }
`;

const DrawerTabBar = styled.div`
  padding: 0 12px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  display: flex;
  gap: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
`;

const DrawerTabButton = styled.button<{ $active: boolean }>`
  position: relative;
  flex: 0 0 auto;
  min-height: 38px;
  padding: 0 14px;
  border: 0;
  background: transparent;
  color: ${({ $active }) => ($active ? '#f3f4f6' : '#9aa4b2')};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    left: 14px;
    right: 14px;
    bottom: -1px;
    height: 2px;
    border-radius: 999px;
    background: ${({ $active }) => ($active ? 'rgba(224,161,94,.92)' : 'transparent')};
  }
`;

const DrawerBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 16px;
  display: grid;
  gap: 12px;
  align-content: start;
`;

function text(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function formatDateTime(value: string | null | undefined) {
  const normalized = text(value);
  if (!normalized) return '--';
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatCompactDate(value: string | null | undefined) {
  const normalized = text(value);
  if (!normalized) return '--';
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatNumber(value: number | null | undefined) {
  return new Intl.NumberFormat().format(value ?? 0);
}

function formatUsdAmount(value: number | null | undefined) {
  const amount = Number(value ?? 0);
  const maximumFractionDigits = amount >= 1 ? 2 : amount > 0 ? 4 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(amount);
}

function formatPercent(value: number | null | undefined) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  return `${Math.round(value * 100)}%`;
}

function truncate(value: string | null | undefined, limit = 220) {
  const normalized = text(value);
  if (!normalized) return '--';
  return normalized.length > limit ? `${normalized.slice(0, limit).trim()}...` : normalized;
}

function humanizeToken(value: string | null | undefined) {
  const normalized = text(value);
  if (!normalized) return '--';
  return normalized.replace(/_/g, ' ');
}

function getPlanningActionProgressLabel(actionLabel: string | null | undefined) {
  const normalized = text(actionLabel).toLowerCase();
  if (normalized === 'start program') return 'Starting Program...';
  if (normalized === 'update program') return 'Updating Program...';
  return 'Working...';
}

const BUSINESS_PROFILE_FIELD_ORDER = ['businessName', 'name', 'website', 'contactName', 'contactEmail', 'businessOverview', 'notes'] as const;

const BUSINESS_PROFILE_FIELD_LABELS: Record<(typeof BUSINESS_PROFILE_FIELD_ORDER)[number], string> = {
  businessName: 'Business name',
  name: 'Organization name',
  website: 'Website',
  contactName: 'Primary contact',
  contactEmail: 'Contact email',
  businessOverview: 'Business overview',
  notes: 'Notes',
};

function getArtifactStructuredFields(artifact: AgenticSourceArtifact) {
  if (!artifact.structuredFields || typeof artifact.structuredFields !== 'object') {
    return {} as Record<string, string>;
  }
  return Object.fromEntries(
    Object.entries(artifact.structuredFields)
      .map(([key, value]) => [key, text(value)])
      .filter(([, value]) => value.length > 0),
  ) as Record<string, string>;
}

function getArtifactTypeLabel(artifact: AgenticSourceArtifact) {
  if (artifact.artifactType === 'client_profile') {
    return 'Business profile';
  }
  return humanizeToken(artifact.artifactType);
}

function getArtifactTitle(artifact: AgenticSourceArtifact) {
  if (artifact.artifactType === 'client_profile') {
    const fields = getArtifactStructuredFields(artifact);
    return fields.businessName || fields.name || 'Business profile';
  }
  return artifact.title || artifact.sourceRef;
}

function getArtifactFieldEntries(artifact: AgenticSourceArtifact) {
  if (artifact.artifactType !== 'client_profile') {
    return [] as Array<{ key: (typeof BUSINESS_PROFILE_FIELD_ORDER)[number]; label: string; value: string }>;
  }
  const fields = getArtifactStructuredFields(artifact);
  return BUSINESS_PROFILE_FIELD_ORDER.reduce<Array<{ key: (typeof BUSINESS_PROFILE_FIELD_ORDER)[number]; label: string; value: string }>>((entries, key) => {
      const value = fields[key];
      if (!value) return entries;
      entries.push({
        key,
        label: BUSINESS_PROFILE_FIELD_LABELS[key],
        value,
      });
      return entries;
    }, []);
}

function renderArtifactPreview(artifact: AgenticSourceArtifact) {
  const fieldEntries = getArtifactFieldEntries(artifact);
  if (!fieldEntries.length) {
    return (
      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.7, fontWeight: 700 }}>
        {truncate(artifact.normalizedText, 320)}
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {fieldEntries.map((entry) => (
        <div key={`${artifact.artifactId}:${entry.key}`} style={{ display: 'grid', gap: 2 }}>
          <div style={{ color: '#8f9aae', fontSize: 10, lineHeight: 1.4, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {entry.label}
          </div>
          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.7, fontWeight: 700 }}>
            {entry.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function startOfMonth(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), 1);
}

function addMonths(value: Date, offset: number) {
  return new Date(value.getFullYear(), value.getMonth() + offset, 1);
}

function addDays(value: Date, offset: number) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate() + offset);
}

function buildMonthKey(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabelWithYear(value: Date) {
  return value.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function formatMonthButtonLabel(value: Date) {
  return value.toLocaleDateString(undefined, { month: 'long' });
}

function formatDayRangeLabel(start: Date, endExclusive: Date) {
  const end = addDays(endExclusive, -1);
  return end.getDate() > start.getDate() ? `${start.getDate()}-${end.getDate()}` : `${start.getDate()}`;
}

function formatDayRangeLabelWithMonth(start: Date, endExclusive: Date) {
  const end = addDays(endExclusive, -1);
  if (end.getMonth() !== start.getMonth()) {
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}-${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  if (end.getDate() > start.getDate()) {
    return `${start.toLocaleDateString(undefined, { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }
  return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function getRunTimestamp(value: string | null | undefined) {
  const normalized = text(value);
  if (!normalized) return 0;
  const parsed = new Date(normalized).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function filterAndSortInitiatives(
  cards: ProgramCardData[],
  options: {
    query?: string;
    typeFilter?: ProgramTypeFilter;
    statusFilter?: ProgramStatusFilter;
    sortOrder?: ProgramSortOrder;
  },
) {
  const normalizedQuery = text(options.query).toLowerCase();
  const typeFilter = options.typeFilter ?? 'all';
  const statusFilter = options.statusFilter ?? 'all';
  const sortOrder = options.sortOrder ?? 'updated';

  const filtered = cards.filter(({ program, latestRun }) => {
    if (typeFilter !== 'all' && program.programType !== typeFilter) return false;
    if (statusFilter !== 'all' && program.status !== statusFilter) return false;
    if (!normalizedQuery) return true;
    const searchText = [
      program.title,
      program.objective,
      program.primaryServiceTitle,
      program.primaryServiceCode,
      program.primaryLaneKey,
      program.primaryScopeLabel,
      program.primaryScopeCode,
      latestRun?.summaryText,
      latestRun?.planClassification,
    ]
      .map((value) => text(value).toLowerCase())
      .filter(Boolean)
      .join(' ');
    return searchText.includes(normalizedQuery);
  });

  return [...filtered].sort((left, right) => {
    if (sortOrder === 'oldest') {
      return getRunTimestamp(left.program.createdAt) - getRunTimestamp(right.program.createdAt);
    }
    if (sortOrder === 'most_versions') {
      return right.versionCount - left.versionCount
        || getRunTimestamp(right.program.updatedAt) - getRunTimestamp(left.program.updatedAt);
    }
    if (sortOrder === 'most_attention') {
      return right.attentionCount - left.attentionCount
        || right.newerRecurringNoteCount - left.newerRecurringNoteCount
        || getRunTimestamp(right.program.updatedAt) - getRunTimestamp(left.program.updatedAt);
    }
    return getRunTimestamp(right.program.updatedAt) - getRunTimestamp(left.program.updatedAt);
  });
}

function getProgramServiceFamilyLabel(program: AgenticPlanningProgramSummary) {
  return text(program.primaryServiceTitle)
    || humanizeToken(program.primaryServiceCode)
    || 'Service scope pending';
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildServiceFamilyRegex(serviceFamilyLabel: string, options?: { requireSuffix?: boolean }) {
  const labelTokens = text(serviceFamilyLabel)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[/_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((value) => value.trim())
    .filter(Boolean);
  if (!labelTokens.length) {
    return null;
  }
  const joinedTokens = labelTokens.map(escapeRegex).join('(?:\\s|&|and|/|-|_)+');
  return new RegExp(
    options?.requireSuffix
      ? `^\\s*${joinedTokens}(?:\\s|:|-|/|_)+`
      : `^\\s*${joinedTokens}\\s*$`,
    'i',
  );
}

function getFallbackInitiativeCardTitle(anchorRun: AgenticPlanningRunSummary | null) {
  if (!anchorRun) return 'Planning workstream';
  const modePrefix = anchorRun.mode === 'onboarding_initial_build' ? 'Onboarding' : 'Recurring';
  if (anchorRun.planClassification === 'review_required' || anchorRun.planClassification === 'insufficient_input_review') {
    return `${modePrefix} Review and Clarification`;
  }
  if (anchorRun.planClassification === 'discovery_first') {
    return `${modePrefix} Discovery Follow-up`;
  }
  if (anchorRun.planClassification === 'full_build') {
    return `${modePrefix} Build Plan`;
  }
  if (anchorRun.planClassification === 'update_existing_project') {
    return modePrefix === 'Onboarding' ? 'Onboarding Existing Work Update' : 'Existing Work Update';
  }
  if (anchorRun.planClassification === 'create_new_project') {
    return modePrefix === 'Onboarding' ? 'Onboarding New Work Plan' : 'Net-new Work Plan';
  }
  if (anchorRun.planClassification === 'backlog_only') {
    return 'Backlog Follow-up';
  }
  if (anchorRun.planClassification === 'no_change') {
    return `${modePrefix} No-change Check`;
  }
  return `${modePrefix} Planning Workstream`;
}

function getProgramDisplayTitle(
  program: AgenticPlanningProgramSummary,
  serviceFamilyLabel: string,
  anchorRun: AgenticPlanningRunSummary | null,
) {
  const rawTitle = text(program.title);
  if (!rawTitle) return getFallbackInitiativeCardTitle(anchorRun);
  const fullServiceMatch = buildServiceFamilyRegex(serviceFamilyLabel);
  if (fullServiceMatch?.test(rawTitle)) {
    return getFallbackInitiativeCardTitle(anchorRun);
  }
  const servicePrefix = buildServiceFamilyRegex(serviceFamilyLabel, { requireSuffix: true });
  const trimmedTitle = servicePrefix ? rawTitle.replace(servicePrefix, '').trim() : rawTitle;
  if (!trimmedTitle) return getFallbackInitiativeCardTitle(anchorRun);
  return trimmedTitle;
}

function toneForPlanMode(mode: string | null | undefined): Tone {
  if (mode === 'full_build') return 'good';
  if (mode === 'discovery_first') return 'warn';
  if (mode === 'insufficient_input_review') return 'danger';
  if (mode === 'update_existing_project' || mode === 'create_new_project') return 'good';
  if (mode === 'backlog_only' || mode === 'review_required') return 'warn';
  if (mode === 'no_change') return 'info';
  return 'neutral';
}

function toneForCoverage(status: string | null | undefined): Tone {
  if (status === 'metadata_driven') return 'good';
  if (status === 'fallback') return 'warn';
  if (status === 'unresolved') return 'danger';
  return 'neutral';
}

function validationTone(results: AgenticValidationResult[]) {
  if (results.some((entry) => entry.status === 'fail')) return 'danger';
  if (results.some((entry) => entry.status === 'warn')) return 'warn';
  return 'good';
}

function renderTextList(values: string[], emptyText = 'No items') {
  if (!values.length) return <EmptyText>{emptyText}</EmptyText>;
  return (
    <TextList>
      {values.map((entry, index) => (
        <TextListItem key={`${entry}:${index}`}>{entry}</TextListItem>
      ))}
    </TextList>
  );
}

const DRAWER_TABS: Array<{ id: DrawerTabId; label: string }> = [
  { id: 'trace', label: 'Trace' },
  { id: 'inputs', label: 'Raw Context' },
  { id: 'context', label: 'Scope' },
  { id: 'provenance', label: 'Provenance' },
  { id: 'taxonomy', label: 'Taxonomy + KBase' },
  { id: 'diagnostics', label: 'Diagnostics' },
];

function HeroActivityChart({
  loading,
  points,
  reduceMotion,
  monthLabel,
  previousMonthLabel,
  nextMonthLabel,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
}: {
  loading: boolean;
  points: HeroActivityPoint[];
  reduceMotion: boolean | null | undefined;
  monthLabel: string;
  previousMonthLabel: string;
  nextMonthLabel: string;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const width = 560;
  const height = 96;
  const padding = { top: 6, right: 8, bottom: 18, left: 4 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maxValue = Math.max(1, ...points.map((entry) => Math.max(entry.totalRuns, entry.draftRuns, entry.publishedRuns)));
  const groupWidth = points.length ? plotWidth / points.length : plotWidth;
  const barWidth = Math.min(10, Math.max(6, groupWidth * 0.18));
  const barGap = Math.min(4, Math.max(2, groupWidth * 0.06));
  const tickLines = [0.25, 0.5, 0.75];
  const linePath = points.map((entry, index) => {
    const x = padding.left + groupWidth * index + groupWidth / 2;
    const y = padding.top + plotHeight - (entry.totalRuns / maxValue) * plotHeight;
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  return (
    <HeroChartSection>
      <HeroChartTopline>
        <HeroChartLabel>Recent planning activity</HeroChartLabel>
        <HeroChartControls>
          <HeroChartRange>{monthLabel}</HeroChartRange>
          <HeroChartToggle>
            <HeroChartToggleButton type="button" onClick={onPrevious} disabled={!canGoPrevious}>
              {previousMonthLabel}
            </HeroChartToggleButton>
            <HeroChartToggleButton type="button" $active={!canGoNext} onClick={onNext} disabled={!canGoNext}>
              {nextMonthLabel}
            </HeroChartToggleButton>
          </HeroChartToggle>
        </HeroChartControls>
      </HeroChartTopline>
      <HeroChartLegend aria-hidden="true">
        <HeroChartLegendItem>
          <HeroChartLegendSwatch $color="#e0a15e" $line />
          Runs
        </HeroChartLegendItem>
        <HeroChartLegendItem>
          <HeroChartLegendSwatch $color="#969696" />
          Drafts
        </HeroChartLegendItem>
        <HeroChartLegendItem>
          <HeroChartLegendSwatch $color="#658963" />
          Published
        </HeroChartLegendItem>
      </HeroChartLegend>
      <HeroChartFrame>
        {loading ? (
          <HeroChartEmpty>Loading recent planner activity...</HeroChartEmpty>
        ) : !points.length || points.every((entry) => entry.totalRuns === 0) ? (
          <HeroChartEmpty>No saved planner runs in {monthLabel}.</HeroChartEmpty>
        ) : (
          <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" role="img" aria-label="Planner activity chart">
            {tickLines.map((tick) => {
              const y = padding.top + plotHeight * tick;
              return (
                <line
                  key={`tick-${tick}`}
                  x1={padding.left}
                  x2={width - padding.right}
                  y1={y}
                  y2={y}
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth="1"
                />
              );
            })}
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={padding.top + plotHeight}
              y2={padding.top + plotHeight}
              stroke="rgba(255,255,255,0.14)"
              strokeWidth="1"
            />

            {points.map((entry, index) => {
              const xCenter = padding.left + groupWidth * index + groupWidth / 2;
              const draftHeight = (entry.draftRuns / maxValue) * plotHeight;
              const publishedHeight = (entry.publishedRuns / maxValue) * plotHeight;
              const draftX = xCenter - barWidth - barGap / 2;
              const publishedX = xCenter + barGap / 2;
              const baseY = padding.top + plotHeight;
              return (
                <React.Fragment key={entry.key}>
                  <title>{`${entry.fullLabel}: ${entry.totalRuns} runs, ${entry.draftRuns} drafts, ${entry.publishedRuns} published`}</title>
                  <motion.rect
                    x={draftX}
                    width={barWidth}
                    rx={3}
                    fill="#969696"
                    initial={reduceMotion ? false : { y: baseY, height: 0 }}
                    animate={{ y: baseY - draftHeight, height: draftHeight }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 0.46, ease: [0.22, 1, 0.36, 1], delay: index * 0.04 }}
                  />
                  <motion.rect
                    x={publishedX}
                    width={barWidth}
                    rx={3}
                    fill="#658963"
                    initial={reduceMotion ? false : { y: baseY, height: 0 }}
                    animate={{ y: baseY - publishedHeight, height: publishedHeight }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 0.46, ease: [0.22, 1, 0.36, 1], delay: index * 0.04 + 0.03 }}
                  />
                  <text
                    x={xCenter}
                    y={height - 3}
                    textAnchor="middle"
                    fill="#7f8a9a"
                    fontSize="8"
                    fontWeight="800"
                    letterSpacing=".08em"
                  >
                    {entry.label}
                  </text>
                </React.Fragment>
              );
            })}

            <motion.path
              d={linePath}
              fill="none"
              stroke="#e0a15e"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduceMotion ? false : { pathLength: 0, opacity: 0.85 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.92, ease: 'easeOut' }}
            />
            {points.map((entry, index) => {
              const x = padding.left + groupWidth * index + groupWidth / 2;
              const y = padding.top + plotHeight - (entry.totalRuns / maxValue) * plotHeight;
              return (
                <motion.circle
                  key={`dot-${entry.key}`}
                  cx={x}
                  cy={y}
                  r={2.8}
                  fill="#e0a15e"
                  initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={reduceMotion ? { duration: 0 } : { duration: 0.24, delay: 0.42 + index * 0.04 }}
                />
              );
            })}
          </svg>
        )}
      </HeroChartFrame>
    </HeroChartSection>
  );
}

function VersionMenuSelect({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string;
  options: VersionMenuOption[];
  onChange: (nextValue: string) => void;
  disabled?: boolean;
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const selectedOption = options.find((entry) => entry.value === value) ?? null;

  const updateMenuPosition = React.useCallback(() => {
    if (!buttonRef.current || typeof window === 'undefined') return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const preferredWidth = Math.max(rect.width, 280);
    const width = Math.min(preferredWidth, window.innerWidth - (viewportPadding * 2));
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
    );
    const availableBelow = Math.max(120, window.innerHeight - rect.bottom - viewportPadding);
    const availableAbove = Math.max(120, rect.top - viewportPadding);
    const preferredHeight = Math.min(320, Math.max(160, options.length * 60));
    const openAbove = availableBelow < Math.min(220, preferredHeight) && availableAbove > availableBelow;
    const maxHeight = Math.max(
      120,
      Math.min(preferredHeight, (openAbove ? availableAbove : availableBelow) - 8),
    );
    const top = openAbove
      ? Math.max(viewportPadding, rect.top - maxHeight - 8)
      : Math.min(window.innerHeight - viewportPadding - maxHeight, rect.bottom + 8);

    setMenuStyle({ top, left, width, maxHeight });
  }, [options.length]);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const handleViewportChange = () => updateMenuPosition();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    setOpen(false);
  }, [value]);

  return (
    <>
      <VersionMenuButton
        ref={buttonRef}
        type="button"
        $open={open}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled && options.length) setOpen((current) => !current);
        }}
        disabled={disabled || !options.length}
      >
        <VersionMenuButtonText>{selectedOption?.label ?? 'Select a version'}</VersionMenuButtonText>
        <ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease' }} />
      </VersionMenuButton>
      {open && menuStyle && typeof document !== 'undefined'
        ? createPortal(
          <AnimatePresence>
            <VersionMenuBackdrop
              type="button"
              aria-label="Close version menu"
              onClick={() => setOpen(false)}
            />
            <VersionMenuPopover
              role="listbox"
              aria-label="Select Program version"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              style={{
                top: menuStyle.top,
                left: menuStyle.left,
                width: menuStyle.width,
                maxHeight: menuStyle.maxHeight,
              }}
            >
              {options.map((entry) => (
                <VersionMenuOptionButton
                  key={`version-menu-option:${entry.value}`}
                  type="button"
                  role="option"
                  aria-selected={entry.value === value}
                  $active={entry.value === value}
                  onClick={() => {
                    onChange(entry.value);
                    setOpen(false);
                  }}
                >
                  <VersionMenuOptionLabel>{entry.label}</VersionMenuOptionLabel>
                  {entry.meta ? <VersionMenuOptionMeta>{entry.meta}</VersionMenuOptionMeta> : null}
                </VersionMenuOptionButton>
              ))}
            </VersionMenuPopover>
          </AnimatePresence>,
          document.body,
        )
        : null}
    </>
  );
}

function PlannerDrawer({
  open,
  tab,
  onClose,
  onSelectTab,
  result,
  reviewHistory,
  latestApply,
  applyHistory,
  trigger,
  triggerHistory,
  triggerEvents,
  triggerEventsLoading,
  attentionItems,
  attentionEventsByItemId,
  attentionEventsLoading,
  attentionActionLoading,
  onAttentionAction,
  teamLabelMap,
  serviceTab,
}: {
  open: boolean;
  tab: DrawerTabId;
  onClose: () => void;
  onSelectTab: (tabId: DrawerTabId) => void;
  result: AgenticPlanningDryRunResult | null;
  reviewHistory: AgenticPlanningRunReviewEvent[];
  latestApply: AgenticPlanningApplyExecution | null;
  applyHistory: AgenticPlanningApplyExecution[];
  trigger: AgenticPlanningTrigger | null;
  triggerHistory: AgenticPlanningTrigger[];
  triggerEvents: AgenticPlanningTriggerEvent[];
  triggerEventsLoading: boolean;
  attentionItems: AgenticPlanningAttentionItem[];
  attentionEventsByItemId: Record<string, AgenticPlanningAttentionEvent[]>;
  attentionEventsLoading: boolean;
  attentionActionLoading: string | null;
  onAttentionAction: (item: AgenticPlanningAttentionItem, action: 'resolve' | 'dismiss') => void;
  teamLabelMap: Map<string, string>;
  serviceTab: OrganizationServiceTabCode;
}) {
  if (!result) return null;
  const purchasedLanes = filterByServiceTab(result.planGraph.boundaryMetadata?.purchasedLanes ?? [], serviceTab, (lane) => lane.serviceCode);
  const executionLanes = filterByServiceTab(result.planGraph.boundaryMetadata?.executionLanes ?? [], serviceTab, (lane) => lane.serviceCode);
  const executionLaneMap = new Map(executionLanes.map((lane) => [lane.laneKey, lane]));
  const subcategoryDecisions = filterByServiceTab(result.planGraph.boundaryMetadata?.subcategoryDecisions ?? [], serviceTab, (entry) => entry.serviceCode);
  const kbProcessDecisions = filterByServiceTab(result.planGraph.boundaryMetadata?.kbProcessDecisions ?? [], serviceTab, (entry) => entry.serviceCode);
  const assignmentIssues = result.teamAssignmentResults.filter((entry) => entry.resolutionStatus !== 'assigned' || entry.requiresManualApproval);
  const validationIssues = result.validationResults.filter((entry) => entry.status !== 'pass');
  const inputSufficiency = result.planGraph.modeMetadata.inputSufficiency;
  const planningConfidenceState = getPlanningConfidenceState(result);
  const recentTriggers = [...triggerHistory]
    .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
    .slice(0, 8);

  let body: React.ReactNode = null;

  if (tab === 'trace') {
    body = (
      <>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Run trace</div>
          <div style={{ color: '#c8d0dc', fontSize: 13, lineHeight: 1.6, fontWeight: 700 }}>
            Run ID {result.runState.runId} | Status {result.runState.status} | Updated {formatDateTime(result.runState.updatedAt)}
          </div>
          <MetaRow>
            <MetaItem>
              <MetaLabel>Draft review</MetaLabel>
              <MetaValue $tone={toneForDraftReviewStatus(result.runSummary.draftReview.reviewStatus)}>
                {formatDraftReviewStatusLabel(result.runSummary.draftReview.reviewStatus)}
              </MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Latest apply</MetaLabel>
              <MetaValue $tone={toneForApplyStatus(latestApply?.status)}>
                {latestApply ? formatApplyStatusLabel(latestApply.status) : 'Not started'}
              </MetaValue>
            </MetaItem>
          </MetaRow>
          <div>
            <MetaLabel>Trace references</MetaLabel>
            {renderTextList(result.runState.traceRefs, 'No trace references returned.')}
          </div>
        </Card>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Planner decisions</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {result.planningDecisions.length ? result.planningDecisions.map((decision) => (
              <Card key={decision.decisionId}>
                <MetaRow>
                  <MetaItem>
                    <MetaLabel>Decision type</MetaLabel>
                    <MetaValue $tone={decision.requiresManualApproval ? 'warn' : 'info'}>{humanizeToken(decision.decisionType)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Review state</MetaLabel>
                    <MetaValue $tone={decision.requiresManualApproval ? 'warn' : 'good'}>
                      {decision.requiresManualApproval ? 'Review required' : 'No review gate'}
                    </MetaValue>
                  </MetaItem>
                </MetaRow>
                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{decision.subjectKey}</div>
                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{decision.rationale}</div>
              </Card>
            )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No planner decisions were returned.</div>}
          </div>
        </Card>
      </>
    );
  } else if (tab === 'inputs') {
    body = (
      <>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Source context</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {result.sourceArtifactBundle.artifacts.map((artifact) => (
              <Card key={artifact.artifactId}>
                <MetaRow>
                  <MetaItem>
                    <MetaLabel>Artifact type</MetaLabel>
                    <MetaValue $tone="info">{getArtifactTypeLabel(artifact)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Source system</MetaLabel>
                    <MetaValue>{artifact.sourceSystem}</MetaValue>
                  </MetaItem>
                  {artifact.meetingType ? (
                    <MetaItem>
                      <MetaLabel>Meeting type</MetaLabel>
                      <MetaValue>{artifact.meetingType}</MetaValue>
                    </MetaItem>
                  ) : null}
                </MetaRow>
                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{getArtifactTitle(artifact)}</div>
                <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700 }}>Captured {formatDateTime(artifact.capturedAt)}</div>
                {renderArtifactPreview(artifact)}
              </Card>
            ))}
          </div>
        </Card>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Context diagnostics</div>
          <MetaRow>
            <MetaItem>
              <MetaLabel>Planner outcome</MetaLabel>
              <MetaValue $tone={toneForPlanMode(inputSufficiency?.planMode)}>
                {formatPlanClassificationLabel(inputSufficiency?.planMode)}
              </MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Context status</MetaLabel>
              <MetaValue>{inputSufficiency?.status || '--'}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Planning confidence</MetaLabel>
              <MetaValue $tone={planningConfidenceState.tone}>{formatPercent(result.planGraph.modeMetadata.confidence)}</MetaValue>
            </MetaItem>
          </MetaRow>
          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
            {[inputSufficiency?.rationale || 'No additional context-diagnostic rationale was returned.', planningConfidenceState.summary].join(' ')}
          </div>
          <Grid $cols="repeat(2, minmax(0, 1fr))">
            <Card>
              <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Required signals present</div>
              {renderTextList(inputSufficiency?.requiredSignalsPresent ?? [], 'No required signals were captured.')}
            </Card>
            <Card>
              <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Required signals missing</div>
              {renderTextList(inputSufficiency?.requiredSignalsMissing.length ? inputSufficiency.requiredSignalsMissing : inputSufficiency?.missingSignals || [], 'No required signals are currently missing.')}
            </Card>
          </Grid>
        </Card>
      </>
    );
  } else if (tab === 'context') {
    body = (
      <>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Purchased service lanes</div>
          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
            Purchased service context defines what this version is allowed to plan against. Lane provenance and ownership live here so the main Program workspace stays focused on review and decisions.
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {purchasedLanes.length ? purchasedLanes.map((lane) => {
              const executionLane = executionLaneMap.get(lane.laneKey);
              const laneOfferings = lane.selectedOfferings.length
                ? lane.selectedOfferings
                : lane.selectedOfferingCodes.length
                  ? lane.selectedOfferingCodes
                  : [];
              return (
                <Card key={lane.laneKey}>
                  <MetaRow>
                    <MetaItem>
                      <MetaLabel>Coverage</MetaLabel>
                      <MetaValue $tone={toneForCoverage(lane.coverageStatus)}>{humanizeToken(lane.coverageStatus)}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                      <MetaLabel>Ownership path</MetaLabel>
                      <MetaValue $tone={lane.fallbackUsed ? 'warn' : 'good'}>
                        {lane.fallbackUsed ? 'Fallback used' : 'Metadata driven'}
                      </MetaValue>
                    </MetaItem>
                    {lane.deliveryPattern ? (
                      <MetaItem>
                        <MetaLabel>Delivery pattern</MetaLabel>
                        <MetaValue>{humanizeToken(lane.deliveryPattern)}</MetaValue>
                      </MetaItem>
                    ) : null}
                  </MetaRow>
                  <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>{lane.serviceTitle || lane.laneKey}</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <MetaLabel>Offerings</MetaLabel>
                    {laneOfferings.length ? (
                      <SimpleChipGrid>
                        {laneOfferings.map((offering) => (
                          <SimpleDashedChip key={`${lane.laneKey}:${offering}`}>{offering}</SimpleDashedChip>
                        ))}
                      </SimpleChipGrid>
                    ) : (
                      <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700 }}>No offering breakdown was returned for this lane.</div>
                    )}
                  </div>
                  <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                    Service team {formatTeamLabel(lane.primaryTeamNodeId, teamLabelMap)} | Supporting execution teams {formatTeamList(executionLane?.supportingTeamNodeIds ?? [], teamLabelMap).join(', ')}
                  </div>
                  <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700 }}>
                    Ownership {lane.ownershipSource || '--'} | Planning {lane.planningSource || '--'}
                  </div>
                  {lane.missingOwnershipFields.length ? (
                    <div>
                      <MetaLabel>Missing ownership fields</MetaLabel>
                      {renderTextList(lane.missingOwnershipFields)}
                    </div>
                  ) : null}
                </Card>
              );
            }) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No purchased lanes returned for this view.</div>}
          </div>
        </Card>
      </>
    );
  } else if (tab === 'provenance') {
    body = (
      <>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Trigger provenance</div>
          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
            {trigger
              ? `${getRunTriggerSummary(result.runSummary, trigger)} | ${humanizeToken(trigger.status)}`
              : 'This draft was started manually from the planning workspace.'}
          </div>
          {trigger ? (
            <MetaRow>
              <MetaItem>
                <MetaLabel>Trigger reason</MetaLabel>
                <MetaValue>{humanizeToken(trigger.triggerReason)}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Source updated</MetaLabel>
                <MetaValue>{formatDateTime(trigger.sourceUpdatedAt)}</MetaValue>
              </MetaItem>
            </MetaRow>
          ) : null}
          <div style={{ display: 'grid', gap: 8 }}>
            <MetaLabel>Trigger events</MetaLabel>
            {triggerEventsLoading ? (
              <EmptyText>Loading trigger events...</EmptyText>
            ) : triggerEvents.length ? triggerEvents.map((event) => (
              <Card key={event.eventId}>
                <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>
                  {humanizeToken(event.eventType)} | {humanizeToken(event.status)}
                </div>
                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                  {event.message || event.reasonCode || 'No event message recorded.'}
                </div>
                <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700 }}>{formatDateTime(event.createdAt)}</div>
              </Card>
            )) : <EmptyText>No trigger events recorded.</EmptyText>}
          </div>
        </Card>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Recent planner triggers</div>
          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
            Recent org-level trigger history gives context around other runs, dedupe behavior, and newer note activity beyond this selected version.
          </div>
          {recentTriggers.length ? (
            <ScrollSurface>
              <div style={{ display: 'grid', gap: 10 }}>
                {recentTriggers.map((entry) => (
                  <Card key={`recent-trigger:${entry.triggerId}`}>
                    <MetaRow>
                      <MetaItem>
                        <MetaLabel>Status</MetaLabel>
                        <MetaValue $tone={entry.status === 'succeeded' ? 'good' : entry.status === 'failed' ? 'danger' : entry.status === 'running' ? 'warn' : 'info'}>
                          {humanizeToken(entry.status)}
                        </MetaValue>
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Mode</MetaLabel>
                        <MetaValue>{formatPlanningModeLabel(entry.candidateMode)}</MetaValue>
                      </MetaItem>
                    </MetaRow>
                    <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>
                      {humanizeToken(entry.triggerReason)} | {humanizeToken(entry.sourceKind)}
                      {entry.sourceMeetingType ? ` | ${humanizeToken(entry.sourceMeetingType)}` : ''}
                    </div>
                    <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                      {entry.latestMessage || entry.latestReasonCode || 'No trigger summary was recorded.'}
                    </div>
                    <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700 }}>
                      {entry.triggerId === trigger?.triggerId ? 'Selected run trigger' : entry.linkedRunId ? `Linked run ${entry.linkedRunId.slice(0, 8)}` : 'No linked run'} | Updated {formatDateTime(entry.updatedAt)}
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollSurface>
          ) : (
            <EmptyText>No recent planner triggers were returned.</EmptyText>
          )}
        </Card>
      </>
    );
  } else if (tab === 'taxonomy') {
    body = (
      <>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Taxonomy decisions</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {subcategoryDecisions.length ? subcategoryDecisions.map((entry) => (
              <Card key={entry.decisionKey}>
                <MetaRow>
                  <MetaItem>
                    <MetaLabel>Status</MetaLabel>
                    <MetaValue $tone={entry.reviewRequired ? 'warn' : entry.status === 'ready_for_creation' ? 'info' : 'good'}>
                      {humanizeToken(entry.status)}
                    </MetaValue>
                  </MetaItem>
                  {entry.pairedOperationId ? (
                    <MetaItem>
                      <MetaLabel>Expansion pairing</MetaLabel>
                      <MetaValue $tone="info">Paired taxonomy and KBase expansion</MetaValue>
                    </MetaItem>
                  ) : null}
                </MetaRow>
                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{entry.inferredSubcategoryName}</div>
                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{entry.rationale}</div>
              </Card>
            )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No taxonomy decisions returned.</div>}
          </div>
        </Card>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>KBase decisions</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {kbProcessDecisions.length ? kbProcessDecisions.map((entry) => (
              <Card key={entry.decisionKey}>
                <MetaRow>
                  <MetaItem>
                    <MetaLabel>Status</MetaLabel>
                    <MetaValue $tone={entry.reviewRequired ? 'warn' : entry.status === 'ready_for_creation' ? 'info' : entry.status === 'reuse_existing' ? 'good' : 'neutral'}>
                      {humanizeToken(entry.status)}
                    </MetaValue>
                  </MetaItem>
                  {entry.pairedOperationId ? (
                    <MetaItem>
                      <MetaLabel>Expansion pairing</MetaLabel>
                      <MetaValue $tone="info">Paired taxonomy and KBase expansion</MetaValue>
                    </MetaItem>
                  ) : null}
                </MetaRow>
                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{entry.processTitle}</div>
                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{entry.summary || entry.rationale}</div>
              </Card>
            )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No KBase decisions returned.</div>}
          </div>
        </Card>
      </>
    );
  } else {
    body = (
      <>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Validation diagnostics</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {validationIssues.length ? validationIssues.map((entry) => (
              <Card key={`${entry.ruleName}:${entry.message}`}>
                <MetaRow>
                  <MetaItem>
                    <MetaLabel>Status</MetaLabel>
                    <MetaValue $tone={entry.status === 'fail' ? 'danger' : 'warn'}>{humanizeToken(entry.status)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Severity</MetaLabel>
                    <MetaValue $tone={entry.severity === 'high' ? 'danger' : entry.severity === 'medium' ? 'warn' : 'info'}>{entry.severity}</MetaValue>
                  </MetaItem>
                </MetaRow>
                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{entry.ruleName}</div>
                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{entry.message}</div>
              </Card>
            )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No validation warnings returned.</div>}
          </div>
        </Card>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Assignment diagnostics</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {assignmentIssues.length ? assignmentIssues.map((entry) => (
              <Card key={`${entry.entityType}:${entry.entityKey}`}>
                <MetaRow>
                  <MetaItem>
                    <MetaLabel>Resolution</MetaLabel>
                    <MetaValue $tone={entry.resolutionStatus === 'unresolved' ? 'danger' : 'warn'}>{humanizeToken(entry.resolutionStatus)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Review</MetaLabel>
                    <MetaValue $tone={entry.requiresManualApproval ? 'warn' : 'good'}>
                      {entry.requiresManualApproval ? 'Review required' : 'No review gate'}
                    </MetaValue>
                  </MetaItem>
                </MetaRow>
                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{entry.entityType} | {entry.entityKey}</div>
                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                  Primary {formatTeamLabel(entry.primaryTeamNodeId, teamLabelMap)} | Supporting {formatTeamList(entry.supportingTeamNodeIds, teamLabelMap).join(', ')}
                </div>
              </Card>
            )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No unresolved team assignments returned.</div>}
          </div>
        </Card>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Review timeline</div>
          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
            Full review history for this selected version, including actor, state transition, and any reviewer note that was persisted.
          </div>
          {reviewHistory.length ? (
            <ScrollSurface>
              <div style={{ display: 'grid', gap: 10 }}>
                {[...reviewHistory]
                  .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
                  .map((event) => (
                    <Card key={event.reviewEventId}>
                      <MetaRow>
                        <MetaItem>
                          <MetaLabel>Action</MetaLabel>
                          <MetaValue>{humanizeToken(event.action)}</MetaValue>
                        </MetaItem>
                        <MetaItem>
                          <MetaLabel>Status</MetaLabel>
                          <MetaValue $tone={toneForDraftReviewStatus(event.toReviewStatus)}>
                            {formatDraftReviewStatusLabel(event.toReviewStatus)}
                          </MetaValue>
                        </MetaItem>
                      </MetaRow>
                      <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>
                        {`${formatDraftReviewStatusLabel(event.fromReviewStatus)} -> ${formatDraftReviewStatusLabel(event.toReviewStatus)}`}
                      </div>
                      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                        {event.note || event.reasonCode || 'No review note was recorded.'}
                      </div>
                      <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700 }}>
                        {event.actedByDisplayName || event.actedByUserId || 'System'} | {formatDateTime(event.createdAt)}
                      </div>
                    </Card>
                  ))}
              </div>
            </ScrollSurface>
          ) : (
            <EmptyText>No review timeline exists for this version yet.</EmptyText>
          )}
        </Card>
        <Card>
          <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Apply attempts</div>
          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
            Every persisted apply attempt for this version, including outcome counts and per-operation execution detail.
          </div>
          {applyHistory.length ? (
            <ScrollSurface>
              <div style={{ display: 'grid', gap: 10 }}>
                {[...applyHistory]
                  .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
                  .map((apply) => (
                    <Card key={apply.applyId}>
                      <MetaRow>
                        <MetaItem>
                          <MetaLabel>Attempt</MetaLabel>
                          <MetaValue>#{apply.attemptNumber}</MetaValue>
                        </MetaItem>
                        <MetaItem>
                          <MetaLabel>Status</MetaLabel>
                          <MetaValue $tone={toneForApplyStatus(apply.status)}>
                            {formatApplyStatusLabel(apply.status)}
                          </MetaValue>
                        </MetaItem>
                        <MetaItem>
                          <MetaLabel>Completed</MetaLabel>
                          <MetaValue>{apply.completedAt ? formatDateTime(apply.completedAt) : 'In progress'}</MetaValue>
                        </MetaItem>
                      </MetaRow>
                      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                        {apply.errorSummary || `${formatNumber(apply.succeededOperationCount)} succeeded | ${formatNumber(apply.failedOperationCount)} failed | ${formatNumber(apply.skippedOperationCount)} skipped | ${formatNumber(apply.deferredOperationCount)} deferred`}
                      </div>
                      {apply.operations.length ? (
                        <div style={{ display: 'grid', gap: 8 }}>
                          <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                            Operations
                          </div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            {apply.operations.map((operation) => (
                              <div
                                key={operation.applyOperationId}
                                style={{
                                  display: 'grid',
                                  gap: 4,
                                  paddingTop: 8,
                                  borderTop: '1px dashed rgba(255,255,255,.12)',
                                }}
                              >
                                <MetaRow>
                                  <MetaItem>
                                    <MetaLabel>Action</MetaLabel>
                                    <MetaValue>{humanizeToken(operation.action)}</MetaValue>
                                  </MetaItem>
                                  <MetaItem>
                                    <MetaLabel>Status</MetaLabel>
                                    <MetaValue $tone={toneForApplyStatus(operation.status === 'failed' ? 'failed' : operation.status === 'running' ? 'running' : operation.status === 'pending' ? 'pending' : operation.status === 'deferred' || operation.status === 'skipped' ? 'partially_failed' : 'succeeded')}>
                                      {humanizeToken(operation.status)}
                                    </MetaValue>
                                  </MetaItem>
                                </MetaRow>
                                <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{operation.entityKey}</div>
                                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                  {operation.errorMessage || operation.notes[0] || 'No operation note recorded.'}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </Card>
                  ))}
              </div>
            </ScrollSurface>
          ) : (
            <EmptyText>No apply attempts exist for this version yet.</EmptyText>
          )}
        </Card>
        {attentionItems.length ? (
          <Card>
            <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Attention queue items</div>
            <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
              Run-scoped attention items can be resolved or dismissed here after the operator reviews the version.
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {attentionItems.map((item) => {
                const tone = getAttentionItemTone(item);
                const events = attentionEventsByItemId[item.attentionItemId] ?? [];
                return (
                  <Card key={item.attentionItemId}>
                    <MetaRow>
                      <MetaItem>
                        <MetaLabel>Type</MetaLabel>
                        <MetaValue $tone={tone}>{formatAttentionItemTypeLabel(item.itemType)}</MetaValue>
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Status</MetaLabel>
                        <MetaValue>{formatAttentionStatusLabel(item.status)}</MetaValue>
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Priority</MetaLabel>
                        <MetaValue>{humanizeToken(item.priority)}</MetaValue>
                      </MetaItem>
                    </MetaRow>
                    <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{item.reasonSummary}</div>
                    <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                      {item.sourceSummary || 'No additional source summary was recorded.'}
                    </div>
                    {item.status === 'open' ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <Button
                          type="button"
                          onClick={() => onAttentionAction(item, 'resolve')}
                          disabled={attentionActionLoading !== null}
                        >
                          {attentionActionLoading === `${item.attentionItemId}:resolve` ? 'Resolving...' : 'Resolve'}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => onAttentionAction(item, 'dismiss')}
                          disabled={attentionActionLoading !== null}
                        >
                          {attentionActionLoading === `${item.attentionItemId}:dismiss` ? 'Dismissing...' : 'Dismiss'}
                        </Button>
                      </div>
                    ) : null}
                    <div style={{ display: 'grid', gap: 6 }}>
                      <MetaLabel>Attention events</MetaLabel>
                      {attentionEventsLoading && !events.length ? (
                        <EmptyText>Loading attention events...</EmptyText>
                      ) : events.length ? (
                        <div style={{ display: 'grid', gap: 6 }}>
                          {[...events]
                            .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
                            .map((event) => (
                              <div key={event.attentionEventId} style={{ display: 'grid', gap: 2 }}>
                                <div style={{ color: '#f5f7fb', fontSize: 11, fontWeight: 900 }}>
                                  {humanizeToken(event.eventType)} | {formatAttentionStatusLabel(event.toStatus)}
                                </div>
                                <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                  {event.note || event.reasonCode || 'No attention note was recorded.'}
                                </div>
                                <div style={{ color: '#7f8a9a', fontSize: 10, fontWeight: 700 }}>
                                  {event.actedByDisplayName || event.actedByUserId || 'System'} | {formatDateTime(event.createdAt)}
                                </div>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <EmptyText>No attention events were returned.</EmptyText>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        ) : null}
      </>
    );
  }

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <DrawerBackdrop
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <Drawer
            role="dialog"
            aria-modal="true"
            aria-label="Planner details"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          >
            <DrawerHeader>
              <DrawerHeaderBar>
                <DrawerTitle>Planner details</DrawerTitle>
                <DrawerSubtitle>
                  Secondary context, trace, provenance, taxonomy, and diagnostics for the selected Program version.
                </DrawerSubtitle>
                <DrawerMetaRow>
                  <span>{result.runSummary.mode === 'existing_client_recurring_planning' ? 'Recurring version' : 'Onboarding version'}</span>
                  <span>Run {result.runState.runId.slice(0, 8)}</span>
                  <span>Updated {formatDateTime(result.runState.updatedAt)}</span>
                </DrawerMetaRow>
                <DrawerCloseButton type="button" aria-label="Close planner details" onClick={onClose}>
                  <X size={16} />
                </DrawerCloseButton>
              </DrawerHeaderBar>
            </DrawerHeader>
            <DrawerTabBar>
              {DRAWER_TABS.map((entry) => (
                <DrawerTabButton
                  key={entry.id}
                  type="button"
                  onClick={() => onSelectTab(entry.id)}
                  $active={tab === entry.id}
                >
                  {entry.label}
                </DrawerTabButton>
              ))}
            </DrawerTabBar>
            <DrawerBody>{body}</DrawerBody>
          </Drawer>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

export default function OrganizationAIAgentPanel({
  orgId,
  orgName,
  ownerEmail,
  serviceTab,
  hasActiveServiceContract,
  serviceContract,
  projectCount,
  onOpenMeeting,
}: Props) {
  const reduceMotion = useReducedMotion();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [kickoffSource, setKickoffSource] = useState<OrganizationAIAgentKickoffSource | null>(null);
  const [recurringNoteSources, setRecurringNoteSources] = useState<OrganizationAIAgentRecurringNoteSource[]>([]);
  const [sourceLoading, setSourceLoading] = useState(true);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [history, setHistory] = useState<AgenticPlanningRunSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [programs, setPrograms] = useState<AgenticPlanningProgramSummary[]>([]);
  const [selectedInitiativeId, setSelectedInitiativeId] = useState<string | null>(null);
  const [selectedInitiativeSelections, setSelectedInitiativeSelections] =
    useState<AgenticPlanningProgramSelections | null>(null);
  const [selectedInitiativeRuns, setSelectedInitiativeRuns] = useState<AgenticPlanningRunSummary[]>([]);
  const [, setSelectedInitiativeRecurringSeries] =
    useState<AgenticPlanningRecurringSeriesSummary[]>([]);
  const [latestRunSelection, setLatestRunSelection] = useState<AgenticPlanningRunSummary | null>(null);
  const [currentRunSelection, setCurrentRunSelection] = useState<AgenticPlanningRunSummary | null>(null);
  const [latestApprovedRunSelection, setLatestApprovedRunSelection] = useState<AgenticPlanningRunSummary | null>(null);
  const [triggerHistory, setTriggerHistory] = useState<AgenticPlanningTrigger[]>([]);
  const [attentionItems, setAttentionItems] = useState<AgenticPlanningAttentionItem[]>([]);
  const [attentionCounts, setAttentionCounts] = useState<AgenticPlanningAttentionCount[]>([]);
  const [attentionLoading, setAttentionLoading] = useState(false);
  const [attentionEventsByItemId, setAttentionEventsByItemId] = useState<Record<string, AgenticPlanningAttentionEvent[]>>({});
  const [attentionEventsLoading, setAttentionEventsLoading] = useState(false);
  const [attentionActionLoading, setAttentionActionLoading] = useState<string | null>(null);
  const [monthlyUsage, setMonthlyUsage] = useState<AgenticPlanningMonthlyUsageSummary | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [historyLatestApplyByRunId, setHistoryLatestApplyByRunId] = useState<Record<string, AgenticPlanningApplyExecution | null>>({});
  const [historyApplyLoading, setHistoryApplyLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [plannerResult, setPlannerResult] = useState<AgenticPlanningDryRunResult | null>(null);
  const [cardRunDetailsByRunId, setCardRunDetailsByRunId] = useState<Record<string, AgenticPlanningDryRunResult>>({});
  const [reviewHistory, setReviewHistory] = useState<AgenticPlanningRunReviewEvent[]>([]);
  const [reviewActionLoading, setReviewActionLoading] = useState<AgenticDraftReviewAction | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [showReviewDecisionEditor, setShowReviewDecisionEditor] = useState(false);
  const [reviewApplyOpenPanel, setReviewApplyOpenPanel] = useState<'review' | 'apply'>('review');
  const [latestApplyExecution, setLatestApplyExecution] = useState<AgenticPlanningApplyExecution | null>(null);
  const [applyHistory, setApplyHistory] = useState<AgenticPlanningApplyExecution[]>([]);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyActionLoading, setApplyActionLoading] = useState(false);
  const [selectedTriggerEvents, setSelectedTriggerEvents] = useState<AgenticPlanningTriggerEvent[]>([]);
  const [triggerEventsLoading, setTriggerEventsLoading] = useState(false);
  const [comparisonSummary, setComparisonSummary] = useState<AgenticPlanningRunComparisonSummary | null>(null);
  const [, setComparisonLoading] = useState(false);
  const [comparisonBaseline, setComparisonBaseline] = useState<AgenticPlanningRunComparisonBaseline>('previous');
  const [selectedVersionPhase, setSelectedVersionPhase] = useState<SelectedVersionPhaseKey>('overview');
  const [selectedInputSection, setSelectedInputSection] = useState<InputExplorerSectionKey>('summary');
  const [expandedInputMeetingId, setExpandedInputMeetingId] = useState<string | null>(null);
  const [selectedPlanningGroupKey] = useState<PlanningGroupKey | null>(null);
  const [selectedRoadmapProjectKey, setSelectedRoadmapProjectKey] = useState<string | null>(null);
  const [pendingPlanStartLabel, setPendingPlanStartLabel] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTabId>('trace');
  const [programStarterOpen, setProgramStarterOpen] = useState(false);
  const [programStarterOfferingCode, setProgramStarterOfferingCode] = useState<string | null>(null);
  const [offeringPlaybooksByCode, setOfferingPlaybooksByCode] = useState<Record<string, AgenticOfferingPlaybook>>({});
  const [offeringPlaybooksLoading, setOfferingPlaybooksLoading] = useState(false);
  const [offeringPlaybooksError, setOfferingPlaybooksError] = useState<string | null>(null);
  const [plansView, setPlansView] = useState<PlansViewMode>('list');
  const [chartMonthOffset, setChartMonthOffset] = useState(0);
  const [initiativeSearchQuery, setInitiativeSearchQuery] = useState('');
  const [initiativeTypeFilter, setInitiativeTypeFilter] = useState<ProgramTypeFilter>('all');
  const [initiativeStatusFilter, setInitiativeStatusFilter] = useState<ProgramStatusFilter>('all');
  const [initiativeSortOrder, setInitiativeSortOrder] = useState<ProgramSortOrder>('updated');
  const cardRunDetailLoadingRef = useRef(new Set<string>());
  const cardRunDetailFailedRef = useRef(new Set<string>());

  const loadWorkspaceState = React.useCallback(async (): Promise<{
    programs: AgenticPlanningProgramSummary[];
    history: AgenticPlanningRunSummary[];
    historyError: string | null;
    selections: {
      latestRun: AgenticPlanningRunSummary | null;
      currentRun: AgenticPlanningRunSummary | null;
      latestApprovedRun: AgenticPlanningRunSummary | null;
    };
    triggers: AgenticPlanningTrigger[];
    attentionItems: AgenticPlanningAttentionItem[];
    attentionCounts: AgenticPlanningAttentionCount[];
    monthlyUsage: AgenticPlanningMonthlyUsageSummary | null;
  }> => {
    const [programList, onboardingHistory, recurringHistory, runSelections, triggers, nextAttentionItems, nextAttentionCounts, nextMonthlyUsage] = await Promise.allSettled([
      fetchPlannerPrograms({ orgId, limit: 24 }),
      fetchOnboardingPlannerRunHistory({ orgId, limit: 12 }),
      fetchExistingClientPlannerRunHistory({ orgId, limit: 12 }),
      fetchPlannerRunSelections(orgId),
      fetchPlannerTriggers({ orgId, limit: 24 }),
      fetchPlannerAttentionItems({ orgId, actionableOnly: true, limit: 12 }),
      fetchPlannerAttentionCounts({ orgId, actionableOnly: true }),
      fetchPlannerMonthlyUsage({ orgId }),
    ]);

    const nextHistory = [
      ...(onboardingHistory.status === 'fulfilled' ? onboardingHistory.value : []),
      ...(recurringHistory.status === 'fulfilled' ? recurringHistory.value : []),
    ].sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());

    return {
      programs: programList.status === 'fulfilled' ? programList.value : [],
      history: nextHistory,
      historyError:
        onboardingHistory.status === 'rejected' && recurringHistory.status === 'rejected'
          ? onboardingHistory.reason instanceof Error
            ? onboardingHistory.reason.message
            : 'Failed to load saved planning runs.'
          : null,
      selections:
        runSelections.status === 'fulfilled'
          ? runSelections.value
          : {
              latestRun: null,
              currentRun: null,
              latestApprovedRun: null,
            },
      triggers: triggers.status === 'fulfilled' ? triggers.value : [],
      attentionItems: nextAttentionItems.status === 'fulfilled' ? nextAttentionItems.value : [],
      attentionCounts: nextAttentionCounts.status === 'fulfilled' ? nextAttentionCounts.value : [],
      monthlyUsage: nextMonthlyUsage.status === 'fulfilled' ? nextMonthlyUsage.value : null,
    };
  }, [orgId]);

  const refreshWorkspaceState = React.useCallback(async (options?: {
    preserveSelectedRunId?: string | null;
    preserveSelectedInitiativeId?: string | null;
  }): Promise<void> => {
    setAttentionLoading(true);
    setUsageLoading(true);
    try {
      const next = await loadWorkspaceState();
      setPrograms(next.programs);
      setHistory(next.history);
      setHistoryError(next.historyError);
      setLatestRunSelection(next.selections.latestRun);
      setCurrentRunSelection(next.selections.currentRun);
      setLatestApprovedRunSelection(next.selections.latestApprovedRun);
      setTriggerHistory(next.triggers);
      setAttentionItems(next.attentionItems);
      setAttentionCounts(next.attentionCounts);
      setMonthlyUsage(next.monthlyUsage);
      setSelectedInitiativeId((current) => {
        const preferredRunId = options?.preserveSelectedRunId ?? selectedRunId;
        const preferredRunInitiativeId =
          next.history.find((entry) => entry.runId === preferredRunId)?.programSummary?.primaryProgramId
          ?? next.history.find((entry) => entry.runId === preferredRunId)?.initiativeSummary?.primaryInitiativeId
          ?? null;
        const preferredInitiativeId = preferredRunInitiativeId
          ?? options?.preserveSelectedInitiativeId
          ?? current;
        if (preferredInitiativeId && next.programs.some((entry) => entry.programId === preferredInitiativeId)) {
          return preferredInitiativeId;
        }
        return next.programs[0]?.programId ?? null;
      });
      setSelectedRunId((current) => {
        const preferred = options?.preserveSelectedRunId ?? current;
        if (preferred && next.history.some((entry) => entry.runId === preferred)) {
          return preferred;
        }
        return next.history[0]?.runId ?? preferred ?? null;
      });
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : 'Failed to refresh planning workspace state.');
    } finally {
      setAttentionLoading(false);
      setUsageLoading(false);
    }
  }, [loadWorkspaceState, selectedRunId]);

  useEffect(() => {
    let cancelled = false;
    setSourceLoading(true);
    setSourceError(null);
    setKickoffSource(null);
    setRecurringNoteSources([]);

    void (async () => {
      try {
        const next = await fetchPlanningMeetingSources(orgId);
        if (!cancelled) {
          setKickoffSource(next.kickoffSource);
          setRecurringNoteSources(next.recurringNoteSources);
        }
      } catch (error) {
        if (!cancelled) {
          setKickoffSource(null);
          setRecurringNoteSources([]);
          setSourceError(error instanceof Error ? error.message : 'Failed to load planning source context.');
        }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    setPrograms([]);
    setSelectedInitiativeId(null);
    setSelectedInitiativeSelections(null);
    setSelectedInitiativeRuns([]);
    setSelectedInitiativeRecurringSeries([]);
    setHistory([]);
    setLatestRunSelection(null);
    setCurrentRunSelection(null);
    setLatestApprovedRunSelection(null);
    setTriggerHistory([]);
    setAttentionItems([]);
    setAttentionCounts([]);
    setAttentionLoading(true);
    setAttentionEventsByItemId({});
    setAttentionEventsLoading(false);
    setAttentionActionLoading(null);
    setMonthlyUsage(null);
    setUsageLoading(true);
    setHistoryLatestApplyByRunId({});
    setHistoryApplyLoading(false);
    setSelectedRunId(null);
    setPlannerResult(null);
    setReviewHistory([]);
    setLatestApplyExecution(null);
    setApplyHistory([]);
    setSelectedTriggerEvents([]);
    setComparisonSummary(null);
    setReviewNote('');
    setShowReviewDecisionEditor(false);
    setReviewApplyOpenPanel('review');
    setPendingPlanStartLabel(null);
    setRunError(null);
    setDrawerOpen(false);
    setPlansView('list');
    setComparisonBaseline('previous');
    setChartMonthOffset(0);
    setInitiativeSearchQuery('');
    setInitiativeTypeFilter('all');
    setInitiativeStatusFilter('all');
    setInitiativeSortOrder('updated');

    void (async () => {
      try {
        const next = await loadWorkspaceState();
        if (cancelled) return;
        setPrograms(next.programs);
        setHistory(next.history);
        setHistoryError(next.historyError);
        setLatestRunSelection(next.selections.latestRun);
        setCurrentRunSelection(next.selections.currentRun);
        setLatestApprovedRunSelection(next.selections.latestApprovedRun);
        setTriggerHistory(next.triggers);
        setAttentionItems(next.attentionItems);
        setAttentionCounts(next.attentionCounts);
        setMonthlyUsage(next.monthlyUsage);
        setSelectedInitiativeId(next.programs[0]?.programId ?? null);
        setSelectedRunId(next.history[0]?.runId ?? null);
      } catch (error) {
        if (!cancelled) {
          setPrograms([]);
          setHistory([]);
          setHistoryError(error instanceof Error ? error.message : 'Failed to load saved planning runs.');
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
          setAttentionLoading(false);
          setUsageLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadWorkspaceState]);

  useEffect(() => {
    if (!history.length) {
      setHistoryLatestApplyByRunId({});
      setHistoryApplyLoading(false);
      return undefined;
    }

    let cancelled = false;
    setHistoryApplyLoading(true);

    void (async () => {
      const results = await Promise.allSettled(
        history.map(async (entry) => [entry.runId, await fetchPlannerRunLatestApply(entry.runId)] as const),
      );
      if (cancelled) return;
      const next: Record<string, AgenticPlanningApplyExecution | null> = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          next[result.value[0]] = result.value[1];
        }
      });
      setHistoryLatestApplyByRunId(next);
      setHistoryApplyLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [history]);

  useEffect(() => {
    if (!selectedInitiativeId) {
      setSelectedInitiativeSelections(null);
      setSelectedInitiativeRuns([]);
      setSelectedInitiativeRecurringSeries([]);
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      try {
        const [nextSelections, nextRuns, nextRecurringSeries] = await Promise.all([
          fetchPlannerProgramSelections(selectedInitiativeId),
          fetchPlannerProgramRuns({ programId: selectedInitiativeId, limit: 24 }),
          fetchPlannerProgramRecurringSeries(selectedInitiativeId),
        ]);
        if (cancelled) return;
        setSelectedInitiativeSelections(nextSelections);
        setSelectedInitiativeRuns(nextRuns);
        setSelectedInitiativeRecurringSeries(nextRecurringSeries);
        setSelectedRunId((current) => {
          if (current && nextRuns.some((entry) => entry.runId === current)) {
            return current;
          }
          return (
            nextSelections.currentDraftRun?.runId
            ?? nextSelections.latestRun?.runId
            ?? nextRuns[0]?.runId
            ?? current
          );
        });
      } catch {
        if (!cancelled) {
          setSelectedInitiativeSelections(null);
          setSelectedInitiativeRuns([]);
          setSelectedInitiativeRecurringSeries([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInitiativeId]);

  useEffect(() => {
    if (!selectedRunId) return undefined;
    if (plannerResult?.runState.runId === selectedRunId) return undefined;

    let cancelled = false;
    setDetailLoading(true);
    setApplyLoading(true);
    setRunError(null);
    setPlannerResult(null);
    setReviewHistory([]);
    setLatestApplyExecution(null);
    setApplyHistory([]);

    void (async () => {
      try {
        const [next, nextReviewHistory, nextLatestApply, nextApplyHistory] = await Promise.all([
          fetchPlannerRun(selectedRunId),
          fetchPlannerRunReviewHistory(selectedRunId),
          fetchPlannerRunLatestApply(selectedRunId),
          fetchPlannerRunApplyHistory(selectedRunId),
        ]);
        if (!cancelled) {
          setPlannerResult(next);
          setReviewHistory(nextReviewHistory);
          setLatestApplyExecution(nextLatestApply);
          setApplyHistory(nextApplyHistory);
        }
      } catch (error) {
        if (!cancelled) {
          setPlannerResult(null);
          setRunError(error instanceof Error ? error.message : 'Failed to load the selected version.');
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
          setApplyLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [plannerResult?.runState.runId, selectedRunId]);

  useEffect(() => {
    setCardRunDetailsByRunId({});
    cardRunDetailLoadingRef.current.clear();
    cardRunDetailFailedRef.current.clear();
  }, [orgId, serviceTab]);

  useEffect(() => {
    const runId = plannerResult?.runState.runId;
    if (!runId) return;
    cardRunDetailFailedRef.current.delete(runId);
    setCardRunDetailsByRunId((current) => (
      current[runId] === plannerResult
        ? current
        : {
            ...current,
            [runId]: plannerResult,
          }
    ));
  }, [plannerResult]);

  useEffect(() => {
    if (!selectedRunId) return;
    setHistoryLatestApplyByRunId((current) => {
      const existing = current[selectedRunId] ?? null;
      const next = latestApplyExecution ?? null;
      if (
        (existing?.applyId ?? null) === (next?.applyId ?? null)
        && (existing?.status ?? null) === (next?.status ?? null)
        && (existing?.attemptNumber ?? null) === (next?.attemptNumber ?? null)
      ) {
        return current;
      }
      return {
        ...current,
        [selectedRunId]: next,
      };
    });
  }, [latestApplyExecution, selectedRunId]);

  useEffect(() => {
    if (!selectedRunId) {
      setComparisonSummary(null);
      setComparisonLoading(false);
      return undefined;
    }

    let cancelled = false;
    setComparisonLoading(true);
    setComparisonSummary(null);

    void (async () => {
      try {
        const next = await fetchPlannerRunComparisonSummary(selectedRunId, comparisonBaseline);
        if (!cancelled) {
          setComparisonSummary(next);
        }
      } catch {
        if (!cancelled) {
          setComparisonSummary(null);
        }
      } finally {
        if (!cancelled) {
          setComparisonLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [comparisonBaseline, selectedRunId]);

  useEffect(() => {
    setShowReviewDecisionEditor(false);
    setReviewNote('');
  }, [selectedRunId]);

  const selectedRunTrigger = React.useMemo(() => {
    if (!selectedRunId) return null;
    return [...triggerHistory]
      .filter((entry) => entry.linkedRunId === selectedRunId)
      .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())[0] ?? null;
  }, [selectedRunId, triggerHistory]);

  useEffect(() => {
    if (!selectedRunTrigger?.triggerId) {
      setSelectedTriggerEvents([]);
      setTriggerEventsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setTriggerEventsLoading(true);
    setSelectedTriggerEvents([]);

    void (async () => {
      try {
        const next = await fetchPlannerTriggerEvents(selectedRunTrigger.triggerId, 20);
        if (!cancelled) setSelectedTriggerEvents(next);
      } catch {
        if (!cancelled) setSelectedTriggerEvents([]);
      } finally {
        if (!cancelled) setTriggerEventsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRunTrigger?.triggerId]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [plansView, selectedInitiativeId, selectedRunId]);

  const latestRunSummary = latestRunSelection ?? history[0] ?? plannerResult?.runSummary ?? null;
  const visibleInitiatives = React.useMemo(
    () => filterByServiceTab(programs, serviceTab, (program) => program.primaryServiceCode),
    [programs, serviceTab],
  );
  const historyByRunId = React.useMemo(
    () => new Map(history.map((entry) => [entry.runId, entry])),
    [history],
  );
  const openAttentionItems = React.useMemo(
    () => attentionItems.filter((item) => item.status === 'open'),
    [attentionItems],
  );
  const attentionSummary = React.useMemo(
    () => summarizeAttentionCounts(attentionCounts),
    [attentionCounts],
  );
  const usageContextLabel = React.useMemo(() => {
    if (usageLoading) return 'Loading this month’s saved planner usage.';
    if (!monthlyUsage) return 'Monthly agentic planner usage is unavailable right now.';
    if (monthlyUsage.runCount === 0) {
      return `${monthlyUsage.monthLabel} has no saved agentic planner runs yet.`;
    }
    if (monthlyUsage.trackingStatus === 'complete') {
      return `${monthlyUsage.monthLabel}. Tracked ${formatNumber(monthlyUsage.runCount)} of ${formatNumber(monthlyUsage.runCount)} runs.`;
    }
    if (monthlyUsage.trackingStatus === 'partial') {
      return `${monthlyUsage.monthLabel}. Tracked ${formatNumber(monthlyUsage.trackedRunCount)} of ${formatNumber(monthlyUsage.runCount)} runs.`;
    }
    return `${monthlyUsage.monthLabel} has saved versions, but token tracking is not available on them yet.`;
  }, [monthlyUsage, usageLoading]);
  const usageEstimatedCostTone: Tone = !monthlyUsage?.estimatedCostStatus
    ? 'neutral'
    : monthlyUsage.estimatedCostStatus === 'complete'
      ? 'good'
      : monthlyUsage.estimatedCostStatus === 'partial'
        ? 'warn'
        : 'neutral';
  const usageEstimatedCostLabel = React.useMemo(() => {
    if (!monthlyUsage || monthlyUsage.estimatedCostStatus === 'none') {
      return '--';
    }
    return `~${formatUsdAmount(monthlyUsage.estimatedCostUsd ?? 0)}`;
  }, [monthlyUsage]);
  void usageContextLabel;
  const chartReferenceMonth = React.useMemo(
    () => startOfMonth(new Date()),
    [],
  );
  const selectedChartMonth = React.useMemo(
    () => addMonths(chartReferenceMonth, chartMonthOffset),
    [chartMonthOffset, chartReferenceMonth],
  );
  const selectedChartMonthLabel = React.useMemo(
    () => formatMonthLabelWithYear(selectedChartMonth),
    [selectedChartMonth],
  );
  const canGoToPreviousChartMonth = chartMonthOffset > -12;
  const canGoToNextChartMonth = chartMonthOffset < 0;
  const previousChartMonthLabel = formatMonthButtonLabel(
    canGoToPreviousChartMonth ? addMonths(selectedChartMonth, -1) : selectedChartMonth,
  );
  const nextChartMonthLabel = formatMonthButtonLabel(
    canGoToNextChartMonth ? addMonths(selectedChartMonth, 1) : selectedChartMonth,
  );
  const heroActivityPoints = React.useMemo<HeroActivityPoint[]>(() => {
    const monthStart = startOfMonth(selectedChartMonth);
    const nextMonthStart = addMonths(monthStart, 1);
    const totalDays = Math.max(1, Math.round((nextMonthStart.getTime() - monthStart.getTime()) / 86400000));
    const bucketCount = Math.max(1, Math.ceil(totalDays / 7));
    const buckets = Array.from({ length: bucketCount }, (_, index) => {
      const bucketStart = addDays(monthStart, index * 7);
      const bucketEnd = addDays(monthStart, Math.min(totalDays, (index + 1) * 7));
      return {
        key: `${buildMonthKey(monthStart)}:${index}`,
        label: formatDayRangeLabel(bucketStart, bucketEnd),
        fullLabel: formatDayRangeLabelWithMonth(bucketStart, bucketEnd),
        totalRuns: 0,
        draftRuns: 0,
        publishedRuns: 0,
      };
    });

    history.forEach((entry) => {
      const createdAt = new Date(entry.createdAt);
      if (Number.isNaN(createdAt.getTime())) return;
      if (createdAt.getTime() < monthStart.getTime() || createdAt.getTime() >= nextMonthStart.getTime()) return;
      const diffDays = Math.floor((createdAt.getTime() - monthStart.getTime()) / 86400000);
      const bucket = buckets[Math.min(Math.floor(diffDays / 7), bucketCount - 1)];
      if (!bucket) return;
      bucket.totalRuns += 1;
      if (historyLatestApplyByRunId[entry.runId]?.status === 'succeeded') {
        bucket.publishedRuns += 1;
      } else {
        bucket.draftRuns += 1;
      }
    });

    return buckets;
  }, [history, historyLatestApplyByRunId, selectedChartMonth]);
  const heroChartLoading = historyLoading || (history.length > 0 && historyApplyLoading && Object.keys(historyLatestApplyByRunId).length === 0);
  const attentionByRunId = React.useMemo(() => {
    const mapping = new Map<string, AgenticPlanningAttentionItem[]>();
    openAttentionItems.forEach((item) => {
      const runId = item.runId ?? item.linkedRun?.runId ?? null;
      if (!runId) return;
      mapping.set(runId, [...(mapping.get(runId) ?? []), item]);
    });
    return mapping;
  }, [openAttentionItems]);
  const initiativeIdByRunId = React.useMemo(() => {
    const mapping = new Map<string, string>();
    history.forEach((entry) => {
      const initiativeId = getRunPrimaryProgramId(entry);
      if (initiativeId) {
        mapping.set(entry.runId, initiativeId);
      }
    });
    selectedInitiativeRuns.forEach((entry) => {
      const initiativeId = getRunPrimaryProgramId(entry) || selectedInitiativeId;
      if (initiativeId) {
        mapping.set(entry.runId, initiativeId);
      }
    });
    const plannerRunId = plannerResult?.runSummary?.runId;
    const plannerInitiativeId = getRunPrimaryProgramId(plannerResult?.runSummary ?? null);
    if (plannerRunId && plannerInitiativeId) {
      mapping.set(plannerRunId, plannerInitiativeId);
    }
    return mapping;
  }, [history, plannerResult?.runSummary, selectedInitiativeId, selectedInitiativeRuns]);
  const versionCountByInitiativeId = React.useMemo(() => {
    const counts = new Map<string, number>();
    history.forEach((entry) => {
      const initiativeId = getRunPrimaryProgramId(entry);
      if (!initiativeId) return;
      counts.set(initiativeId, (counts.get(initiativeId) ?? 0) + 1);
    });
    return counts;
  }, [history]);
  const attentionCountByInitiativeId = React.useMemo(() => {
    const counts = new Map<string, number>();
    openAttentionItems.forEach((item) => {
      const runId = item.runId ?? item.linkedRun?.runId ?? null;
      if (!runId) return;
      const initiativeId = initiativeIdByRunId.get(runId);
      if (!initiativeId) return;
      counts.set(initiativeId, (counts.get(initiativeId) ?? 0) + 1);
    });
    return counts;
  }, [initiativeIdByRunId, openAttentionItems]);
  const initiativeCards = React.useMemo<ProgramCardData[]>(() => (
    visibleInitiatives.map((program) => {
      const latestRun =
        (program.latestRunId ? historyByRunId.get(program.latestRunId) ?? null : null)
        ?? (selectedInitiativeSelections?.program.programId === program.programId
          ? selectedInitiativeSelections.latestRun
          : null);
      const latestApprovedRun =
        (program.latestApprovedRunId ? historyByRunId.get(program.latestApprovedRunId) ?? null : null)
        ?? (selectedInitiativeSelections?.program.programId === program.programId
          ? selectedInitiativeSelections.latestApprovedRun
          : null);
      const currentDraftRun =
        (program.currentDraftRunId ? historyByRunId.get(program.currentDraftRunId) ?? null : null)
        ?? (selectedInitiativeSelections?.program.programId === program.programId
          ? selectedInitiativeSelections.currentDraftRun
          : null);
      const anchorRun = currentDraftRun ?? latestRun ?? latestApprovedRun;
      return {
        program,
        latestRun,
        latestApprovedRun,
        currentDraftRun,
        latestApply:
          (program.latestAppliedRunId ? historyLatestApplyByRunId[program.latestAppliedRunId] ?? null : null)
          ?? null,
        attentionCount: attentionCountByInitiativeId.get(program.programId) ?? 0,
        newerRecurringNoteCount: anchorRun ? getNewerRecurringNoteCountForRun(anchorRun, recurringNoteSources) : 0,
        versionCount:
          selectedInitiativeId === program.programId && selectedInitiativeRuns.length
            ? selectedInitiativeRuns.length
            : versionCountByInitiativeId.get(program.programId) ?? 0,
      };
    })
  ), [
    attentionCountByInitiativeId,
    historyByRunId,
    historyLatestApplyByRunId,
    recurringNoteSources,
    selectedInitiativeId,
    selectedInitiativeRuns.length,
    selectedInitiativeSelections,
    versionCountByInitiativeId,
    visibleInitiatives,
  ]);
  const visibleInitiativeCards = React.useMemo(
    () => filterAndSortInitiatives(initiativeCards, {
      query: initiativeSearchQuery,
      typeFilter: initiativeTypeFilter,
      statusFilter: initiativeStatusFilter,
      sortOrder: initiativeSortOrder,
    }),
    [initiativeCards, initiativeSearchQuery, initiativeSortOrder, initiativeStatusFilter, initiativeTypeFilter],
  );
  const availableProgramStarterOfferings = React.useMemo(
    () => serviceContract?.offerings ?? [],
    [serviceContract],
  );
  useEffect(() => {
    let cancelled = false;
    const serviceCode = text(serviceContract?.serviceCode);
    if (!serviceCode) {
      setOfferingPlaybooksByCode({});
      setOfferingPlaybooksLoading(false);
      setOfferingPlaybooksError(null);
      return undefined;
    }
    setOfferingPlaybooksLoading(true);
    setOfferingPlaybooksError(null);
    void fetchOfferingPlaybooks({ serviceCode })
      .then((playbooks) => {
        if (cancelled) return;
        const nextByCode: Record<string, AgenticOfferingPlaybook> = {};
        playbooks.forEach((playbook) => {
          if (!nextByCode[playbook.offeringCode]) {
            nextByCode[playbook.offeringCode] = playbook;
          }
        });
        setOfferingPlaybooksByCode(nextByCode);
      })
      .catch((error) => {
        if (cancelled) return;
        setOfferingPlaybooksByCode({});
        setOfferingPlaybooksError(
          error instanceof Error ? error.message : 'Unable to load offering guidance right now.',
        );
      })
      .finally(() => {
        if (cancelled) return;
        setOfferingPlaybooksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [serviceContract?.id, serviceContract?.serviceCode]);
  const programCardsByOfferingCode = React.useMemo(() => {
    const mapping = new Map<string, ProgramCardData[]>();
    initiativeCards.forEach((entry) => {
      const offeringCode = text(entry.program.primaryScopeCode);
      if (!offeringCode) return;
      const current = mapping.get(offeringCode) ?? [];
      current.push(entry);
      mapping.set(offeringCode, current);
    });
    mapping.forEach((entries, offeringCode) => {
      mapping.set(
        offeringCode,
        [...entries].sort(
          (left, right) => new Date(right.program.updatedAt).getTime() - new Date(left.program.updatedAt).getTime(),
        ),
      );
    });
    return mapping;
  }, [initiativeCards]);
  const selectedProgramStarterOffering = React.useMemo(
    () => availableProgramStarterOfferings.find((entry) => entry.offeringCode === programStarterOfferingCode) ?? null,
    [availableProgramStarterOfferings, programStarterOfferingCode],
  );
  const selectedProgramStarterPlaybook = React.useMemo(
    () => (
      selectedProgramStarterOffering
        ? offeringPlaybooksByCode[selectedProgramStarterOffering.offeringCode] ?? null
        : null
    ),
    [offeringPlaybooksByCode, selectedProgramStarterOffering],
  );
  const selectedProgramStarterPrograms = React.useMemo(
    () => (selectedProgramStarterOffering
      ? programCardsByOfferingCode.get(selectedProgramStarterOffering.offeringCode) ?? []
      : []),
    [programCardsByOfferingCode, selectedProgramStarterOffering],
  );
  const visibleCardAnchorRunIds = React.useMemo(
    () => visibleInitiativeCards
      .map((entry) => (
        entry.currentDraftRun?.runId
        ?? entry.latestRun?.runId
        ?? entry.latestApprovedRun?.runId
        ?? null
      ))
      .filter((value, index, all): value is string => Boolean(value) && all.indexOf(value) === index),
    [visibleInitiativeCards],
  );
  const hasInitiativeFiltersActive =
    initiativeSearchQuery.trim().length > 0
    || initiativeTypeFilter !== 'all'
    || initiativeStatusFilter !== 'all'
    || initiativeSortOrder !== 'updated';
  useEffect(() => {
    const loadingRuns = cardRunDetailLoadingRef.current;
    const missingRunIds = visibleCardAnchorRunIds.filter((runId) => (
      !cardRunDetailsByRunId[runId]
      && !loadingRuns.has(runId)
      && !cardRunDetailFailedRef.current.has(runId)
    ));
    if (!missingRunIds.length) return undefined;

    let cancelled = false;
    missingRunIds.forEach((runId) => {
      loadingRuns.add(runId);
    });

    void (async () => {
      const results = await Promise.all(
        missingRunIds.map(async (runId) => {
          try {
            return {
              runId,
              result: await fetchPlannerRun(runId),
              error: null,
            };
          } catch (error) {
            return {
              runId,
              result: null,
              error,
            };
          }
        }),
      );
      if (cancelled) return;
      const nextLoaded: Record<string, AgenticPlanningDryRunResult> = {};
      results.forEach(({ runId, result, error }) => {
        loadingRuns.delete(runId);
        if (result) {
          cardRunDetailFailedRef.current.delete(runId);
          nextLoaded[runId] = result;
          return;
        }
        if (error) {
          cardRunDetailFailedRef.current.add(runId);
        }
      });
      if (Object.keys(nextLoaded).length) {
        setCardRunDetailsByRunId((current) => ({
          ...current,
          ...nextLoaded,
        }));
      }
    })();

    return () => {
      cancelled = true;
      missingRunIds.forEach((runId) => {
        loadingRuns.delete(runId);
      });
    };
  }, [cardRunDetailsByRunId, visibleCardAnchorRunIds]);
  const historyVersionMap = React.useMemo(() => buildRunVersionMap(history), [history]);
  const selectedInitiative = React.useMemo(
    () => visibleInitiatives.find((entry) => entry.programId === selectedInitiativeId)
      ?? programs.find((entry) => entry.programId === selectedInitiativeId)
      ?? selectedInitiativeSelections?.program
      ?? null,
    [programs, selectedInitiativeId, selectedInitiativeSelections?.program, visibleInitiatives],
  );
  const selectedInitiativeVersionMap = React.useMemo(
    () => buildRunVersionMap(selectedInitiativeRuns),
    [selectedInitiativeRuns],
  );
  const versionOptions = React.useMemo(
    () => [...selectedInitiativeRuns]
      .sort((left, right) => (selectedInitiativeVersionMap.get(right.runId)?.index ?? 0) - (selectedInitiativeVersionMap.get(left.runId)?.index ?? 0)),
    [selectedInitiativeRuns, selectedInitiativeVersionMap],
  );
  const orphanRuns = React.useMemo(
    () => filterByServiceTab(
      history.filter((entry) => !getRunPrimaryProgramId(entry)),
      serviceTab,
      (entry) => entry.purchasedLaneSummary[0]?.serviceCode ?? null,
    ),
    [history, serviceTab],
  );
  const selectedRunSummary = pendingPlanStartLabel
    ? null
    : (
    selectedInitiativeRuns.find((entry) => entry.runId === selectedRunId)
    ?? history.find((entry) => entry.runId === selectedRunId)
    ?? plannerResult?.runSummary
    ?? selectedInitiativeSelections?.latestRun
    ?? latestRunSummary
    );
  const isPlanDetailView =
    plansView === 'detail' && Boolean(selectedInitiativeId || selectedRunId || selectedRunSummary || detailLoading || plannerResult || pendingPlanStartLabel);
  const headerState = getPlanningHeaderState({
    orgName,
    ownerEmail,
    serviceTab,
    hasActiveServiceContract,
    projectCount,
    programCount: visibleInitiatives.length,
    kickoffSource,
    recurringNoteSources,
    latestRunSummary,
    historyCount: history.length,
  });
  const listViewStartAction = React.useMemo(() => {
    if (!headerState.primaryAction) return null;
    return {
      ...headerState.primaryAction,
      label: 'Start Program',
    };
  }, [headerState.primaryAction]);
  const selectedRunNewerRecurringNoteCount = getNewerRecurringNoteCountForRun(selectedRunSummary, recurringNoteSources);
  const selectedRunAttentionItems = React.useMemo(
    () => (selectedRunSummary?.runId ? attentionByRunId.get(selectedRunSummary.runId) ?? [] : []),
    [attentionByRunId, selectedRunSummary?.runId],
  );
  const selectedRunDetailResult = React.useMemo(
    () => plannerResult
      ?? (selectedRunSummary?.runId ? cardRunDetailsByRunId[selectedRunSummary.runId] ?? null : null),
    [cardRunDetailsByRunId, plannerResult, selectedRunSummary?.runId],
  );
  const selectedRunDetailTeamLabelMap = React.useMemo(
    () => buildTeamLabelMap(selectedRunDetailResult),
    [selectedRunDetailResult],
  );
  useEffect(() => {
    if (!selectedRunAttentionItems.length) {
      setAttentionEventsByItemId({});
      setAttentionEventsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAttentionEventsLoading(true);

    void (async () => {
      const results = await Promise.allSettled(
        selectedRunAttentionItems.map(async (item) => [item.attentionItemId, await fetchPlannerAttentionEvents(item.attentionItemId)] as const),
      );
      if (cancelled) return;
      const next: Record<string, AgenticPlanningAttentionEvent[]> = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          next[result.value[0]] = result.value[1];
        }
      });
      setAttentionEventsByItemId(next);
      setAttentionEventsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRunAttentionItems]);
  const teamLabelMap = React.useMemo(
    () => buildTeamLabelMap(plannerResult),
    [plannerResult],
  );
  const roadmapProjectCards = React.useMemo(
    () => (plannerResult ? groupProjectsForDisplay(plannerResult, null) : []),
    [plannerResult],
  );
  const roadmapProjectSummaries = React.useMemo<RoadmapProjectSummary[]>(
    () => roadmapProjectCards.map(({ project, milestones }, index) => {
      const milestoneCount = milestones.length;
      const deliverableCount = milestones.reduce((sum, entry) => sum + entry.deliverables.length, 0);
      const ticketCount = milestones.reduce((sum, entry) => sum + entry.tickets.length, 0);
      const routeParts = [
        humanizeToken(project.executionOfferingCode || ''),
        humanizeToken(project.executionOfferingSubcategoryId || ''),
      ].filter(Boolean);
      return {
        projectKey: project.key,
        order: index + 1,
        teamLabel: formatTeamLabel(project.teamNodeId, teamLabelMap),
        collaboratorLabel: project.supportingTeamNodeIds.length
          ? formatTeamList(project.supportingTeamNodeIds, teamLabelMap).join(', ')
          : null,
        milestoneCount,
        deliverableCount,
        ticketCount,
        routeLabel: routeParts.length ? routeParts.join(' / ') : null,
      };
    }),
    [roadmapProjectCards, teamLabelMap],
  );
  const roadmapTeamCount = React.useMemo(
    () => new Set(
      roadmapProjectCards.flatMap(({ project }) => [project.teamNodeId, ...(project.supportingTeamNodeIds ?? [])].filter(Boolean)),
    ).size,
    [roadmapProjectCards],
  );
  const selectedRoadmapProject = React.useMemo(
    () => roadmapProjectCards.find(({ project }) => project.key === selectedRoadmapProjectKey)
      ?? null,
    [roadmapProjectCards, selectedRoadmapProjectKey],
  );
  const projectCards = React.useMemo(
    () => (plannerResult ? groupProjectsForDisplay(plannerResult, serviceTab) : []),
    [plannerResult, serviceTab],
  );
  const milestoneCards = React.useMemo(
    () => projectCards.flatMap(({ project, milestones }) => milestones.map(({ milestone, deliverables, tickets }) => ({
      project,
      milestone,
      deliverables,
      tickets,
    }))),
    [projectCards],
  );
  const scopeCounts = plannerResult ? getImmediateScopeCounts(plannerResult) : { immediateProjects: 0, immediateTickets: 0, backlogProjects: 0, backlogTickets: 0 };
  const inputSufficiency = plannerResult?.planGraph.modeMetadata.inputSufficiency;
  const purchasedLanes = plannerResult ? filterByServiceTab(plannerResult.planGraph.boundaryMetadata?.purchasedLanes ?? [], serviceTab, (lane) => lane.serviceCode) : [];
  const subcategoryDecisions = plannerResult ? filterByServiceTab(plannerResult.planGraph.boundaryMetadata?.subcategoryDecisions ?? [], serviceTab, (entry) => entry.serviceCode) : [];
  const kbProcessDecisions = plannerResult ? filterByServiceTab(plannerResult.planGraph.boundaryMetadata?.kbProcessDecisions ?? [], serviceTab, (entry) => entry.serviceCode) : [];
  const visibleFacts = plannerResult ? plannerResult.factSet.facts.filter((fact) => !fact.relatedServiceCodes?.length || fact.relatedServiceCodes.some((code) => serviceCodeMatchesTab(code, serviceTab))) : [];
  const discoveredFacts = visibleFacts.filter((fact) => fact.classification !== 'assumed').slice(0, 6);
  const planningGroups: PlanningExplorerGroup[] = React.useMemo(
    () => [
      {
        key: 'projects',
        title: 'Projects',
        summary: `${formatNumber(projectCards.length)} projects`,
      },
      {
        key: 'milestones',
        title: 'Milestones',
        summary: `${formatNumber(milestoneCards.length)} milestones | ${formatNumber(selectedRunSummary?.counts.tickets ?? 0)} tickets`,
      },
      {
        key: 'taxonomy_kbase',
        title: 'Taxonomy + KBase',
        summary: `${formatNumber(subcategoryDecisions.length)} taxonomy | ${formatNumber(kbProcessDecisions.length)} KBase`,
      },
    ],
    [
      kbProcessDecisions.length,
      milestoneCards.length,
      projectCards.length,
      selectedRunSummary?.counts.tickets,
      subcategoryDecisions.length,
    ],
  );
  const selectedPlanningGroup =
    planningGroups.find((group) => group.key === selectedPlanningGroupKey)
    ?? planningGroups[0]
    ?? null;
  const validationWarnings = plannerResult?.validationResults.filter((entry) => entry.status === 'warn') ?? [];
  const validationFailures = plannerResult?.validationResults.filter((entry) => entry.status === 'fail') ?? [];
  const planningConfidenceState = getPlanningConfidenceState(plannerResult);
  const outcomeSummary = getPlanOutcomeSummary(selectedRunSummary);
  const applyEligibilityState = getApplyEligibilityState({
    result: plannerResult,
    latestApply: latestApplyExecution,
  });
  const reviewStatus = plannerResult?.runSummary.draftReview.reviewStatus ?? null;
  const reviewStatusTone = toneForDraftReviewStatus(reviewStatus);
  const latestReviewEvent = reviewHistory.length ? reviewHistory[reviewHistory.length - 1] : null;
  const pairedOperations = new Set([...subcategoryDecisions, ...kbProcessDecisions].map((entry) => entry.pairedOperationId).filter(Boolean));
  const selectedPlanRefreshAction =
    selectedRunSummary?.mode === 'existing_client_recurring_planning'
      ? (
          recurringNoteSources.length
            ? {
                kind: 'run_recurring' as const,
                label: selectedInitiative ? 'Update Program' : 'Start Program',
                scheduleEventIds: recurringNoteSources
                  .map((source) => source.event.id)
                  .filter((value, index, all) => Boolean(value) && all.indexOf(value) === index)
                  .slice(0, 3),
              }
            : null
        )
      : kickoffSource?.event?.id && kickoffSource.note
        ? {
            kind: 'run_onboarding' as const,
            label: selectedInitiative ? 'Update Program' : 'Start Program',
            scheduleEventIds: [kickoffSource.event.id],
          }
        : null;
  const selectedInitiativeScopeLabel = selectedInitiative
    ? getProgramScopeLabel(selectedInitiative)
    : SERVICE_LABELS[serviceTab];
  const selectedProgramExecutionSummary = React.useMemo(
    () => getProgramExecutionSummary({
      program: selectedInitiative,
      summary: selectedRunSummary,
      result: selectedRunDetailResult,
      teamLabelMap: selectedRunDetailTeamLabelMap,
    }),
    [selectedInitiative, selectedRunDetailResult, selectedRunDetailTeamLabelMap, selectedRunSummary],
  );
  const overviewCollaboratorRows = React.useMemo(
    () => {
      const primaryTeamNodeId = selectedProgramExecutionSummary.primaryTeamNodeId;
      const rows = new Map<string, { teamKey: string; teamLabel: string; projectLabels: string[] }>();
      const redundantProjectPrefixes = [
        text(orgName),
        text(selectedInitiativeScopeLabel),
        text(selectedInitiative?.title),
        text(selectedRunSummary ? getPlanRunDisplayTitle(selectedRunSummary) : ''),
      ].filter(Boolean);

      roadmapProjectSummaries.forEach((summary) => {
        const projectCard = roadmapProjectCards.find(({ project }) => project.key === summary.projectKey);
        if (!projectCard) return;

        const collaboratorTeamIds = new Set<string>();
        if (projectCard.project.teamNodeId && projectCard.project.teamNodeId !== primaryTeamNodeId) {
          collaboratorTeamIds.add(projectCard.project.teamNodeId);
        }
        projectCard.project.supportingTeamNodeIds
          .filter((teamNodeId) => Boolean(teamNodeId) && teamNodeId !== primaryTeamNodeId)
          .forEach((teamNodeId) => collaboratorTeamIds.add(teamNodeId));

        collaboratorTeamIds.forEach((teamNodeId) => {
          const existing = rows.get(teamNodeId) ?? {
            teamKey: teamNodeId,
            teamLabel: formatTeamLabel(teamNodeId, teamLabelMap),
            projectLabels: [],
          };
          const rawProjectLabel = text(projectCard.project.name) || 'Project pending';
          let conciseProjectLabel = rawProjectLabel;
          redundantProjectPrefixes.forEach((prefix) => {
            if (!prefix) return;
            if (conciseProjectLabel.toLowerCase().startsWith(prefix.toLowerCase())) {
              conciseProjectLabel = conciseProjectLabel.slice(prefix.length).replace(/^[\s\-|:]+/, '').trim() || rawProjectLabel;
            }
          });
          const delimiterMatch = conciseProjectLabel.match(/^(.*?)\s+-\s+(.*)$/);
          if (delimiterMatch) {
            const leadingSegment = delimiterMatch[1]?.trim().toLowerCase() ?? '';
            const trailingSegment = delimiterMatch[2]?.trim() ?? '';
            const looksRedundant = redundantProjectPrefixes.some((prefix) => {
              const normalizedPrefix = prefix.trim().toLowerCase();
              return normalizedPrefix
                && (leadingSegment.includes(normalizedPrefix) || normalizedPrefix.includes(leadingSegment));
            });
            if (looksRedundant && trailingSegment) {
              conciseProjectLabel = trailingSegment;
            }
          }
          const projectLabel = `#${summary.order} ${conciseProjectLabel}`;
          if (!existing.projectLabels.includes(projectLabel)) {
            existing.projectLabels.push(projectLabel);
          }
          rows.set(teamNodeId, existing);
        });
      });

      return Array.from(rows.values()).sort((left, right) => left.teamLabel.localeCompare(right.teamLabel));
    },
    [orgName, roadmapProjectCards, roadmapProjectSummaries, selectedInitiative?.title, selectedInitiativeScopeLabel, selectedProgramExecutionSummary.primaryTeamNodeId, selectedRunSummary, teamLabelMap],
  );
  const selectedTriggerSummary = selectedRunSummary
    ? getRunTriggerSummary(selectedRunSummary, selectedRunTrigger)
    : '--';
  const comparison = comparisonSummary?.comparison ?? null;
  const selectedVersionInfo = selectedRunSummary?.runId
    ? selectedInitiativeVersionMap.get(selectedRunSummary.runId)
      ?? historyVersionMap.get(selectedRunSummary.runId)
      ?? null
    : null;
  const baselineVersionInfo = comparisonSummary?.baselineRunId
    ? selectedInitiativeVersionMap.get(comparisonSummary.baselineRunId)
      ?? historyVersionMap.get(comparisonSummary.baselineRunId)
      ?? null
    : null;
  const selectedVersionLabel = selectedVersionInfo?.label ?? '--';
  const baselineVersionLabel = baselineVersionInfo?.label ?? (comparisonSummary?.baselineAvailable ? 'previous version' : 'no earlier version');
  const selectedVersionMenuOptions = React.useMemo(
    () => versionOptions.map((entry) => {
      const version = selectedInitiativeVersionMap.get(entry.runId) ?? historyVersionMap.get(entry.runId);
      const badges = [
        version?.isLatest ? 'Latest' : null,
        entry.runId === (selectedInitiativeSelections?.latestApprovedRun?.runId ?? latestApprovedRunSelection?.runId) ? 'Approved' : null,
      ].filter(Boolean);
      return {
        value: entry.runId,
        label: `${version?.label ?? '--'} | ${formatCompactDate(entry.updatedAt)}`,
        meta: badges.length
          ? `${formatPlanningModeLabel(entry.mode)} | ${badges.join(' | ')}`
          : formatPlanningModeLabel(entry.mode),
      };
    }),
    [
      historyVersionMap,
      latestApprovedRunSelection?.runId,
      selectedInitiativeSelections?.latestApprovedRun?.runId,
      selectedInitiativeVersionMap,
      versionOptions,
    ],
  );
  const selectedInitiativeVersionPointers = selectedInitiative
    ? [
      {
        key: 'latest',
        label: 'Latest version',
        run:
          selectedInitiativeSelections?.latestRun
          ?? (selectedInitiative.latestRunId ? historyByRunId.get(selectedInitiative.latestRunId) ?? null : null),
        emptyLabel: 'No saved version yet',
      },
      {
        key: 'draft',
        label: 'Current draft',
        run:
          selectedInitiativeSelections?.currentDraftRun
          ?? (selectedInitiative.currentDraftRunId ? historyByRunId.get(selectedInitiative.currentDraftRunId) ?? null : null),
        emptyLabel: 'No open draft',
      },
      {
        key: 'approved',
        label: 'Latest approved',
        run:
          selectedInitiativeSelections?.latestApprovedRun
          ?? (selectedInitiative.latestApprovedRunId ? historyByRunId.get(selectedInitiative.latestApprovedRunId) ?? null : null),
        emptyLabel: 'Nothing approved yet',
      },
      {
        key: 'applied',
        label: 'Latest applied',
        run:
          selectedInitiativeSelections?.latestAppliedRun
          ?? (selectedInitiative.latestAppliedRunId ? historyByRunId.get(selectedInitiative.latestAppliedRunId) ?? null : null),
        emptyLabel: 'Nothing applied yet',
      },
    ].map((entry) => {
      const version = entry.run
        ? selectedInitiativeVersionMap.get(entry.run.runId)
          ?? historyVersionMap.get(entry.run.runId)
          ?? null
        : null;
      return {
        ...entry,
        versionLabel: version?.label ?? '--',
        metaLabel: entry.run ? formatCompactDate(entry.run.updatedAt) : entry.emptyLabel,
      };
    })
    : [];
  const humanizedComparisonHeadline = humanizeComparisonCopy(comparison?.humanSummary.headline);
  const humanizedComparisonBullets = (comparison?.humanSummary.bullets ?? []).map((item) => humanizeComparisonCopy(item)).filter(Boolean);
  const specialistInvocations = plannerResult?.planGraph.boundaryMetadata?.specialistProvenance?.invocations ?? [];
  const specialistProvenanceNotes = plannerResult?.planGraph.boundaryMetadata?.specialistProvenance?.notes ?? [];
  const specialistInfluence = getSpecialistInfluenceSummary({
    summary: plannerResult?.runSummary.specialistSummary ?? selectedRunSummary?.specialistSummary ?? null,
    invocations: specialistInvocations,
  });
  const reviewRequiredExpansionCount =
    subcategoryDecisions.filter((entry) => entry.reviewRequired).length
    + kbProcessDecisions.filter((entry) => entry.reviewRequired).length;
  // Retained while the legacy selected-version detail body remains commented out.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedPlanningWriteOperations = React.useMemo(() => {
    if (!plannerResult || !selectedPlanningGroup) return [];
    const allowedActions = new Set(PLANNING_EXECUTION_ACTIONS_BY_GROUP[selectedPlanningGroup.key]);
    return plannerResult.writePlan.operations.filter((operation) => allowedActions.has(operation.action));
  }, [plannerResult, selectedPlanningGroup]);
  const selectedPlanningApplyOperations = React.useMemo(() => {
    if (!latestApplyExecution || !selectedPlanningGroup) return [];
    const allowedActions = new Set(PLANNING_EXECUTION_ACTIONS_BY_GROUP[selectedPlanningGroup.key]);
    return latestApplyExecution.operations.filter((operation) => allowedActions.has(operation.action));
  }, [latestApplyExecution, selectedPlanningGroup]);
  // Retained while the legacy selected-version detail body remains commented out.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedPlanningExecutionStage: 'planning' | 'in_progress' | 'build' =
    selectedPlanningApplyOperations.length
      ? (
          latestApplyExecution?.status === 'pending' || latestApplyExecution?.status === 'running'
            ? 'in_progress'
            : 'build'
        )
      : 'planning';
  const selectedRunStatusNotes: Array<{ label: string; tone: Tone }> = [];
  if (selectedRunSummary?.runId === (selectedInitiativeSelections?.currentDraftRun?.runId ?? currentRunSelection?.runId)) {
    selectedRunStatusNotes.push({ label: 'Current draft', tone: 'info' });
  }
  if (
    selectedRunSummary?.runId
    && selectedRunSummary.runId === (selectedInitiativeSelections?.latestApprovedRun?.runId ?? latestApprovedRunSelection?.runId)
  ) {
    selectedRunStatusNotes.push({ label: 'Approved baseline', tone: 'good' });
  }
  if (selectedRunNewerRecurringNoteCount > 0) {
    selectedRunStatusNotes.push({
      label: `${selectedRunNewerRecurringNoteCount} newer recurring note${selectedRunNewerRecurringNoteCount === 1 ? '' : 's'}`,
      tone: 'warn',
    });
  }
  const selectedRunStatusSummary = [
    ...selectedRunStatusNotes.map((item) => item.label),
    selectedRunAttentionItems.length
      ? `${formatNumber(selectedRunAttentionItems.length)} action-needed item${selectedRunAttentionItems.length === 1 ? '' : 's'}`
      : null,
  ].filter(Boolean).join(' | ');
  const getSummaryCountDelta = (key: keyof NonNullable<typeof comparison>['summaryDelta']['counts']) => {
    if (!comparison) return null;
    return comparison.summaryDelta.counts[key].delta;
  };
  const selectedOverviewMetrics: DashboardMetricData[] = plannerResult && selectedRunSummary
    ? [
        {
          label: 'Projects',
          value: selectedRunSummary.counts.projects,
          meta: `${formatNumber(scopeCounts.immediateProjects)} immediate scope`,
          delta: getSummaryCountDelta('projects'),
        },
        {
          label: 'Milestones',
          value: selectedRunSummary.counts.milestones,
          meta: `${formatNumber(plannerResult?.planGraph.deliverables.length ?? 0)} deliverables`,
          delta: getSummaryCountDelta('milestones'),
        },
        {
          label: 'Confidence',
          value: null,
          valueLabel: formatPercent(plannerResult.planGraph.modeMetadata.confidence),
          meta: planningConfidenceState.summary,
        },
        {
          label: 'Warnings',
          value: validationWarnings.length,
          meta: `${formatNumber(validationFailures.length)} failures returned`,
        },
      ]
    : [];
  const showApplyExecutionPanel =
    plannerResult?.runSummary.draftReview.reviewStatus === 'approved_draft'
    || Boolean(latestApplyExecution)
    || applyHistory.length > 0;
  const latestReviewTimestamp = latestReviewEvent ? new Date(latestReviewEvent.createdAt).getTime() : 0;
  const latestApplyTimestamp = latestApplyExecution ? new Date(latestApplyExecution.updatedAt).getTime() : 0;
  const defaultLifecyclePanel: 'review' | 'apply' =
    showApplyExecutionPanel && latestApplyTimestamp > latestReviewTimestamp
      ? 'apply'
      : 'review';
  const showReviewComposer = Boolean(plannerResult)
    && reviewStatus !== 'approved_draft'
    && reviewStatus !== 'superseded';
  const canAdjustApprovedReview = reviewStatus === 'approved_draft'
    && latestApplyExecution?.status !== 'succeeded';
  const hasDecisionOrApplyAction =
    showReviewComposer
    || canAdjustApprovedReview
    || Boolean(applyEligibilityState.actionLabel);
  const reviewSummaryText = !plannerResult
    ? ''
    : reviewStatus === 'approved_draft'
      ? plannerResult.runSummary.draftReview.latestReviewNote
        || 'This version has been approved. Apply remains an explicit separate step.'
      : reviewStatus === 'needs_revision'
        ? plannerResult.runSummary.draftReview.latestReviewNote
          || 'This version needs revision before it should move forward.'
        : reviewStatus === 'rejected_draft'
          ? plannerResult.runSummary.draftReview.latestReviewNote
            || 'This version was rejected and will not move forward unless the review decision changes.'
          : reviewStatus === 'superseded'
            ? plannerResult.runSummary.draftReview.latestReviewNote
              || 'A newer version replaced this one.'
            : plannerResult.runSummary.draftReview.latestReviewNote
              || plannerResult.approvalGate.reasons[0]
              || 'This version is waiting for human review.';
  const applyStatusTone = latestApplyExecution
    ? toneForApplyStatus(latestApplyExecution.status)
    : applyEligibilityState.tone;
  const applyHeadline = latestApplyExecution
    ? formatApplyStatusLabel(latestApplyExecution.status)
    : applyEligibilityState.label;
  const applySummaryText = latestApplyExecution?.errorSummary || applyEligibilityState.summary;
  const sourceArtifacts = plannerResult?.sourceArtifactBundle.artifacts ?? [];
  const assumedFacts = visibleFacts.filter((fact) => fact.classification === 'assumed').slice(0, 6);
  const inferredWorkTypeLabels = subcategoryDecisions.length
    ? subcategoryDecisions.map((entry) => entry.inferredSubcategoryName)
    : Array.from(new Set(discoveredFacts.map((fact) => fact.category).filter(Boolean)));
  const applyBlockedOperations =
    latestApplyExecution?.operations.filter((operation) => (
      operation.status === 'failed'
      || operation.status === 'deferred'
      || operation.status === 'skipped'
    )) ?? [];
  const selectedVersionPhaseSteps = [
    {
      key: 'overview' as const,
      label: 'Overview',
      summary: `${selectedProgramExecutionSummary.primaryTeamLabel} | ${formatNumber(roadmapProjectCards.length)} project${roadmapProjectCards.length === 1 ? '' : 's'}`,
    },
    {
      key: 'inputs' as const,
      label: 'Context',
      summary: sourceArtifacts.length
        ? `${formatNumber(sourceArtifacts.length)} source${sourceArtifacts.length === 1 ? '' : 's'}`
        : selectedRunTrigger
          ? 'Trigger context only'
          : 'Version metadata',
    },
    {
      key: 'discovery' as const,
      label: 'Discovery',
      summary: `${formatNumber(discoveredFacts.length)} finding${discoveredFacts.length === 1 ? '' : 's'}${plannerResult?.planGraph.assumptions.length ? ` | ${formatNumber(plannerResult.planGraph.assumptions.length)} assumptions` : ''}`,
    },
    {
      key: 'roadmap' as const,
      label: 'Roadmap',
      summary: `${formatNumber(roadmapProjectCards.length)} projects | ${formatNumber(roadmapTeamCount)} teams`,
    },
    {
      key: 'review_apply' as const,
      label: 'Review & Apply',
      summary: selectedRunAttentionItems.length
        ? `${formatNumber(selectedRunAttentionItems.length)} attention`
        : latestApplyExecution
          ? formatApplyStatusLabel(latestApplyExecution.status)
          : formatDraftReviewStatusLabel(reviewStatus),
    },
  ];

  function renderOverviewPhase() {
    if (!plannerResult || !selectedRunSummary) return null;

    const overviewTitle =
      text(selectedInitiativeScopeLabel)
      || text(selectedInitiative?.title)
      || text(getPlanRunDisplayTitle(selectedRunSummary));
    const overviewNarrativeCandidates = [
      selectedInitiative?.objective,
      outcomeSummary,
    ];
    const normalizedOverviewTitle = overviewTitle.toLowerCase();
    const normalizedProgramTitle = text(selectedInitiative?.title).toLowerCase();
    const overviewNarrative = overviewNarrativeCandidates.find((candidate) => {
      const normalized = text(candidate).toLowerCase();
      if (!normalized) return false;
      if (normalized === normalizedOverviewTitle || normalized === normalizedProgramTitle) return false;
      if (normalized.includes('workstream')) return false;
      if (normalizedOverviewTitle && normalized.includes(`${normalizedOverviewTitle} offering`)) return false;
      if (normalizedOverviewTitle && normalized.includes(normalizedOverviewTitle) && normalized.includes('service scope')) return false;
      return true;
    }) || null;
    const overviewChangeHeadline = humanizedComparisonHeadline
      || (
        comparisonSummary?.baselineAvailable
          ? `Changes from ${baselineVersionLabel} to ${selectedVersionLabel}`
          : `${selectedVersionLabel} is the current saved version.`
      );
    const overviewChangeNotes = humanizedComparisonBullets.length
      ? humanizedComparisonBullets
      : comparison?.comparability.warnings.length
        ? comparison.comparability.warnings
        : [];

    return (
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LayoutGrid size={16} />
          <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Overview</div>
        </div>

        <DashboardTopGrid>
          <motion.div {...getDashboardMotionProps(0.02)}>
            <DashboardColumnStack>
              <DashboardSummaryBlock>
                <Eyebrow $tone={selectedInitiative ? toneForProgramStatus(selectedInitiative.status) : toneForPlanMode(selectedRunSummary.planClassification)}>
                  {selectedInitiative
                    ? `${formatProgramTypeLabel(selectedInitiative.programType)} Program | ${selectedVersionLabel} | ${formatPlanningModeLabel(selectedRunSummary.mode)}`
                    : `${selectedVersionLabel} | ${formatPlanningModeLabel(selectedRunSummary.mode)} | ${formatPlanClassificationLabel(selectedRunSummary.planClassification)}`}
                </Eyebrow>
                <DashboardTitle>{overviewTitle}</DashboardTitle>
                {overviewNarrative ? (
                  <DashboardNarrative>{overviewNarrative}</DashboardNarrative>
                ) : null}
                <DashboardMetaStrip>
                  <DashboardMetaText>Last updated {formatDateTime(selectedRunSummary.updatedAt)}</DashboardMetaText>
                </DashboardMetaStrip>
              </DashboardSummaryBlock>

              <RoadmapSectionBlock>
                <RoadmapSectionTitle>What changed</RoadmapSectionTitle>
                <RoadmapSectionBody>{overviewChangeHeadline}</RoadmapSectionBody>
                {overviewChangeNotes.length ? (
                  <RoadmapNoteList>
                    {overviewChangeNotes.map((note, index) => (
                      <RoadmapNoteItem key={`overview-change:${index}`}>{note}</RoadmapNoteItem>
                    ))}
                  </RoadmapNoteList>
                ) : (
                  <EmptyText>
                    {comparisonSummary?.baselineAvailable
                      ? 'No structured change notes were returned for this version.'
                      : 'No earlier version is available yet, so this version is shown as the current baseline.'}
                  </EmptyText>
                )}
              </RoadmapSectionBlock>
            </DashboardColumnStack>
          </motion.div>

          <motion.div {...getDashboardMotionProps(0.08)}>
            <DashboardColumnStack>
              <OverviewMetricGrid>
                {selectedOverviewMetrics.map((metric, index) => (
                  <motion.div
                    key={`selected-overview-metric:${metric.label}`}
                    {...getDashboardMotionProps(0.1 + (index * 0.04))}
                    style={{ minWidth: 0 }}
                  >
                    <DashboardMetricCard>
                      <DashboardMetricTopline>
                        <MetaLabel>{metric.label}</MetaLabel>
                        {comparisonSummary?.baselineAvailable && comparison && metric.delta !== null && metric.delta !== undefined ? (
                          <DashboardMetricDelta
                            $direction={metric.delta > 0 ? 'increase' : metric.delta < 0 ? 'decrease' : 'neutral'}
                          >
                            <DashboardMetricDeltaBar
                              $direction={metric.delta > 0 ? 'increase' : metric.delta < 0 ? 'decrease' : 'neutral'}
                            />
                            {metric.delta > 0 ? '+' : ''}{formatNumber(metric.delta)}
                          </DashboardMetricDelta>
                        ) : null}
                      </DashboardMetricTopline>
                      <DashboardMetricValue>{formatNumber(metric.value)}</DashboardMetricValue>
                      <DashboardMetricMeta title={metric.meta}>{metric.meta}</DashboardMetricMeta>
                    </DashboardMetricCard>
                  </motion.div>
                ))}
              </OverviewMetricGrid>

              <DashboardStatusRail style={{ gridTemplateColumns: '1fr' }}>
                <DashboardStatusPanel>
                  <DashboardStatusGroup>
                    <DashboardStatusLabel>Collaborators</DashboardStatusLabel>
                    <DashboardStatusValue $tone={overviewCollaboratorRows.length ? 'info' : 'neutral'}>
                      {overviewCollaboratorRows.length
                        ? `${formatNumber(overviewCollaboratorRows.length)} additional team${overviewCollaboratorRows.length === 1 ? '' : 's'}`
                        : 'No collaborator teams'}
                    </DashboardStatusValue>
                  </DashboardStatusGroup>
                  {overviewCollaboratorRows.length ? (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {overviewCollaboratorRows.map((entry) => (
                        <div
                          key={`overview-collaborator:${entry.teamKey}`}
                          style={{
                            display: 'grid',
                            gap: 3,
                            paddingTop: 8,
                            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                          }}
                        >
                          <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.4, fontWeight: 900 }}>
                            {entry.teamLabel}
                          </div>
                          <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                            {entry.projectLabels.join(' | ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <DashboardStatusNote $tone="neutral">
                      Primary team only.
                    </DashboardStatusNote>
                  )}
                </DashboardStatusPanel>
                <DashboardStatusPanel>
                  <DashboardStatusGroup>
                    <DashboardStatusLabel>Program versions</DashboardStatusLabel>
                    <DashboardStatusValue $tone="info">
                      Explicit latest, draft, approved, and applied pointers
                    </DashboardStatusValue>
                  </DashboardStatusGroup>
                  <DashboardStatusSubvalue $tone="info">
                    Program-scoped version semantics
                  </DashboardStatusSubvalue>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {selectedInitiativeVersionPointers.map((entry) => (
                      <div
                        key={`initiative-pointer:${entry.key}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          alignItems: 'baseline',
                          paddingTop: 6,
                          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <div style={{ color: '#8f9aae', fontSize: 11, lineHeight: 1.4, fontWeight: 800 }}>
                          {entry.label}
                        </div>
                        <div style={{ display: 'grid', gap: 2, justifyItems: 'end', minWidth: 0 }}>
                          <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.4, fontWeight: 900 }}>
                            {entry.versionLabel}
                          </div>
                          <div style={{ color: '#9aa5b6', fontSize: 10, lineHeight: 1.4, fontWeight: 800 }}>
                            {entry.metaLabel}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </DashboardStatusPanel>
              </DashboardStatusRail>
            </DashboardColumnStack>
          </motion.div>
        </DashboardTopGrid>
      </Section>
    );
  }

  function renderInputsPhase() {
    if (!plannerResult) return null;

    const requiredSignalsPresent = inputSufficiency?.requiredSignalsPresent ?? [];
    const requiredSignalsMissing =
      inputSufficiency?.requiredSignalsMissing.length
        ? inputSufficiency.requiredSignalsMissing
        : inputSufficiency?.missingSignals || [];
    const supportingArtifacts = sourceArtifacts.filter(
      (artifact) => artifact.artifactType !== 'onboarding_note' && artifact.artifactType !== 'recurring_note',
    );
    const meetingNoteEntries = [
      ...(kickoffSource ? [{
        key: `kickoff:${kickoffSource.event.id}`,
        kind: 'kickoff' as const,
        label: 'Kickoff meeting',
        event: kickoffSource.event,
        note: kickoffSource.note,
      }] : []),
      ...recurringNoteSources.map((source) => ({
        key: `recurring:${source.note.id}`,
        kind: 'recurring' as const,
        label: 'Recurring note',
        event: source.event,
        note: source.note,
      })),
    ];
    const purchasedOfferingCount = purchasedLanes.reduce((sum, lane) => {
      const laneOfferings = lane.selectedOfferings.length ? lane.selectedOfferings : lane.selectedOfferingCodes;
      return sum + new Set(laneOfferings.filter(Boolean)).size;
    }, 0);
    const sourceBundleLabel = [
      kickoffSource ? '1 kickoff source' : 'No kickoff source',
      `${formatNumber(recurringNoteSources.length)} recurring note${recurringNoteSources.length === 1 ? '' : 's'}`,
      `${formatNumber(supportingArtifacts.length)} supporting artifact${supportingArtifacts.length === 1 ? '' : 's'}`,
    ].join(' | ');
    const inputSections: Array<{ key: InputExplorerSectionKey; label: string; meta: string }> = [
      {
        key: 'summary',
        label: 'Summary',
        meta: `${inputSufficiency?.status || '--'} | ${formatPercent(plannerResult.planGraph.modeMetadata.confidence)}`,
      },
      {
        key: 'meetings',
        label: 'Meeting notes',
        meta: meetingNoteEntries.length
          ? `${formatNumber(meetingNoteEntries.length)} note${meetingNoteEntries.length === 1 ? '' : 's'}${selectedRunNewerRecurringNoteCount > 0 ? ` | ${formatNumber(selectedRunNewerRecurringNoteCount)} newer recurring` : ''}`
          : 'No meeting references',
      },
      {
        key: 'artifacts',
        label: 'Supporting artifacts',
        meta: supportingArtifacts.length
          ? `${formatNumber(supportingArtifacts.length)} supporting artifact${supportingArtifacts.length === 1 ? '' : 's'}`
          : 'No supporting artifacts',
      },
      {
        key: 'scope',
        label: 'Contract scope',
        meta: `${formatNumber(purchasedLanes.length)} lane${purchasedLanes.length === 1 ? '' : 's'} | ${formatNumber(purchasedOfferingCount)} offering${purchasedOfferingCount === 1 ? '' : 's'}`,
      },
    ];
    const selectedInputSectionMeta = inputSections.find((entry) => entry.key === selectedInputSection) ?? inputSections[0];

    let detailTitle = 'Context summary';
    let detailBody: React.ReactNode = 'This version pulls together meeting references, supporting artifacts, and purchased scope. Use the left rail to inspect each input type without duplicating the Meetings workspace.';
    let detailAction: React.ReactNode = null;
    let detailContent: React.ReactNode = null;

    if (selectedInputSection === 'summary') {
      detailTitle = 'Context summary';
      detailBody = [inputSufficiency?.rationale || 'No context-diagnostic rationale was returned.', planningConfidenceState.summary]
        .filter(Boolean)
        .join(' ');
      detailAction = (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            type="button"
            onClick={() => {
              setDrawerTab('inputs');
              setDrawerOpen(true);
            }}
          >
            Open raw context
          </Button>
        </div>
      );
      detailContent = (
        <>
          <RoadmapMetaGrid>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Source origin</RoadmapMetaLabel>
              <RoadmapMetaValue>{selectedTriggerSummary}</RoadmapMetaValue>
            </RoadmapMetaCell>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Source updated</RoadmapMetaLabel>
              <RoadmapMetaValue>{selectedRunTrigger ? formatDateTime(selectedRunTrigger.sourceUpdatedAt) : 'Started from workspace'}</RoadmapMetaValue>
            </RoadmapMetaCell>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Context status</RoadmapMetaLabel>
              <RoadmapMetaValue>{inputSufficiency?.status || '--'}</RoadmapMetaValue>
            </RoadmapMetaCell>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Confidence</RoadmapMetaLabel>
              <RoadmapMetaValue>{formatPercent(plannerResult.planGraph.modeMetadata.confidence)}</RoadmapMetaValue>
            </RoadmapMetaCell>
          </RoadmapMetaGrid>
          <ExplorerDivider />
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>Included context types</div>
            <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.7, fontWeight: 700 }}>
              {sourceBundleLabel}
            </div>
            <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
              {selectedRunNewerRecurringNoteCount > 0
                ? `${formatNumber(selectedRunNewerRecurringNoteCount)} newer recurring note${selectedRunNewerRecurringNoteCount === 1 ? '' : 's'} exist beyond this saved version.`
                : 'No newer recurring note freshness issues are attached to this version.'}
            </div>
          </div>
          <ExplorerDivider />
          <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <MetaLabel>Signals present</MetaLabel>
              {renderTextList(requiredSignalsPresent, 'No required signals were captured.')}
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <MetaLabel>Signals missing</MetaLabel>
              {renderTextList(requiredSignalsMissing, 'No required signals are currently missing.')}
            </div>
          </div>
        </>
      );
    } else if (selectedInputSection === 'meetings') {
      detailTitle = 'Meeting notes';
      detailBody = meetingNoteEntries.length
        ? 'Kickoff and recurring meeting notes are grouped here as the planning record for this Program version.'
        : 'No kickoff or recurring meeting notes were returned for this version.';
      detailAction = null;
      detailContent = meetingNoteEntries.length ? (
        <ExplorerDetailScroll>
          {meetingNoteEntries.map((entry) => {
            const expanded = expandedInputMeetingId === entry.key;
            const summaryText =
              entry.note?.summary
              || entry.note?.clientUpdates
              || entry.note?.decisions
              || entry.note?.nextSteps
              || entry.note?.internalContext
              || 'No saved meeting summary.';
            return (
              <div
                key={entry.key}
                style={{
                  display: 'grid',
                  gap: 10,
                  paddingTop: 12,
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setExpandedInputMeetingId((current) => (current === entry.key ? null : entry.key));
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'flex-start',
                    border: 0,
                    padding: 0,
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                    <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.4, fontWeight: 900 }}>
                      {entry.event.title}
                    </div>
                    <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                      {`${entry.label} | ${humanizeToken(entry.event.meetingType)} | Meeting ${formatCompactDate(entry.event.startsAt)}${entry.note?.updatedAt ? ` | Note ${formatCompactDate(entry.note.updatedAt)}` : ''}`}
                    </div>
                  </div>
                  <ChevronDown
                    size={16}
                    style={{
                      color: '#8bbcff',
                      flexShrink: 0,
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.18s ease',
                    }}
                  />
                </button>
                {expanded ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ color: '#8f9aae', fontSize: 10, lineHeight: 1.4, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Meeting summary
                    </div>
                    <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.7, fontWeight: 700 }}>
                      {summaryText}
                    </div>
                    {onOpenMeeting ? (
                      <Button type="button" onClick={() => onOpenMeeting(entry.event.id)}>
                        View meeting notes
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </ExplorerDetailScroll>
      ) : <EmptyText>No meeting notes are linked to this version.</EmptyText>;
    } else if (selectedInputSection === 'artifacts') {
      detailTitle = 'Supporting artifacts';
      detailBody = supportingArtifacts.length
        ? 'These non-meeting artifacts add profile or context data beyond the kickoff and recurring note references.'
        : 'No supporting artifacts were returned for this version.';
      detailAction = (
        <Button
          type="button"
          onClick={() => {
            setDrawerTab('inputs');
            setDrawerOpen(true);
          }}
        >
          Open raw context
        </Button>
      );
      detailContent = supportingArtifacts.length ? (
        <ExplorerDetailScroll>
          {supportingArtifacts.map((artifact) => (
            <div
              key={artifact.artifactId}
              style={{
                display: 'grid',
                gap: 6,
                paddingTop: 12,
                borderTop: '1px solid rgba(255, 255, 255, 0.06)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.4, fontWeight: 900 }}>
                  {getArtifactTitle(artifact)}
                </div>
                <div style={{ color: '#8bbcff', fontSize: 10, lineHeight: 1.4, fontWeight: 800 }}>
                  {getArtifactTypeLabel(artifact)}
                </div>
              </div>
              <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                {`Captured ${formatDateTime(artifact.capturedAt)}`}
              </div>
              {renderArtifactPreview(artifact)}
            </div>
          ))}
        </ExplorerDetailScroll>
      ) : <EmptyText>No supporting artifacts were returned for this version.</EmptyText>;
    } else if (selectedInputSection === 'scope') {
      detailTitle = 'Contract scope';
      detailBody = 'Purchased service scope remains the contract boundary. This Program version stays inside those purchased lanes and offerings.';
      detailAction = (
        <Button
          type="button"
          onClick={() => {
            setDrawerTab('context');
            setDrawerOpen(true);
          }}
        >
          Open scope detail
        </Button>
      );
      detailContent = purchasedLanes.length ? (
        <ExplorerDetailScroll>
          {purchasedLanes.map((lane) => {
            const laneOfferings = lane.selectedOfferings.length
              ? lane.selectedOfferings
              : lane.selectedOfferingCodes;
            return (
              <div
                key={lane.laneKey}
                style={{
                  display: 'grid',
                  gap: 8,
                  paddingTop: 12,
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.4, fontWeight: 900 }}>
                    {lane.serviceTitle || lane.laneKey}
                  </div>
                  <div style={{ color: toneForCoverage(lane.coverageStatus) === 'good' ? '#7dd7ab' : toneForCoverage(lane.coverageStatus) === 'warn' ? '#f0bf7c' : '#9aa5b6', fontSize: 10, lineHeight: 1.4, fontWeight: 800 }}>
                    {humanizeToken(lane.coverageStatus)}
                  </div>
                </div>
                {laneOfferings.length ? (
                  <SimpleChipGrid>
                    {laneOfferings.map((offering) => (
                      <SimpleDashedChip key={`${lane.laneKey}:${offering}`}>{offering}</SimpleDashedChip>
                    ))}
                  </SimpleChipGrid>
                ) : (
                  <EmptyText>No offering breakdown was returned for this lane.</EmptyText>
                )}
              </div>
            );
          })}
        </ExplorerDetailScroll>
      ) : <EmptyText>No purchased lanes were returned for this version.</EmptyText>;
    }

    return (
      <Section>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock3 size={16} />
              <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Context</div>
            </div>
            <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
              Context is organized by source type so you can scan what shaped the Program, then open the exact detail you need.
            </div>
          </div>
          <Button
            type="button"
            onClick={() => {
              setDrawerTab('inputs');
              setDrawerOpen(true);
            }}
          >
            Open raw context
          </Button>
        </div>
        <ExplorerShell>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Input types
            </div>
            <ExplorerList>
              {inputSections.map((section) => (
                <ExplorerListButton
                  key={`input-section:${section.key}`}
                  type="button"
                  $active={selectedInputSection === section.key}
                  onClick={() => setSelectedInputSection(section.key)}
                >
                  <ExplorerListLabel>{section.label}</ExplorerListLabel>
                  <ExplorerListMeta>{section.meta}</ExplorerListMeta>
                </ExplorerListButton>
              ))}
            </ExplorerList>
          </div>

          <ExplorerDetail>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', flexWrap: 'wrap' }}>
              <div style={{ display: 'grid', gap: 6 }}>
                <Eyebrow $tone="info">{selectedInputSectionMeta.label}</Eyebrow>
                <div style={{ color: '#f5f7fb', fontSize: 18, lineHeight: 1.35, fontWeight: 900 }}>{detailTitle}</div>
                <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.7, fontWeight: 700 }}>{detailBody}</div>
              </div>
              {detailAction}
            </div>
            <ExplorerDivider />
            {detailContent}
          </ExplorerDetail>
        </ExplorerShell>
      </Section>
    );
  }

  function renderDiscoveryPhase() {
    if (!plannerResult) return null;

    const discoveryNotes = [
      ...plannerResult.planGraph.assumptions,
      ...assumedFacts.map((fact) => fact.statement),
      ...plannerResult.planGraph.notes,
    ].filter((value, index, all) => Boolean(value) && all.indexOf(value) === index);

    return (
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileSearch size={16} />
          <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Discovery</div>
        </div>
        <Eyebrow $tone={toneForPlanMode(inputSufficiency?.planMode)}>
          {formatPlanClassificationLabel(inputSufficiency?.planMode)}
        </Eyebrow>
        <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
          {inputSufficiency?.rationale || 'Discovery shows what the planner learned from the source material, which work types it inferred, and which assumptions still shape the current Program version.'}
        </div>
        <RoadmapMetaGrid>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Discovered needs</RoadmapMetaLabel>
            <RoadmapMetaValue>{formatNumber(discoveredFacts.length)}</RoadmapMetaValue>
          </RoadmapMetaCell>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Inferred work types</RoadmapMetaLabel>
            <RoadmapMetaValue>{formatNumber(inferredWorkTypeLabels.length)}</RoadmapMetaValue>
          </RoadmapMetaCell>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Assumptions and notes</RoadmapMetaLabel>
            <RoadmapMetaValue>{formatNumber(discoveryNotes.length)}</RoadmapMetaValue>
          </RoadmapMetaCell>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Confidence</RoadmapMetaLabel>
            <RoadmapMetaValue>{formatPercent(plannerResult.planGraph.modeMetadata.confidence)}</RoadmapMetaValue>
          </RoadmapMetaCell>
        </RoadmapMetaGrid>

        <RoadmapDivider />

        <RoadmapSectionBlock>
          <RoadmapSectionTitle>Discovered needs</RoadmapSectionTitle>
          <RoadmapSectionBody>
            These are the concrete needs the planner pulled from the source material and treated as meaningful inputs to the Program.
          </RoadmapSectionBody>
          {discoveredFacts.length ? (
            <RoadmapNoteList>
              {discoveredFacts.map((fact) => (
                <RoadmapNoteItem key={fact.factId}>
                  <strong>{[humanizeToken(fact.classification), fact.category].filter(Boolean).join(' | ')}</strong>
                  <br />
                  {fact.statement}
                </RoadmapNoteItem>
              ))}
            </RoadmapNoteList>
          ) : (
            <EmptyText>No discovered needs were returned for this version.</EmptyText>
          )}
        </RoadmapSectionBlock>

        <RoadmapDivider />

        <RoadmapSectionBlock>
          <RoadmapSectionTitle>Inferred work types</RoadmapSectionTitle>
          <RoadmapSectionBody>
            These work types show how the planner grouped the discovered needs into actionable categories for the Program.
          </RoadmapSectionBody>
          {inferredWorkTypeLabels.length ? (
            <SimpleChipGrid>
              {inferredWorkTypeLabels.map((label) => (
                <SimpleDashedChip key={`discovery-work-type:${label}`}>{label}</SimpleDashedChip>
              ))}
            </SimpleChipGrid>
          ) : (
            <EmptyText>No inferred work types were returned for this version.</EmptyText>
          )}
        </RoadmapSectionBlock>

        <RoadmapDivider />

        <RoadmapSectionBlock>
          <RoadmapSectionTitle>Assumptions and notes</RoadmapSectionTitle>
          <RoadmapSectionBody>
            These are the planner assumptions, unresolved notes, and inferred context still shaping the current draft.
          </RoadmapSectionBody>
          {discoveryNotes.length ? (
            <RoadmapNoteList>
              {discoveryNotes.map((note, index) => (
                <RoadmapNoteItem key={`discovery-note:${index}`}>{note}</RoadmapNoteItem>
              ))}
            </RoadmapNoteList>
          ) : (
            <EmptyText>No assumptions or notes were returned for this version.</EmptyText>
          )}
        </RoadmapSectionBlock>
      </Section>
    );
  }

  function renderRoadmapPhase() {
    if (!plannerResult) return null;

    const selectedRoadmapProjectSummary = selectedRoadmapProject
      ? roadmapProjectSummaries.find((entry) => entry.projectKey === selectedRoadmapProject.project.key) ?? null
      : null;
    const roadmapMilestoneCount = roadmapProjectCards.reduce((sum, entry) => sum + entry.milestones.length, 0);
    const roadmapDeliverableCount = roadmapProjectCards.reduce(
      (sum, entry) => sum + entry.milestones.reduce((milestoneSum, milestoneEntry) => milestoneSum + milestoneEntry.deliverables.length, 0),
      0,
    );
    const roadmapTicketCount = roadmapProjectCards.reduce(
      (sum, entry) => sum + entry.milestones.reduce((milestoneSum, milestoneEntry) => milestoneSum + milestoneEntry.tickets.length, 0),
      0,
    );
    const roadmapSpecialistNotes = [
      ...(plannerResult.runSummary.specialistSummary?.influenceSummaries ?? []),
      ...specialistProvenanceNotes,
    ].filter((value, index, all) => Boolean(value) && all.indexOf(value) === index);
    const roadmapChangeHeadline = humanizedComparisonHeadline
      || (
        comparisonSummary?.baselineAvailable
          ? `Changes from ${baselineVersionLabel} to ${selectedVersionLabel}`
          : `${selectedVersionLabel} is the current saved roadmap.`
      );
    const roadmapChangeNotes = humanizedComparisonBullets.length
      ? humanizedComparisonBullets
      : comparison?.comparability.warnings.length
        ? comparison.comparability.warnings
        : [];
    const selectedRoadmapTickets = selectedRoadmapProject
      ? selectedRoadmapProject.milestones.flatMap(({ tickets }) => tickets)
      : [];
    const selectedRoadmapReadyTickets = selectedRoadmapProject
      ? selectedRoadmapTickets.filter((ticket) => {
          const hasResolvedScope = Boolean(
            text(ticket.offeringSubcategoryId)
            || text(ticket.executionOfferingSubcategoryId)
            || text(selectedRoadmapProject.project.offeringSubcategoryId)
            || text(selectedRoadmapProject.project.executionOfferingSubcategoryId),
          );
          const hasResolvedTeam = Boolean(
            text(ticket.proposedExecutionTeamNodeId)
            || text(selectedRoadmapProject.project.teamNodeId)
            || text(selectedRoadmapProject.project.executionServiceCode),
          );
          return hasResolvedScope && hasResolvedTeam;
        }).length
      : 0;
    const selectedRoadmapProgressPercent = selectedRoadmapTickets.length
      ? Math.round((selectedRoadmapReadyTickets / selectedRoadmapTickets.length) * 100)
      : 0;
    const selectedRoadmapProjectNotes = selectedRoadmapProject
      ? (() => {
          const searchTerms = [
            selectedRoadmapProject.project.name,
            selectedRoadmapProject.project.objective,
            selectedRoadmapProject.project.brief,
            humanizeToken(selectedRoadmapProject.project.offeringSubcategoryId || ''),
            humanizeToken(selectedRoadmapProject.project.executionOfferingCode || ''),
            humanizeToken(selectedRoadmapProject.project.executionOfferingSubcategoryId || ''),
          ]
            .flatMap((value) => text(value).toLowerCase().split(/\s+/))
            .map((value) => value.trim())
            .filter((value) => value.length >= 4);
          const matchedNotes = roadmapSpecialistNotes.filter((note) => {
            const normalizedNote = note.toLowerCase();
            return searchTerms.some((term) => normalizedNote.includes(term));
          });
          return matchedNotes.length ? matchedNotes : roadmapSpecialistNotes.slice(0, 3);
        })()
      : roadmapSpecialistNotes;
    const selectedRoadmapProjectEntityKeys = selectedRoadmapProject
      ? new Set([
          selectedRoadmapProject.project.key,
          ...selectedRoadmapProject.milestones.map(({ milestone }) => milestone.key),
          ...selectedRoadmapTickets.map((ticket) => ticket.key),
        ])
      : null;
    const selectedRoadmapProjectSubcategoryDecisions = selectedRoadmapProjectEntityKeys
      ? subcategoryDecisions.filter((entry) => entry.entityKeys.some((key) => selectedRoadmapProjectEntityKeys.has(key)))
      : [];
    const selectedRoadmapProjectKbDecisions = selectedRoadmapProjectEntityKeys
      ? kbProcessDecisions.filter((entry) => entry.entityKeys.some((key) => selectedRoadmapProjectEntityKeys.has(key)))
      : [];
    const selectedRoadmapProjectTaxonomyPairCount = new Set([
      ...selectedRoadmapProjectSubcategoryDecisions.map((entry) => entry.pairedOperationId).filter(Boolean),
      ...selectedRoadmapProjectKbDecisions.map((entry) => entry.pairedOperationId).filter(Boolean),
    ]).size;

    const renderRoadmapOverview = () => (
      <>
        <RoadmapTopline>
          <RoadmapHeaderBlock>
            <Eyebrow $tone="info">Roadmap overview</Eyebrow>
            <RoadmapHeadline>{selectedInitiative?.objective || outcomeSummary}</RoadmapHeadline>
            <RoadmapSubtitle>
              {selectedRunStatusSummary
                ? `${selectedRunStatusSummary}. Select a project on the left to inspect the detailed roadmap.`
                : 'Select a project on the left to inspect the detailed roadmap.'}
            </RoadmapSubtitle>
          </RoadmapHeaderBlock>
        </RoadmapTopline>

        <RoadmapMetaGrid>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Offering</RoadmapMetaLabel>
            <RoadmapMetaValue>{selectedInitiativeScopeLabel}</RoadmapMetaValue>
          </RoadmapMetaCell>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Primary team</RoadmapMetaLabel>
            <RoadmapMetaValue>{selectedProgramExecutionSummary.primaryTeamLabel}</RoadmapMetaValue>
          </RoadmapMetaCell>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Roadmap coverage</RoadmapMetaLabel>
            <RoadmapMetaValue>{`${formatNumber(roadmapProjectCards.length)} projects | ${formatNumber(roadmapMilestoneCount)} milestones`}</RoadmapMetaValue>
          </RoadmapMetaCell>
          <RoadmapMetaCell>
            <RoadmapMetaLabel>Collaboration</RoadmapMetaLabel>
            <RoadmapMetaValue>
              {selectedProgramExecutionSummary.collaboratorTeamsLabel || 'No collaborator teams are attached to this roadmap yet.'}
            </RoadmapMetaValue>
          </RoadmapMetaCell>
        </RoadmapMetaGrid>

        <RoadmapDivider />

        <RoadmapSectionBlock>
          <RoadmapSectionTitle>Program summary</RoadmapSectionTitle>
          <RoadmapSectionBody>
            {selectedInitiative?.objective || outcomeSummary || 'No Program summary was returned for this roadmap.'}
          </RoadmapSectionBody>
          <RoadmapSectionBody>
            {`${formatNumber(roadmapProjectCards.length)} projects, ${formatNumber(roadmapMilestoneCount)} milestones, ${formatNumber(roadmapDeliverableCount)} deliverables, and ${formatNumber(roadmapTicketCount)} tickets are currently mapped across ${formatNumber(roadmapTeamCount)} teams.`}
          </RoadmapSectionBody>
          {selectedProgramExecutionSummary.projectScopeLabel ? (
            <RoadmapSectionBody>
              {`Primary project scope: ${selectedProgramExecutionSummary.projectScopeLabel}.`}
            </RoadmapSectionBody>
          ) : null}
        </RoadmapSectionBlock>

        <RoadmapDivider />

        <RoadmapSectionBlock>
          <RoadmapSectionTitle>What changed</RoadmapSectionTitle>
          <RoadmapSectionBody>{roadmapChangeHeadline}</RoadmapSectionBody>
          {roadmapChangeNotes.length ? (
            <RoadmapNoteList>
              {roadmapChangeNotes.map((note, index) => (
                <RoadmapNoteItem key={`roadmap-change:${index}`}>{note}</RoadmapNoteItem>
              ))}
            </RoadmapNoteList>
          ) : (
            <EmptyText>
              {comparisonSummary?.baselineAvailable
                ? 'No structured change notes were returned for this version.'
                : 'No earlier version is available yet, so this roadmap is shown as the current baseline.'}
            </EmptyText>
          )}
        </RoadmapSectionBlock>

        <RoadmapDivider />

        <RoadmapSectionBlock>
          <RoadmapSectionTitle>Specialist notes</RoadmapSectionTitle>
          <RoadmapSectionBody>{specialistInfluence.summary}</RoadmapSectionBody>
          {roadmapSpecialistNotes.length ? (
            <RoadmapNoteList>
              {roadmapSpecialistNotes.map((note, index) => (
                <RoadmapNoteItem key={`roadmap-specialist:${index}`}>{note}</RoadmapNoteItem>
              ))}
            </RoadmapNoteList>
          ) : (
            <EmptyText>No specialist notes were returned for this roadmap.</EmptyText>
          )}
        </RoadmapSectionBlock>
      </>
    );

    const renderSelectedRoadmapProject = () => {
      if (!selectedRoadmapProject) return null;

      return (
        <>
          <RoadmapTopline>
            <RoadmapHeaderBlock>
              <RoadmapOverviewButton type="button" onClick={() => setSelectedRoadmapProjectKey(null)}>
                Program overview
              </RoadmapOverviewButton>
              <Eyebrow $tone="info">
                {`Project ${selectedRoadmapProjectSummary?.order ?? 1} of ${formatNumber(roadmapProjectCards.length)}`}
              </Eyebrow>
              <RoadmapHeadline>{selectedRoadmapProject.project.name}</RoadmapHeadline>
              <RoadmapSubtitle>
                {selectedRoadmapProject.project.brief
                  || selectedRoadmapProject.project.objective
                  || 'No project brief was returned for this roadmap step.'}
              </RoadmapSubtitle>
            </RoadmapHeaderBlock>

            <RoadmapMetric>
              <RoadmapMetricRing $percent={selectedRoadmapProgressPercent} $muted={!selectedRoadmapTickets.length}>
                <RoadmapMetricRingInner>
                  <RoadmapMetricValue>{selectedRoadmapTickets.length ? `${selectedRoadmapProgressPercent}%` : '--'}</RoadmapMetricValue>
                  <RoadmapMetricSubvalue>{selectedRoadmapTickets.length ? `${selectedRoadmapReadyTickets}/${selectedRoadmapTickets.length}` : 'No tickets'}</RoadmapMetricSubvalue>
                </RoadmapMetricRingInner>
              </RoadmapMetricRing>
              <RoadmapMetricLabel>Roadmap completeness</RoadmapMetricLabel>
              <RoadmapMetricNote>
                Planner output does not expose live closed-ticket state yet. This metric reflects how many tickets in the roadmap are fully defined with scope and team ownership.
              </RoadmapMetricNote>
            </RoadmapMetric>
          </RoadmapTopline>

          <RoadmapMetaGrid>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Project category</RoadmapMetaLabel>
              <RoadmapMetaValue>
                {text(selectedRoadmapProject.project.offeringSubcategoryId)
                  ? humanizeToken(selectedRoadmapProject.project.offeringSubcategoryId)
                  : 'Pending classification'}
              </RoadmapMetaValue>
            </RoadmapMetaCell>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Responsible team</RoadmapMetaLabel>
              <RoadmapMetaValue>{formatTeamLabel(selectedRoadmapProject.project.teamNodeId, teamLabelMap)}</RoadmapMetaValue>
            </RoadmapMetaCell>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Collaborators</RoadmapMetaLabel>
              <RoadmapMetaValue>
                {selectedRoadmapProject.project.supportingTeamNodeIds.length
                  ? formatTeamList(selectedRoadmapProject.project.supportingTeamNodeIds, teamLabelMap).join(', ')
                  : 'Program team only'}
              </RoadmapMetaValue>
            </RoadmapMetaCell>
            <RoadmapMetaCell>
              <RoadmapMetaLabel>Execution route</RoadmapMetaLabel>
              <RoadmapMetaValue>{selectedRoadmapProjectSummary?.routeLabel || 'Primary team delivery'}</RoadmapMetaValue>
            </RoadmapMetaCell>
          </RoadmapMetaGrid>

          <RoadmapDivider />

          <RoadmapSectionBlock>
            <RoadmapSectionTitle>Project brief</RoadmapSectionTitle>
            <RoadmapSectionBody>
              {selectedRoadmapProject.project.brief
                || selectedRoadmapProject.project.objective
                || 'No project brief was returned for this roadmap step.'}
            </RoadmapSectionBody>
            <RoadmapSectionBody>
              {selectedRoadmapProject.project.objective
                ? `Why this project is included: ${selectedRoadmapProject.project.objective}`
                : `Why this project is included: this work moves ${selectedInitiativeScopeLabel} toward the overall Program outcome.`}
            </RoadmapSectionBody>
          </RoadmapSectionBlock>

          <RoadmapDivider />

          <RoadmapSectionBlock>
            <RoadmapSectionTitle>Specialist notes</RoadmapSectionTitle>
            {selectedRoadmapProjectNotes.length ? (
              <RoadmapNoteList>
                {selectedRoadmapProjectNotes.map((note, index) => (
                  <RoadmapNoteItem key={`roadmap-project-specialist:${selectedRoadmapProject.project.key}:${index}`}>{note}</RoadmapNoteItem>
                ))}
              </RoadmapNoteList>
            ) : (
              <EmptyText>No specialist notes were attached to this project.</EmptyText>
            )}
          </RoadmapSectionBlock>

          <RoadmapDivider />

          <RoadmapSectionBlock>
            <RoadmapSectionTitle>Milestones and deliverables</RoadmapSectionTitle>
            {selectedRoadmapProject.milestones.length ? (
              <RoadmapMilestoneList>
                {selectedRoadmapProject.milestones.map(({ milestone, deliverables }, milestoneIndex) => (
                  <RoadmapMilestoneBlock key={`roadmap-milestone:${milestone.key}`}>
                    <RoadmapMilestoneTitle>{`Milestone ${milestoneIndex + 1}: ${milestone.title}`}</RoadmapMilestoneTitle>
                    {milestone.outcome ? (
                      <RoadmapMilestoneOutcome>{milestone.outcome}</RoadmapMilestoneOutcome>
                    ) : null}
                    {deliverables.length ? (
                      <RoadmapSublist>
                        {deliverables.map((deliverable) => (
                          <li key={`roadmap-deliverable:${deliverable.key}`}>{deliverable.title}</li>
                        ))}
                      </RoadmapSublist>
                    ) : (
                      <EmptyText>No deliverables were returned for this milestone.</EmptyText>
                    )}
                  </RoadmapMilestoneBlock>
                ))}
              </RoadmapMilestoneList>
            ) : (
              <EmptyText>No milestones were returned for this project.</EmptyText>
            )}
          </RoadmapSectionBlock>

          <RoadmapDivider />

          <RoadmapSectionBlock>
            <RoadmapSectionTitle>Ticket sequence</RoadmapSectionTitle>
            {selectedRoadmapTickets.length ? (
              <RoadmapTicketList>
                {selectedRoadmapTickets.map((ticket, ticketIndex) => (
                  <RoadmapTicketItem key={`roadmap-ticket:${ticket.key}`}>
                    <RoadmapTicketTitle>{`${ticketIndex + 1}. ${ticket.title}`}</RoadmapTicketTitle>
                    <RoadmapTicketMeta>
                      {[
                        formatTeamLabel(ticket.proposedExecutionTeamNodeId ?? selectedRoadmapProject.project.teamNodeId, teamLabelMap),
                        text(ticket.offeringSubcategoryId)
                          ? humanizeToken(ticket.offeringSubcategoryId)
                          : text(ticket.executionOfferingSubcategoryId)
                            ? humanizeToken(ticket.executionOfferingSubcategoryId)
                            : 'Subcategory pending',
                      ].join(' | ')}
                    </RoadmapTicketMeta>
                    {ticket.description ? (
                      <RoadmapSectionBody>{ticket.description}</RoadmapSectionBody>
                    ) : null}
                  </RoadmapTicketItem>
                ))}
              </RoadmapTicketList>
            ) : (
              <EmptyText>No tickets were returned for this project.</EmptyText>
            )}
          </RoadmapSectionBlock>

          <RoadmapDivider />

          <RoadmapSectionBlock>
            <RoadmapSectionTitle>Taxonomy and KBase</RoadmapSectionTitle>
            <RoadmapSectionBody>
              Classification lives with the project execution details. This shows the subcategories and KBase processes this project reused or proposed so the ticket sequence can stay properly scoped.
            </RoadmapSectionBody>
            {(selectedRoadmapProjectSubcategoryDecisions.length || selectedRoadmapProjectKbDecisions.length) ? (
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                  {`${formatNumber(selectedRoadmapProjectTaxonomyPairCount)} paired operation${selectedRoadmapProjectTaxonomyPairCount === 1 ? '' : 's'} | ${formatNumber(selectedRoadmapProjectSubcategoryDecisions.length)} subcategory decision${selectedRoadmapProjectSubcategoryDecisions.length === 1 ? '' : 's'} | ${formatNumber(selectedRoadmapProjectKbDecisions.length)} KBase decision${selectedRoadmapProjectKbDecisions.length === 1 ? '' : 's'}`}
                </div>
                {selectedRoadmapProjectSubcategoryDecisions.length ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <RoadmapSectionBody style={{ margin: 0 }}>Subcategories</RoadmapSectionBody>
                    <RoadmapNoteList>
                      {selectedRoadmapProjectSubcategoryDecisions.map((entry) => (
                        <RoadmapNoteItem key={`roadmap-project-taxonomy:${entry.decisionKey}`}>
                          <strong>{entry.existingSubcategoryName || entry.inferredSubcategoryName}</strong>
                          <br />
                          {`${humanizeToken(entry.status)} under ${entry.parentOfferingName || humanizeToken(entry.offeringCode)}.`}
                          {entry.pairedOperationId ? ' Paired KBase expansion is linked.' : ''}
                          <br />
                          {entry.rationale}
                        </RoadmapNoteItem>
                      ))}
                    </RoadmapNoteList>
                  </div>
                ) : null}
                {selectedRoadmapProjectKbDecisions.length ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <RoadmapSectionBody style={{ margin: 0 }}>KBase processes</RoadmapSectionBody>
                    <RoadmapNoteList>
                      {selectedRoadmapProjectKbDecisions.map((entry) => (
                        <RoadmapNoteItem key={`roadmap-project-kbase:${entry.decisionKey}`}>
                          <strong>{entry.existingProcessTitle || entry.processTitle}</strong>
                          <br />
                          {`${humanizeToken(entry.status)} for ${entry.inferredSubcategoryName}.`}
                          {entry.pairedOperationId ? ' Paired taxonomy expansion is linked.' : ''}
                          <br />
                          {entry.summary || entry.rationale}
                        </RoadmapNoteItem>
                      ))}
                    </RoadmapNoteList>
                  </div>
                ) : null}
              </div>
            ) : (
              <EmptyText>No taxonomy or KBase changes were attached to this project.</EmptyText>
            )}
          </RoadmapSectionBlock>
        </>
      );
    };

    return (
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FolderTree size={16} />
          <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Roadmap</div>
        </div>
        <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
          The roadmap is the clearest view of this Program. Use the project rail to inspect the ordered work, assigned teams, milestones, deliverables, and ticket sequence that move the Program to completion.
        </div>

        <RoadmapShell>
          <RoadmapSidebar>
            <div style={{ display: 'grid', gap: 4 }}>
              <RoadmapSidebarTitle>Projects</RoadmapSidebarTitle>
              <RoadmapSidebarHint>
                Project order sets the execution flow. Earlier projects can unblock later work. Click a project to inspect its detail, or leave all projects unselected to keep the roadmap overview visible.
              </RoadmapSidebarHint>
            </div>
            {roadmapProjectCards.length ? (
              <RoadmapProjectRail>
                {roadmapProjectCards.map(({ project }) => {
                  const summary = roadmapProjectSummaries.find((entry) => entry.projectKey === project.key);
                  const orderLabel = String(summary?.order ?? 1).padStart(2, '0');
                  return (
                    <RoadmapProjectButton
                      key={`roadmap-step:${project.key}`}
                      type="button"
                      $active={selectedRoadmapProject?.project.key === project.key}
                      onClick={() => {
                        setSelectedRoadmapProjectKey((current) => (current === project.key ? null : project.key));
                      }}
                    >
                      <RoadmapProjectOrderBadge $active={selectedRoadmapProject?.project.key === project.key}>
                        {orderLabel}
                      </RoadmapProjectOrderBadge>
                      <RoadmapProjectTitle>{project.name}</RoadmapProjectTitle>
                      <RoadmapProjectTeam>{summary?.teamLabel || formatTeamLabel(project.teamNodeId, teamLabelMap)}</RoadmapProjectTeam>
                    </RoadmapProjectButton>
                  );
                })}
              </RoadmapProjectRail>
            ) : (
              <EmptyText>No roadmap projects were returned for this Program version yet.</EmptyText>
            )}
          </RoadmapSidebar>

          <RoadmapMain>
            {selectedRoadmapProject ? renderSelectedRoadmapProject() : renderRoadmapOverview()}
          </RoadmapMain>
        </RoadmapShell>
      </Section>
    );
  }
  function renderReviewApplyPhase() {
    if (!plannerResult) return null;

    return (
      <>
        {(hasDecisionOrApplyAction || selectedRunAttentionItems.length || reviewHistory.length || applyHistory.length) ? (
          <Section>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle2 size={16} />
              <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Review &amp; Apply</div>
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {hasDecisionOrApplyAction ? (
                <Grid $cols="repeat(2, minmax(0, 1fr))">
                  <Card>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'start' }}>
                        <div style={{ display: 'grid', gap: 4 }}>
                          <Eyebrow $tone={reviewStatusTone}>Review</Eyebrow>
                          <div style={{ color: '#f5f7fb', fontSize: 18, fontWeight: 900 }}>
                            {formatDraftReviewStatusLabel(reviewStatus)}
                          </div>
                        </div>
                        <div style={{ display: 'grid', gap: 6, justifyItems: 'end', textAlign: 'right' }}>
                          <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 700 }}>
                            {latestReviewEvent?.createdAt ? formatDateTime(latestReviewEvent?.createdAt ?? '') : 'Waiting for review'}
                          </div>
                          <AccordionToggle
                            type="button"
                            onClick={() => setReviewApplyOpenPanel('review')}
                            aria-expanded={reviewApplyOpenPanel === 'review'}
                            style={{ width: 'auto', justifyItems: 'end' }}
                          >
                            <ChevronDown
                              size={16}
                              style={{
                                color: '#cdd5e0',
                                transform: reviewApplyOpenPanel === 'review' ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 180ms ease',
                              }}
                            />
                          </AccordionToggle>
                        </div>
                      </div>
                      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                        {reviewSummaryText}
                      </div>
                      {latestReviewEvent ? (
                        <div style={{ color: '#8f9aae', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                          Reviewed by {latestReviewEvent?.actedByDisplayName || latestReviewEvent?.actedByUserId || 'System'}
                        </div>
                      ) : null}

                      <AnimatePresence initial={false}>
                        {reviewApplyOpenPanel === 'review' ? (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                            style={{ overflow: 'hidden' }}
                          >
                            <div style={{ display: 'grid', gap: 12 }}>
                              {showReviewComposer ? (
                                <>
                                  <TextArea
                                    value={reviewNote}
                                    onChange={(event) => setReviewNote(event.target.value)}
                                    placeholder="Optional reviewer note"
                                  />
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {(reviewStatus === 'review_required' || reviewStatus === 'needs_revision' || reviewStatus === 'rejected_draft') ? (
                                      <Button
                                        type="button"
                                        $primary
                                        onClick={() => void handleReviewAction('approve')}
                                        disabled={reviewActionLoading !== null}
                                      >
                                        {reviewActionLoading === 'approve' ? 'Approving...' : 'Approve'}
                                      </Button>
                                    ) : null}
                                    {(reviewStatus === 'review_required' || reviewStatus === 'rejected_draft') ? (
                                      <Button
                                        type="button"
                                        onClick={() => void handleReviewAction('request_revision')}
                                        disabled={reviewActionLoading !== null}
                                      >
                                        {reviewActionLoading === 'request_revision' ? 'Saving...' : 'Request revision'}
                                      </Button>
                                    ) : null}
                                    {(reviewStatus === 'review_required' || reviewStatus === 'needs_revision') ? (
                                      <Button
                                        type="button"
                                        onClick={() => void handleReviewAction('reject')}
                                        disabled={reviewActionLoading !== null}
                                      >
                                        {reviewActionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                                      </Button>
                                    ) : null}
                                  </div>
                                </>
                              ) : canAdjustApprovedReview ? (
                                <div style={{ display: 'grid', gap: 10 }}>
                                  <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                    Approval is already recorded. Only change it if this version should move back out of the apply path.
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    <Button
                                      type="button"
                                      onClick={() => setShowReviewDecisionEditor((current) => !current)}
                                    >
                                      {showReviewDecisionEditor ? 'Hide review controls' : 'Change review decision'}
                                    </Button>
                                  </div>
                                  {showReviewDecisionEditor ? (
                                    <>
                                      <TextArea
                                        value={reviewNote}
                                        onChange={(event) => setReviewNote(event.target.value)}
                                        placeholder="Optional reviewer note"
                                      />
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        <Button
                                          type="button"
                                          onClick={() => void handleReviewAction('request_revision')}
                                          disabled={reviewActionLoading !== null}
                                        >
                                          {reviewActionLoading === 'request_revision' ? 'Saving...' : 'Request revision'}
                                        </Button>
                                        <Button
                                          type="button"
                                          onClick={() => void handleReviewAction('reject')}
                                          disabled={reviewActionLoading !== null}
                                        >
                                          {reviewActionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                                        </Button>
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </Card>
                  {(showApplyExecutionPanel || Boolean(applyEligibilityState.actionLabel)) ? (
                    <Card>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                          <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                            <Eyebrow $tone={applyStatusTone}>Apply</Eyebrow>
                            <div style={{ color: '#f5f7fb', fontSize: 18, fontWeight: 900 }}>
                              {applyHeadline}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gap: 6, justifyItems: 'end', textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 700 }}>
                              {latestApplyExecution?.updatedAt ? formatDateTime(latestApplyExecution?.updatedAt ?? '') : applyEligibilityState.eligible ? 'Ready to run' : 'Not available'}
                            </div>
                            <AccordionToggle
                              type="button"
                              onClick={() => setReviewApplyOpenPanel('apply')}
                              aria-expanded={reviewApplyOpenPanel === 'apply'}
                              style={{ width: 'auto', justifyItems: 'end' }}
                            >
                              <ChevronDown
                                size={16}
                                style={{
                                  color: '#cdd5e0',
                                  transform: reviewApplyOpenPanel === 'apply' ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 180ms ease',
                                }}
                              />
                            </AccordionToggle>
                          </div>
                        </div>
                        <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                          {applySummaryText}
                        </div>
                        <AnimatePresence initial={false}>
                          {reviewApplyOpenPanel === 'apply' ? (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                              style={{ overflow: 'hidden' }}
                            >
                              <div style={{ display: 'grid', gap: 12, paddingTop: 12, borderTop: '1px dashed rgba(255,255,255,.12)' }}>
                                {applyEligibilityState.actionLabel ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    <Button
                                      type="button"
                                      $primary
                                      onClick={() => void handleApplyAction()}
                                      disabled={!applyEligibilityState.eligible || applyActionLoading || applyLoading}
                                    >
                                      {applyActionLoading ? 'Applying...' : applyEligibilityState.actionLabel}
                                    </Button>
                                  </div>
                                ) : null}
                              </div>
                            </motion.div>
                          ) : null}
                        </AnimatePresence>
                      </div>
                    </Card>
                  ) : null}
                </Grid>
              ) : null}

              <Grid $cols="repeat(2, minmax(0, 1fr))">
                <Card>
                  <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Review timeline</div>
                  {reviewHistory.length ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      {[...reviewHistory]
                        .slice(-4)
                        .reverse()
                        .map((event) => (
                          <div key={event.reviewEventId} style={{ display: 'grid', gap: 3 }}>
                            <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>
                              {`${formatDraftReviewStatusLabel(event.fromReviewStatus)} -> ${formatDraftReviewStatusLabel(event.toReviewStatus)}`}
                            </div>
                            <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                              {event.note || 'No reviewer note recorded.'}
                            </div>
                            <div style={{ color: '#7f8a9a', fontSize: 10, fontWeight: 700 }}>
                              {event.actedByDisplayName || event.actedByUserId || 'System'} | {formatDateTime(event.createdAt)}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : <EmptyText>No review history has been recorded yet.</EmptyText>}
                </Card>
                <Card>
                  <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Apply attempts</div>
                  {applyHistory.length ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      {[...applyHistory]
                        .slice(-4)
                        .reverse()
                        .map((apply) => (
                          <div key={apply.applyId} style={{ display: 'grid', gap: 3 }}>
                            <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>
                              {formatApplyStatusLabel(apply.status)} | {formatDateTime(apply.updatedAt)}
                            </div>
                            <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                              {apply.errorSummary || `${formatNumber(apply.operations.length)} operation${apply.operations.length === 1 ? '' : 's'} recorded.`}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : <EmptyText>No apply attempts have been recorded yet.</EmptyText>}
                </Card>
              </Grid>

              <Grid $cols="repeat(2, minmax(0, 1fr))">
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', flexWrap: 'wrap' }}>
                    <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Attention queue</div>
                    <Button
                      type="button"
                      onClick={() => {
                        setDrawerTab('diagnostics');
                        setDrawerOpen(true);
                      }}
                    >
                      Open logs
                    </Button>
                  </div>
                  {selectedRunAttentionItems.length ? (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {selectedRunAttentionItems.map((item) => {
                        const tone = getAttentionItemTone(item);
                        return (
                          <Card key={item.attentionItemId}>
                            <MetaRow>
                              <MetaItem>
                                <MetaLabel>Type</MetaLabel>
                                <MetaValue $tone={tone}>{formatAttentionItemTypeLabel(item.itemType)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Status</MetaLabel>
                                <MetaValue>{formatAttentionStatusLabel(item.status)}</MetaValue>
                              </MetaItem>
                            </MetaRow>
                            <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{item.reasonSummary}</div>
                            <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                              {item.sourceSummary || 'No additional source summary was recorded.'}
                            </div>
                            {item.status === 'open' ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                <Button
                                  type="button"
                                  onClick={() => void handleAttentionAction(item, 'resolve')}
                                  disabled={attentionActionLoading !== null}
                                >
                                  {attentionActionLoading === `${item.attentionItemId}:resolve` ? 'Resolving...' : 'Resolve'}
                                </Button>
                                <Button
                                  type="button"
                                  onClick={() => void handleAttentionAction(item, 'dismiss')}
                                  disabled={attentionActionLoading !== null}
                                >
                                  {attentionActionLoading === `${item.attentionItemId}:dismiss` ? 'Dismissing...' : 'Dismiss'}
                                </Button>
                              </div>
                            ) : null}
                          </Card>
                        );
                      })}
                    </div>
                  ) : <EmptyText>No run-scoped attention items are open.</EmptyText>}
                </Card>
                <Card>
                  <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Blocked or deferred operations</div>
                  {applyBlockedOperations.length ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      {applyBlockedOperations.slice(0, 6).map((operation) => (
                        <div key={operation.applyOperationId} style={{ display: 'grid', gap: 3 }}>
                          <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>
                            {humanizeToken(operation.action)} | {operation.entityKey}
                          </div>
                          <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                            {operation.notes[0] || `Status ${humanizeToken(operation.status)}.`}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                      {latestApplyExecution
                        ? 'No blocked or deferred operation reasons were recorded on the latest apply.'
                        : applyEligibilityState.summary}
                    </div>
                  )}
                  {(subcategoryDecisions.length || kbProcessDecisions.length) ? (
                    <>
                      <MetaLabel>Taxonomy + KBase summary</MetaLabel>
                      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                        {`${formatNumber(pairedOperations.size)} paired operation${pairedOperations.size === 1 ? '' : 's'} | ${formatNumber(reviewRequiredExpansionCount)} review-required expansion${reviewRequiredExpansionCount === 1 ? '' : 's'}`}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <Button
                          type="button"
                          onClick={() => {
                            setDrawerTab('taxonomy');
                            setDrawerOpen(true);
                          }}
                        >
                          Open taxonomy + KBase details
                        </Button>
                      </div>
                    </>
                  ) : null}
                </Card>
              </Grid>
            </div>
          </Section>
        ) : null}

        <Section>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={16} />
            <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Validation signals</div>
          </div>
          <Eyebrow $tone={validationTone(plannerResult.validationResults)}>
            {humanizeToken(plannerResult.approvalGate.status)}
          </Eyebrow>
          <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
            Validation shows the planner's warnings, failures, and gate reasons for this version. Human review and explicit apply actions remain separate.
          </div>
          <Grid $cols="repeat(2, minmax(0, 1fr))">
            <Card>
              <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Approval gate reasons</div>
              {renderTextList(plannerResult.approvalGate.reasons, 'No approval gate reasons were returned.')}
            </Card>
            <Card>
              <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Validation findings</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {[...validationFailures, ...validationWarnings].length ? [...validationFailures, ...validationWarnings].slice(0, 6).map((entry) => (
                  <div key={`${entry.ruleName}:${entry.message}`} style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                    {entry.ruleName}: {entry.message}
                  </div>
                )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No warnings or failures returned.</div>}
              </div>
            </Card>
          </Grid>
        </Section>
      </>
    );
  }

  useEffect(() => {
    if (!selectedRunId) {
      setReviewApplyOpenPanel('review');
      return;
    }
    setReviewApplyOpenPanel(defaultLifecyclePanel);
  }, [defaultLifecyclePanel, selectedRunId]);

  useEffect(() => {
    setSelectedVersionPhase('overview');
    setSelectedInputSection('summary');
  }, [selectedRunSummary?.runId]);

  useEffect(() => {
    if (!roadmapProjectCards.length) {
      if (selectedRoadmapProjectKey !== null) {
        setSelectedRoadmapProjectKey(null);
      }
      return;
    }
    if (selectedRoadmapProjectKey && roadmapProjectCards.some(({ project }) => project.key === selectedRoadmapProjectKey)) {
      return;
    }
    if (selectedRoadmapProjectKey !== null) {
      setSelectedRoadmapProjectKey(null);
    }
  }, [roadmapProjectCards, selectedRoadmapProjectKey]);

  useEffect(() => {
    if (!programStarterOpen) return;
    if (
      programStarterOfferingCode
      && availableProgramStarterOfferings.some((entry) => entry.offeringCode === programStarterOfferingCode)
    ) {
      return;
    }
    setProgramStarterOfferingCode(availableProgramStarterOfferings[0]?.offeringCode ?? null);
  }, [availableProgramStarterOfferings, programStarterOfferingCode, programStarterOpen]);

  function openInitiative(initiativeId: string | null | undefined, preferredRunId?: string | null) {
    if (!initiativeId) return;
    setPlansView('detail');
    setSelectedInitiativeId(initiativeId);
    if (preferredRunId) {
      setSelectedRunId(preferredRunId);
    }
  }

  function openRun(runId: string | null | undefined) {
    if (!runId) return;
    const initiativeId = initiativeIdByRunId.get(runId)
      ?? getRunPrimaryProgramId(history.find((entry) => entry.runId === runId) ?? null);
    if (initiativeId) {
      openInitiative(initiativeId, runId);
      return;
    }
    setPlansView('detail');
    setSelectedRunId(runId);
  }

  function handleStartProgramClick() {
    if (!availableProgramStarterOfferings.length || running || sourceLoading) return;
    setProgramStarterOpen(true);
  }

  async function executePlanningAction(
    action: typeof headerState.primaryAction,
    options?: {
      serviceContractId?: string | null;
      offeringCode?: string | null;
    },
  ) {
    if (!action || running || sourceLoading) return;
    if (action.kind === 'open_latest_plan') {
      openRun(action.runId ?? latestRunSummary?.runId ?? null);
      return;
    }
    const shouldOpenPendingDetailView =
      !(plansView === 'detail' && Boolean(selectedRunId || plannerResult));
    if (shouldOpenPendingDetailView) {
      setPlansView('detail');
      setSelectedInitiativeId(null);
      setSelectedRunId(null);
      setPlannerResult(null);
      setReviewHistory([]);
      setLatestApplyExecution(null);
      setApplyHistory([]);
      setSelectedTriggerEvents([]);
      setComparisonSummary(null);
      setPendingPlanStartLabel(
        getPlanningActionProgressLabel(action.label),
      );
    }
    setRunning(true);
    setRunError(null);
    try {
      const next =
        action.kind === 'run_recurring'
          ? await runExistingClientPlannerDryRun({
              orgId,
              scheduleEventIds: action.scheduleEventIds ?? [],
              serviceContractId: options?.serviceContractId ?? undefined,
              offeringCode: options?.offeringCode ?? undefined,
            })
          : await runOnboardingPlannerDryRun({
              orgId,
              scheduleEventId: action.scheduleEventIds?.[0] ?? kickoffSource?.event?.id ?? '',
              serviceContractId: options?.serviceContractId ?? undefined,
              offeringCode: options?.offeringCode ?? undefined,
            });
      setPlannerResult(next);
      setReviewHistory([]);
      setLatestApplyExecution(null);
      setApplyHistory([]);
      const nextInitiativeId = getRunPrimaryProgramId(next.runSummary);
      setSelectedInitiativeId(nextInitiativeId ?? null);
      setSelectedRunId(next.runSummary.runId);
      setPendingPlanStartLabel(null);
      setPlansView('detail');
      setHistory((current) => [next.runSummary, ...current.filter((entry) => entry.runId !== next.runSummary.runId)]);
      await refreshWorkspaceState({
        preserveSelectedRunId: next.runSummary.runId,
        preserveSelectedInitiativeId: nextInitiativeId ?? null,
      });
    } catch (error) {
      setPendingPlanStartLabel(null);
      setRunError(error instanceof Error ? error.message : 'Planning draft failed.');
    } finally {
      setRunning(false);
    }
  }

  async function handleHeaderAction(action: typeof headerState.primaryAction) {
    await executePlanningAction(action);
  }

  async function handleProgramStarterRun() {
    if (!listViewStartAction || !selectedProgramStarterOffering) return;
    setProgramStarterOpen(false);
    await executePlanningAction(listViewStartAction, {
      serviceContractId: selectedProgramStarterOffering.serviceContractId,
      offeringCode: selectedProgramStarterOffering.offeringCode,
    });
  }

  async function handleReviewAction(action: AgenticDraftReviewAction) {
    if (!selectedRunSummary?.runId || reviewActionLoading) return;
    setReviewActionLoading(action);
    setRunError(null);
    try {
      const next = await reviewPlannerRun({
        runId: selectedRunSummary.runId,
        action,
        note: reviewNote,
      });
      setPlannerResult(next);
      setReviewNote('');
      setShowReviewDecisionEditor(false);
      const [nextReviewHistory, nextLatestApply, nextApplyHistory] = await Promise.all([
        fetchPlannerRunReviewHistory(next.runSummary.runId),
        fetchPlannerRunLatestApply(next.runSummary.runId),
        fetchPlannerRunApplyHistory(next.runSummary.runId),
      ]);
      setReviewHistory(nextReviewHistory);
      setLatestApplyExecution(nextLatestApply);
      setApplyHistory(nextApplyHistory);
      await refreshWorkspaceState({
        preserveSelectedRunId: next.runSummary.runId,
        preserveSelectedInitiativeId: getRunPrimaryProgramId(next.runSummary) ?? selectedInitiativeId,
      });
    } catch (error) {
      setRunError(error instanceof Error ? error.message : 'Draft review action failed.');
    } finally {
      setReviewActionLoading(null);
    }
  }

  async function handleApplyAction() {
    if (!selectedRunSummary?.runId || applyActionLoading || !applyEligibilityState.eligible) return;
    setApplyActionLoading(true);
    setRunError(null);
    try {
      const next = await applyPlannerRun(selectedRunSummary.runId);
      setLatestApplyExecution(next);
      const nextHistory = await fetchPlannerRunApplyHistory(selectedRunSummary.runId);
      setApplyHistory(nextHistory);
      await refreshWorkspaceState({
        preserveSelectedRunId: selectedRunSummary.runId,
        preserveSelectedInitiativeId: getRunPrimaryProgramId(selectedRunSummary) ?? selectedInitiativeId,
      });
    } catch (error) {
      setRunError(error instanceof Error ? error.message : 'Apply failed.');
      try {
        const [nextLatestApply, nextApplyHistory] = await Promise.all([
          fetchPlannerRunLatestApply(selectedRunSummary.runId),
          fetchPlannerRunApplyHistory(selectedRunSummary.runId),
        ]);
        setLatestApplyExecution(nextLatestApply);
        setApplyHistory(nextApplyHistory);
      } catch {
        // Preserve the existing local apply state if the refresh fails.
      }
    } finally {
      setApplyActionLoading(false);
    }
  }

  function getAttentionTargetRunId(item: AgenticPlanningAttentionItem) {
    if (item.runId) return item.runId;
    if (item.linkedRun?.runId) return item.linkedRun.runId;
    if (item.itemType === 'new_note_after_latest_plan') {
      if (currentRunSelection && currentRunSelection.mode === item.mode) return currentRunSelection.runId;
      if (latestRunSelection && latestRunSelection.mode === item.mode) return latestRunSelection.runId;
    }
    return null;
  }

  async function handleAttentionAction(item: AgenticPlanningAttentionItem, action: 'resolve' | 'dismiss') {
    if (attentionActionLoading) return;
    setAttentionActionLoading(`${item.attentionItemId}:${action}`);
    setRunError(null);
    try {
      await updatePlannerAttentionItem({
        attentionItemId: item.attentionItemId,
        action,
      });
      try {
        const nextEvents = await fetchPlannerAttentionEvents(item.attentionItemId);
        setAttentionEventsByItemId((current) => ({
          ...current,
          [item.attentionItemId]: nextEvents,
        }));
      } catch {
        // Preserve existing event history if the refresh fails.
      }
      await refreshWorkspaceState({
        preserveSelectedRunId: getAttentionTargetRunId(item) ?? selectedRunId,
        preserveSelectedInitiativeId: selectedInitiativeId,
      });
    } catch (error) {
      setRunError(error instanceof Error ? error.message : 'Attention queue action failed.');
    } finally {
      setAttentionActionLoading(null);
    }
  }

  const viewAnimationProps = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 14 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 },
        transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const },
      };
  const getDashboardMotionProps = (delay = 0) => (
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 18 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.34, delay, ease: [0.22, 1, 0.36, 1] as const },
        }
  );

  return (
    <Root>
      <AnimatePresence initial={false}>
        {!isPlanDetailView ? (
          <motion.div key="planner-header" {...viewAnimationProps}>
            <Header>
              <HeaderTopRow>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <Title>{headerState.title}</Title>
                  <HeaderMetaLine>
                    {sourceLoading
                      ? `${SERVICE_LABELS[serviceTab]} planning surface for ${orgName} | Loading planning sources...`
                      : headerState.contextLine}
                  </HeaderMetaLine>
                </div>
                {hasActiveServiceContract && availableProgramStarterOfferings.length ? (
                  <HeaderInlineActionButton type="button" onClick={handleStartProgramClick} disabled={running}>
                    {running ? getPlanningActionProgressLabel('Start Program') : 'Start Program'}
                  </HeaderInlineActionButton>
                ) : null}
              </HeaderTopRow>

              <HeaderRow>
                <HeroActivityChart
                  loading={heroChartLoading}
                  points={heroActivityPoints}
                  reduceMotion={reduceMotion}
                  monthLabel={selectedChartMonthLabel}
                  previousMonthLabel={previousChartMonthLabel}
                  nextMonthLabel={nextChartMonthLabel}
                  canGoPrevious={canGoToPreviousChartMonth}
                  canGoNext={canGoToNextChartMonth}
                  onPrevious={() => setChartMonthOffset((current) => Math.max(-12, current - 1))}
                  onNext={() => setChartMonthOffset((current) => Math.min(0, current + 1))}
                />

                <HeaderAside>
                  <Card>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                        Agentic usage (tokens)
                      </div>
                    </div>
                    {monthlyUsage ? (
                      <div style={{ display: 'grid', gap: 10 }}>
                        <UsageMetricsGrid>
                          <UsageMetricItem>
                            <MetaLabel>Total</MetaLabel>
                            <MetaValue>{formatNumber(monthlyUsage.totalTokens)}</MetaValue>
                          </UsageMetricItem>
                          <UsageMetricItem>
                            <MetaLabel>Input</MetaLabel>
                            <MetaValue>{formatNumber(monthlyUsage.inputTokens)}</MetaValue>
                          </UsageMetricItem>
                          <UsageMetricItem>
                            <MetaLabel>Output</MetaLabel>
                            <MetaValue>{formatNumber(monthlyUsage.outputTokens)}</MetaValue>
                          </UsageMetricItem>
                          <UsageMetricItem>
                            <MetaLabel>Runs</MetaLabel>
                            <MetaValue>{formatNumber(monthlyUsage.runCount)}</MetaValue>
                          </UsageMetricItem>
                          <UsageMetricItem>
                            <MetaLabel>Est. USD</MetaLabel>
                            <MetaValue $tone={usageEstimatedCostTone}>{usageEstimatedCostLabel}</MetaValue>
                          </UsageMetricItem>
                        </UsageMetricsGrid>
                      </div>
                    ) : usageLoading ? (
                      <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                        Loading usage...
                      </div>
                    ) : (
                      <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                        No monthly usage yet.
                      </div>
                    )}
                  </Card>

                  <Card style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                          Attention queue
                        </div>
                        {!attentionLoading && attentionSummary.totalOpen === 0 ? (
                          <div style={{ color: '#7f8a9a', fontSize: 11, lineHeight: 1.5, fontWeight: 700 }}>
                            No items waiting
                          </div>
                        ) : null}
                      </div>
                      {attentionLoading ? (
                        <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 800 }}>
                          Loading...
                        </div>
                      ) : attentionSummary.totalOpen > 0 ? (
                        <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900, textAlign: 'right' }}>
                          {`${formatNumber(attentionSummary.totalOpen)} item${attentionSummary.totalOpen === 1 ? '' : 's'}`}
                        </div>
                      ) : null}
                    </div>
                    <HeroAttentionList>
                      {attentionLoading ? (
                        <EmptyText>Loading attention queue...</EmptyText>
                      ) : openAttentionItems.length ? (
                        openAttentionItems.map((item) => {
                          const targetRunId = getAttentionTargetRunId(item);
                          const targetRunSummary = targetRunId ? historyByRunId.get(targetRunId) ?? null : null;
                          const itemTitle = targetRunSummary
                            ? getPlanRunDisplayTitle(targetRunSummary)
                            : text(item.linkedRun?.summaryText) || formatAttentionItemTypeLabel(item.itemType);
                          const itemDescription = text(item.reasonSummary) || text(item.sourceSummary) || 'Attention item';
                          return targetRunId ? (
                            <HeroAttentionItemCard key={item.attentionItemId}>
                              <HeroAttentionItemButton
                                type="button"
                                onClick={() => openRun(targetRunId)}
                                title={`${itemTitle} | ${itemDescription}`}
                              >
                                <HeroAttentionItemTitle>{itemTitle}</HeroAttentionItemTitle>
                                <HeroAttentionItemDescription>{itemDescription}</HeroAttentionItemDescription>
                              </HeroAttentionItemButton>
                            </HeroAttentionItemCard>
                          ) : (
                            <HeroAttentionItemCard key={item.attentionItemId}>
                              <div style={{ padding: '10px 12px', display: 'grid', gap: 4 }}>
                                <HeroAttentionItemTitle>{itemTitle}</HeroAttentionItemTitle>
                                <HeroAttentionItemDescription>{itemDescription}</HeroAttentionItemDescription>
                              </div>
                            </HeroAttentionItemCard>
                          );
                        })
                      ) : null}
                    </HeroAttentionList>
                  </Card>
                </HeaderAside>
              </HeaderRow>
            </Header>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <Content ref={contentRef}>
        {sourceError ? <Section><div style={{ color: '#ff9a9a', fontSize: 13, fontWeight: 800 }}>{sourceError}</div></Section> : null}
        {historyError ? <Section><div style={{ color: '#ff9a9a', fontSize: 13, fontWeight: 800 }}>{historyError}</div></Section> : null}
        {runError ? <Section><div style={{ color: '#ff9a9a', fontSize: 13, fontWeight: 800 }}>{runError}</div></Section> : null}

        {!isPlanDetailView && !sourceLoading && headerState.state === 'missing_prerequisites' && !latestRunSummary ? (
          <Section>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Planning is blocked</div>
              <div style={{ color: '#cdd5e0', fontSize: 13, lineHeight: 1.6, fontWeight: 700 }}>
                {headerState.description}
              </div>
              <div style={{ color: '#99a5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                This surface remains draft-only. No action here writes projects or tickets automatically.
              </div>
            </div>
          </Section>
        ) : null}

        <AnimatePresence initial={false} mode="wait">
          {!isPlanDetailView && plansView === 'list' ? (
              <motion.div key="plans-list" {...viewAnimationProps}>
                <Section>
                  <PlansToolbar>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'start' }}>
                      <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Programs</div>
                      <Eyebrow>
                        {initiativeCards.length
                          ? visibleInitiativeCards.length === initiativeCards.length
                            ? `${formatNumber(initiativeCards.length)} programs available`
                            : `Showing ${formatNumber(visibleInitiativeCards.length)} of ${formatNumber(initiativeCards.length)} programs`
                          : 'No programs yet'}
                      </Eyebrow>
                    </div>
                    {initiativeCards.length ? (
                      <PlansControlGrid>
                        <PlansField>
                          <PlansFieldLabel>Search</PlansFieldLabel>
                          <PlansInput
                            type="search"
                            placeholder="Search program, scope, or objective"
                            value={initiativeSearchQuery}
                            onChange={(event) => setInitiativeSearchQuery(event.target.value)}
                          />
                        </PlansField>
                        <PlansField>
                          <PlansFieldLabel>Type</PlansFieldLabel>
                          <PlansSelect value={initiativeTypeFilter} onChange={(event) => setInitiativeTypeFilter(event.target.value as ProgramTypeFilter)}>
                            <option value="all">All types</option>
                            <option value="product">Product</option>
                            <option value="plan">Plan</option>
                          </PlansSelect>
                        </PlansField>
                        <PlansField>
                          <PlansFieldLabel>Status</PlansFieldLabel>
                          <PlansSelect value={initiativeStatusFilter} onChange={(event) => setInitiativeStatusFilter(event.target.value as ProgramStatusFilter)}>
                            <option value="all">All statuses</option>
                            <option value="drafting">Drafting</option>
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="archived">Archived</option>
                          </PlansSelect>
                        </PlansField>
                        <PlansField>
                          <PlansFieldLabel>Sort</PlansFieldLabel>
                          <PlansSelect value={initiativeSortOrder} onChange={(event) => setInitiativeSortOrder(event.target.value as ProgramSortOrder)}>
                            <option value="updated">Recently updated</option>
                            <option value="oldest">Oldest first</option>
                            <option value="most_versions">Most versions</option>
                            <option value="most_attention">Most attention needed</option>
                          </PlansSelect>
                        </PlansField>
                      </PlansControlGrid>
                    ) : null}
                    {initiativeCards.length && hasInitiativeFiltersActive ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>
                          Filters are active on the Program list.
                        </div>
                        <Button
                          type="button"
                          onClick={() => {
                            setInitiativeSearchQuery('');
                            setInitiativeTypeFilter('all');
                            setInitiativeStatusFilter('all');
                            setInitiativeSortOrder('updated');
                          }}
                        >
                          Clear filters
                        </Button>
                      </div>
                    ) : null}
                  </PlansToolbar>
                  {initiativeCards.length ? (
                    visibleInitiativeCards.length ? (
                      <InitiativeCardsGrid>
                        {visibleInitiativeCards.map((entry) => {
                          const anchorRun = entry.currentDraftRun ?? entry.latestRun ?? entry.latestApprovedRun;
                          const anchorRunDetail = anchorRun ? cardRunDetailsByRunId[anchorRun.runId] ?? null : null;
                          const cardTeamLabelMap = buildTeamLabelMap(anchorRunDetail);
                          const cardExecutionSummary = getProgramExecutionSummary({
                            program: entry.program,
                            summary: anchorRun,
                            result: anchorRunDetail,
                            teamLabelMap: cardTeamLabelMap,
                          });
                          const applyReadiness = anchorRun ? getRunSummaryApplyReadiness(anchorRun) : null;
                          const latestVersionInfo = anchorRun ? historyVersionMap.get(anchorRun.runId) ?? null : null;
                          const serviceFamilyLabel = getProgramServiceFamilyLabel(entry.program);
                          const scopeLabel = text(entry.program.primaryScopeLabel);
                          const programScopeLine =
                            scopeLabel && scopeLabel.toLowerCase() !== serviceFamilyLabel.toLowerCase()
                              ? `${serviceFamilyLabel} | ${scopeLabel}`
                              : scopeLabel || serviceFamilyLabel;
                          const displayInitiativeTitle = getProgramDisplayTitle(entry.program, serviceFamilyLabel, anchorRun);
                          const cardStatusNotes: Array<{ label: string; tone: Tone }> = [];
                          if (entry.latestApprovedRun && entry.latestApprovedRun.runId !== entry.currentDraftRun?.runId) {
                            cardStatusNotes.push({ label: 'Approved baseline', tone: 'good' });
                          }
                          if (entry.newerRecurringNoteCount > 0) {
                            cardStatusNotes.push({
                              label: `${entry.newerRecurringNoteCount} newer note${entry.newerRecurringNoteCount === 1 ? '' : 's'}`,
                              tone: 'warn',
                            });
                          }
                          if (entry.attentionCount > 0) {
                            cardStatusNotes.push({
                              label: `${formatNumber(entry.attentionCount)} action item${entry.attentionCount === 1 ? '' : 's'}`,
                              tone: 'warn',
                            });
                          }
                          if (entry.program.recurringSeries.length > 0) {
                            cardStatusNotes.push({
                              label: `${formatNumber(entry.program.recurringSeries.length)} recurring series`,
                              tone: 'info',
                            });
                          }
                          const secondaryStatus = entry.latestApply
                            ? `Latest apply ${formatApplyStatusLabel(entry.latestApply.status)}`
                            : anchorRun?.draftReview?.reviewStatus === 'approved_draft'
                              ? 'Ready to apply'
                              : anchorRun
                                ? formatDraftReviewStatusLabel(anchorRun.draftReview?.reviewStatus)
                                : null;
                          return (
                            <InitiativeCardButton
                              key={entry.program.programId}
                              type="button"
                              $active={selectedInitiativeId === entry.program.programId}
                              onClick={() => {
                                openInitiative(
                                  entry.program.programId,
                                  entry.currentDraftRun?.runId ?? entry.latestRun?.runId ?? entry.latestApprovedRun?.runId ?? null,
                                );
                              }}
                            >
                              <InitiativeCardHeader>
                                <InitiativeCardHeaderText>
                                  <InitiativeCardMetaLine>
                                    {formatProgramTypeLabel(entry.program.programType)} | Updated {formatCompactDate(entry.program.updatedAt)}
                                  </InitiativeCardMetaLine>
                                  <div style={{ color: '#d0d7e2', fontSize: 11, lineHeight: 1.5, fontWeight: 900 }}>
                                    {programScopeLine}
                                  </div>
                                  <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.55, fontWeight: 700 }}>
                                    Primary team {cardExecutionSummary.primaryTeamLabel}
                                  </div>
                                  <InitiativeCardTitle>{displayInitiativeTitle}</InitiativeCardTitle>
                                  <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.55, fontWeight: 700 }}>
                                    Project scope {cardExecutionSummary.projectScopeLabel}
                                  </div>
                                  {cardExecutionSummary.collaboratorTeamsLabel ? (
                                    <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.55, fontWeight: 700 }}>
                                      Collaborators {cardExecutionSummary.collaboratorTeamsLabel}
                                    </div>
                                  ) : null}
                                </InitiativeCardHeaderText>
                                <InitiativeCardStatusStack>
                                  <InitiativeCardPrimaryStatus $tone={toneForProgramStatus(entry.program.status)}>
                                    {formatProgramStatusLabel(entry.program.status)}
                                  </InitiativeCardPrimaryStatus>
                                  {secondaryStatus ? (
                                    <InitiativeCardSecondaryStatus $tone={entry.latestApply ? toneForApplyStatus(entry.latestApply.status) : applyReadiness?.tone}>
                                      {secondaryStatus}
                                    </InitiativeCardSecondaryStatus>
                                  ) : null}
                                  {cardStatusNotes.map((item) => (
                                    <InitiativeCardStatusMeta key={`${entry.program.programId}:${item.label}`} $tone={item.tone}>
                                      {item.label}
                                    </InitiativeCardStatusMeta>
                                  ))}
                                </InitiativeCardStatusStack>
                              </InitiativeCardHeader>
                              <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700, marginTop: 'auto' }}>
                                {latestVersionInfo ? `Latest ${latestVersionInfo.label} | ` : ''}
                                {formatNumber(entry.versionCount)} version{entry.versionCount === 1 ? '' : 's'}
                                {anchorRun ? ` | ${formatNumber(anchorRun.counts.projects)} projects | ${formatNumber(anchorRun.counts.milestones)} milestones | ${formatNumber(anchorRun.counts.tickets)} tickets` : ''}
                              </div>
                            </InitiativeCardButton>
                          );
                        })}
                      </InitiativeCardsGrid>
                    ) : (
                      <div style={{ display: 'grid', gap: 10 }}>
                        <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>No Programs match the current filters.</div>
                        <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                          Try a broader search, remove one of the filters, or switch the sort order back to a wider view of the Program workspace.
                        </div>
                      </div>
                    )
                  ) : (
                    <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>
                      {availableProgramStarterOfferings.length
                        ? 'No Programs are visible yet in this workspace. Start Program to choose a purchased offering and create the first Program for that scope.'
                        : 'No Programs are visible yet in this workspace.'}
                    </div>
                  )}
                  {orphanRuns.length ? (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>Unlinked versions</div>
                        <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                          These saved versions are not linked to a durable Program yet. They remain visible here so review-required or out-of-scope work is not hidden.
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {orphanRuns.slice(0, 4).map((entry) => (
                          <Card key={`orphan-run:${entry.runId}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                              <div style={{ display: 'grid', gap: 4 }}>
                                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{getPlanRunDisplayTitle(entry)}</div>
                                <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                  {formatPlanningModeLabel(entry.mode)} | {formatDraftReviewStatusLabel(entry.draftReview?.reviewStatus)} | Updated {formatCompactDate(entry.updatedAt)}
                                </div>
                              </div>
                              <Button type="button" onClick={() => openRun(entry.runId)}>
                                Open version
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </Section>
              </motion.div>
            ) : isPlanDetailView ? (
              <motion.div
                key={`selected-initiative:${selectedInitiativeId ?? selectedRunSummary?.runId ?? selectedRunId ?? 'loading'}`}
                {...viewAnimationProps}
                style={{ display: 'grid', gap: 16 }}
              >
                <>
              {selectedRunSummary ? (
                <DashboardSection>
                  <DashboardControlsRow>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <Button type="button" onClick={() => setPlansView('list')}>
                        Back to Programs
                      </Button>
                      {selectedPlanRefreshAction ? (
                        <Button type="button" onClick={() => void handleHeaderAction(selectedPlanRefreshAction)} disabled={running || sourceLoading}>
                          {running ? getPlanningActionProgressLabel(selectedPlanRefreshAction.label) : selectedPlanRefreshAction.label}
                        </Button>
                      ) : null}
                      <Button type="button" onClick={() => { setDrawerTab('trace'); setDrawerOpen(true); }} disabled={!plannerResult}>
                        <PanelRightOpen size={14} />
                        View details
                      </Button>
                    </div>
                    <div style={{ display: 'grid', gap: 6, minWidth: 220 }}>
                      <PlansFieldLabel>Version</PlansFieldLabel>
                      <VersionMenuSelect
                        value={selectedRunSummary?.runId ?? ''}
                        options={selectedVersionMenuOptions}
                        onChange={setSelectedRunId}
                      />
                      {false ? (
                        <PlansSelect
                        value={selectedRunSummary?.runId ?? ''}
                        onChange={(event) => setSelectedRunId(event.target.value)}
                      >
                        {versionOptions.map((entry) => {
                          const version = selectedInitiativeVersionMap.get(entry.runId) ?? historyVersionMap.get(entry.runId);
                          const suffixes = [
                            version?.isLatest ? 'latest' : null,
                            entry.runId === (selectedInitiativeSelections?.latestApprovedRun?.runId ?? latestApprovedRunSelection?.runId) ? 'approved' : null,
                          ].filter(Boolean).join(' · ');
                          return (
                            <option key={`version-option:${entry.runId}`} value={entry.runId}>
                              {`${version?.label ?? '--'} · ${formatCompactDate(entry.updatedAt)}${suffixes ? ` · ${suffixes}` : ''}`}
                            </option>
                          );
                        })}
                        </PlansSelect>
                      ) : null}
                    </div>
                  </DashboardControlsRow>
                </DashboardSection>
              ) : null}

              {pendingPlanStartLabel ? (
                <Section>
                  <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>{pendingPlanStartLabel}</div>
                  <div style={{ color: '#cdd5e0', fontSize: 13, lineHeight: 1.6, fontWeight: 700 }}>
                    The workspace is opening the new draft now. This remains draft-only and does not write projects or tickets while the planner run is being created.
                  </div>
                </Section>
              ) : detailLoading ? (
                <Section>
                  <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Loading selected version...</div>
                  <div style={{ color: '#cdd5e0', fontSize: 13, lineHeight: 1.6, fontWeight: 700 }}>
                    Persisted version detail is being loaded so the Program workspace and drawer stay in sync.
                  </div>
                </Section>
              ) : null}

              {plannerResult ? (
                <>
                  <PhaseStepperSection>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <Eyebrow $tone="info">Inspect by phase</Eyebrow>
                      <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                        Overview now holds the Program summary and lifecycle state. The remaining phases break the selected version down by evidence, discovery, roadmap structure, and review.
                      </div>
                    </div>
                    <PhaseStepperList role="tablist" aria-label="Selected version planning phases">
                      {selectedVersionPhaseSteps.map((phase, index) => (
                        <PhaseStepButton
                          key={`selected-version-phase:${phase.key}`}
                          type="button"
                          role="tab"
                          aria-selected={selectedVersionPhase === phase.key}
                          $active={selectedVersionPhase === phase.key}
                          onClick={() => setSelectedVersionPhase(phase.key)}
                        >
                          <PhaseStepIndex $active={selectedVersionPhase === phase.key}>
                            Phase {index + 1}
                          </PhaseStepIndex>
                          <PhaseStepLabel>{phase.label}</PhaseStepLabel>
                          <PhaseStepSummary $active={selectedVersionPhase === phase.key}>{phase.summary}</PhaseStepSummary>
                        </PhaseStepButton>
                      ))}
                    </PhaseStepperList>
                  </PhaseStepperSection>

                  {selectedVersionPhase === 'overview' ? renderOverviewPhase() : null}
                  {selectedVersionPhase === 'inputs' ? renderInputsPhase() : null}
                  {selectedVersionPhase === 'discovery' ? renderDiscoveryPhase() : null}
                  {selectedVersionPhase === 'roadmap' ? renderRoadmapPhase() : null}
                  {selectedVersionPhase === 'review_apply' ? renderReviewApplyPhase() : null}
                </>
              ) : null}

              {/* Legacy selected-version detail body retained temporarily during the phase-stepper refactor.
            {hasDecisionOrApplyAction ? (
            <Section>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle2 size={16} />
                <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Decision and apply</div>
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                <Card>
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'start' }}>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <Eyebrow $tone={reviewStatusTone}>Step 1 · Review</Eyebrow>
                        <div style={{ color: '#f5f7fb', fontSize: 18, fontWeight: 900 }}>
                          {formatDraftReviewStatusLabel(reviewStatus)}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 6, justifyItems: 'end', textAlign: 'right' }}>
                        <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 700 }}>
                          {latestReviewEvent?.createdAt ? formatDateTime(latestReviewEvent?.createdAt ?? '') : 'Waiting for review'}
                        </div>
                        <AccordionToggle
                          type="button"
                          onClick={() => setReviewApplyOpenPanel('review')}
                          aria-expanded={reviewApplyOpenPanel === 'review'}
                          style={{ width: 'auto', justifyItems: 'end' }}
                        >
                          <ChevronDown
                            size={16}
                            style={{
                              color: '#cdd5e0',
                              transform: reviewApplyOpenPanel === 'review' ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 180ms ease',
                            }}
                          />
                        </AccordionToggle>
                      </div>
                    </div>
                    <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                      {reviewSummaryText}
                    </div>
                    {latestReviewEvent ? (
                      <div style={{ color: '#8f9aae', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                        Reviewed by {latestReviewEvent?.actedByDisplayName || latestReviewEvent?.actedByUserId || 'System'}
                      </div>
                    ) : null}

                    <AnimatePresence initial={false}>
                      {reviewApplyOpenPanel === 'review' ? (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                          style={{ overflow: 'hidden' }}
                        >
                          <div style={{ display: 'grid', gap: 12 }}>
                    {showReviewComposer ? (
                      <>
                        <TextArea
                          value={reviewNote}
                          onChange={(event) => setReviewNote(event.target.value)}
                          placeholder="Optional reviewer note"
                        />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {(reviewStatus === 'review_required' || reviewStatus === 'needs_revision' || reviewStatus === 'rejected_draft') ? (
                            <Button
                              type="button"
                              $primary
                              onClick={() => void handleReviewAction('approve')}
                              disabled={reviewActionLoading !== null}
                            >
                              {reviewActionLoading === 'approve' ? 'Approving...' : 'Approve'}
                            </Button>
                          ) : null}
                          {(reviewStatus === 'review_required' || reviewStatus === 'rejected_draft') ? (
                            <Button
                              type="button"
                              onClick={() => void handleReviewAction('request_revision')}
                              disabled={reviewActionLoading !== null}
                            >
                              {reviewActionLoading === 'request_revision' ? 'Saving...' : 'Request revision'}
                            </Button>
                          ) : null}
                          {(reviewStatus === 'review_required' || reviewStatus === 'needs_revision') ? (
                            <Button
                              type="button"
                              onClick={() => void handleReviewAction('reject')}
                              disabled={reviewActionLoading !== null}
                            >
                              {reviewActionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                            </Button>
                          ) : null}
                        </div>
                      </>
                    ) : canAdjustApprovedReview ? (
                      <div style={{ display: 'grid', gap: 10 }}>
                        <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                          Approval is already recorded. Only change it if this version should move back out of the apply path.
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <Button
                            type="button"
                            onClick={() => setShowReviewDecisionEditor((current) => !current)}
                          >
                            {showReviewDecisionEditor ? 'Hide review controls' : 'Change review decision'}
                          </Button>
                        </div>
                        {showReviewDecisionEditor ? (
                          <>
                            <TextArea
                              value={reviewNote}
                              onChange={(event) => setReviewNote(event.target.value)}
                              placeholder="Optional reviewer note"
                            />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              <Button
                                type="button"
                                onClick={() => void handleReviewAction('request_revision')}
                                disabled={reviewActionLoading !== null}
                              >
                                {reviewActionLoading === 'request_revision' ? 'Saving...' : 'Request revision'}
                              </Button>
                              <Button
                                type="button"
                                onClick={() => void handleReviewAction('reject')}
                                disabled={reviewActionLoading !== null}
                              >
                                {reviewActionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                              </Button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                </Card>
                {showApplyExecutionPanel ? (
                  <Card>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                          <Eyebrow $tone={applyStatusTone}>Step 2 · Apply</Eyebrow>
                          <div style={{ color: '#f5f7fb', fontSize: 18, fontWeight: 900 }}>
                            {applyHeadline}
                          </div>
                        </div>
                        <div style={{ display: 'grid', gap: 6, justifyItems: 'end', textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 700 }}>
                            {latestApplyExecution?.updatedAt ? formatDateTime(latestApplyExecution?.updatedAt ?? '') : applyEligibilityState.eligible ? 'Ready to run' : 'Not available'}
                          </div>
                          <AccordionToggle
                            type="button"
                            onClick={() => setReviewApplyOpenPanel('apply')}
                            aria-expanded={reviewApplyOpenPanel === 'apply'}
                            style={{ width: 'auto', justifyItems: 'end' }}
                          >
                            <ChevronDown
                              size={16}
                              style={{
                                color: '#cdd5e0',
                                transform: reviewApplyOpenPanel === 'apply' ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 180ms ease',
                              }}
                            />
                          </AccordionToggle>
                        </div>
                      </div>
                      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                        {applySummaryText}
                      </div>
                      <AnimatePresence initial={false}>
                        {reviewApplyOpenPanel === 'apply' ? (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                            style={{ overflow: 'hidden' }}
                          >
                            <div style={{ display: 'grid', gap: 12, paddingTop: 12, borderTop: '1px dashed rgba(255,255,255,.12)' }}>
                      {applyEligibilityState.actionLabel ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <Button
                            type="button"
                            $primary
                            onClick={() => void handleApplyAction()}
                            disabled={!applyEligibilityState.eligible || applyActionLoading || applyLoading}
                          >
                            {applyActionLoading ? 'Applying...' : applyEligibilityState.actionLabel}
                          </Button>
                        </div>
                      ) : null}
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </Card>
                ) : null}
                {selectedRunAttentionItems.length ? (
                  <Card>
                    <Eyebrow $tone="warn">Open attention items</Eyebrow>
                    <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>
                      {`${formatNumber(selectedRunAttentionItems.length)} item${selectedRunAttentionItems.length === 1 ? '' : 's'} still need operator attention`}
                    </div>
                    <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                      Resolve or dismiss run-scoped attention items from planner details. The drawer now includes full attention history alongside review and apply logs.
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <Button
                        type="button"
                        onClick={() => {
                          setDrawerTab('diagnostics');
                          setDrawerOpen(true);
                        }}
                      >
                        Open logs
                      </Button>
                    </div>
                  </Card>
                ) : null}
              </div>
            </Section>
            ) : null}

            {(plannerResult.runSummary.specialistSummary?.registeredCount ?? 0) > 0 || specialistInvocations.length ? (
              <Section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Bot size={16} />
                  <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Specialist influence</div>
                </div>
                <Eyebrow $tone={specialistInfluence.tone}>{specialistInfluence.label}</Eyebrow>
                <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                  {specialistInfluence.summary}
                </div>
                <Grid $cols="repeat(3, minmax(0, 1fr))">
                  <Card>
                    <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>Registered</div>
                    <div style={{ color: '#f5f7fb', fontSize: 20, fontWeight: 900 }}>{formatNumber(plannerResult.runSummary.specialistSummary?.registeredCount ?? 0)}</div>
                  </Card>
                  <Card>
                    <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>Applied</div>
                    <div style={{ color: '#f5f7fb', fontSize: 20, fontWeight: 900 }}>{formatNumber(plannerResult.runSummary.specialistSummary?.appliedCount ?? 0)}</div>
                  </Card>
                  <Card>
                    <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>Stages</div>
                    <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>
                      {(plannerResult.runSummary.specialistSummary?.stagesAffected ?? []).map((stage) => formatSpecialistStageLabel(stage)).join(', ') || '--'}
                    </div>
                  </Card>
                </Grid>
                <Grid $cols="repeat(2, minmax(0, 1fr))">
                  <Card>
                    <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Invocation details</div>
                    {specialistInvocations.length ? (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {specialistInvocations.map((invocation, index) => (
                          <Card key={`${invocation.specialistId}:${invocation.stage}:${index}`}>
                            <MetaRow>
                              <MetaItem>
                                <MetaLabel>Specialist</MetaLabel>
                                <MetaValue>{invocation.displayName}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Stage</MetaLabel>
                                <MetaValue>{formatSpecialistStageLabel(invocation.stage)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Outcome</MetaLabel>
                                <MetaValue $tone={invocation.influenceStatus === 'applied' ? 'good' : invocation.influenceStatus === 'partially_applied' ? 'warn' : 'neutral'}>
                                  {formatSpecialistInfluenceStatusLabel(invocation.influenceStatus)}
                                </MetaValue>
                              </MetaItem>
                            </MetaRow>
                            <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                              {invocation.summary || invocation.notes[0] || 'No specialist note recorded.'}
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : <EmptyText>No specialist invocation details were returned.</EmptyText>}
                  </Card>
                  <Card>
                    <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Specialist notes</div>
                    {renderTextList(
                      [
                        ...(plannerResult.runSummary.specialistSummary?.influenceSummaries ?? []),
                        ...specialistProvenanceNotes,
                      ].filter((value, index, all) => Boolean(value) && all.indexOf(value) === index),
                      'No specialist notes were returned.',
                    )}
                  </Card>
                </Grid>
              </Section>
            ) : null}

            <Section>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileSearch size={16} />
                <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Discovery</div>
              </div>
              <Eyebrow $tone={toneForPlanMode(inputSufficiency?.planMode)}>
                {formatPlanClassificationLabel(inputSufficiency?.planMode)}
              </Eyebrow>
              <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                {inputSufficiency?.rationale || 'Discovery explains the facts, scope signals, and evidence the planner used to shape this version.'}
              </div>
              <Grid $cols="repeat(2, minmax(0, 1fr))">
                <Card>
                  <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Discovered needs</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {discoveredFacts.length ? discoveredFacts.map((fact) => (
                      <div key={fact.factId} style={{ display: 'grid', gap: 4 }}>
                        <MetaRow>
                          <MetaItem>
                            <MetaLabel>Fact type</MetaLabel>
                            <MetaValue $tone={fact.classification === 'explicit' ? 'good' : 'info'}>{humanizeToken(fact.classification)}</MetaValue>
                          </MetaItem>
                          <MetaItem>
                            <MetaLabel>Category</MetaLabel>
                            <MetaValue>{fact.category}</MetaValue>
                          </MetaItem>
                        </MetaRow>
                        <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{fact.statement}</div>
                      </div>
                    )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No discovered facts returned.</div>}
                  </div>
                </Card>
                {outOfScopeDecisions.length ? (
                  <Card>
                    <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Out of scope</div>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {outOfScopeDecisions.map((decision) => (
                        <div key={decision.decisionId} style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{decision.rationale}</div>
                      ))}
                    </div>
                  </Card>
                ) : null}
              </Grid>
            </Section>

            <Section>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FolderTree size={16} />
                <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Planning</div>
              </div>
              <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                Planning shows the structure of this saved version and the latest execution updates for the selected planning group. One version can contain one or more projects, milestones, taxonomy/KBase decisions, and collaboration routing.
              </div>
              {planningGroups.length ? (
                <ExplorerShell>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ color: '#8f9aae', fontSize: 10, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Planning groups
                    </div>
                    <ExplorerList>
                      {planningGroups.map((group) => (
                        <ExplorerListButton
                          key={group.key}
                          type="button"
                          $active={selectedPlanningGroup?.key === group.key}
                          onClick={() => setSelectedPlanningGroupKey(group.key)}
                        >
                          <ExplorerListLabel>{group.title}</ExplorerListLabel>
                          <ExplorerListMeta>{group.summary}</ExplorerListMeta>
                        </ExplorerListButton>
                      ))}
                    </ExplorerList>
                  </div>

                  <ExplorerDetail>
                    {selectedPlanningGroup ? (
                      <ExplorerDetailScroll>
                        <div style={{ display: 'grid', gap: 4 }}>
                          <div style={{ color: '#8f9aae', fontSize: 10, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                            Selected planning group
                          </div>
                          <div style={{ color: '#f5f7fb', fontSize: 15, fontWeight: 900 }}>{selectedPlanningGroup.title}</div>
                          <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{selectedPlanningGroup.summary}</div>
                        </div>

                        {selectedPlanningGroup.key === 'projects' ? (
                          <>
                            <MetaRow>
                              <MetaItem>
                                <MetaLabel>Projects</MetaLabel>
                                <MetaValue>{formatNumber(projectCards.length)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Immediate scope</MetaLabel>
                                <MetaValue>{formatNumber(scopeCounts.immediateProjects)}</MetaValue>
                              </MetaItem>
                            </MetaRow>
                            <div style={{ display: 'grid', gap: 14 }}>
                              {projectCards.length ? projectCards.map(({ project, milestones }, projectIndex) => (
                                <div
                                  key={project.key}
                                  style={{
                                    display: 'grid',
                                    gap: 10,
                                    paddingTop: projectIndex ? 14 : 0,
                                    borderTop: projectIndex ? '1px dashed rgba(255,255,255,.12)' : 'none',
                                  }}
                                >
                                  <div style={{ display: 'grid', gap: 4 }}>
                                    <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{project.name}</div>
                                    {project.brief || project.objective ? (
                                      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                        {project.brief || project.objective}
                                      </div>
                                    ) : null}
                                  </div>
                                  <MetaRow>
                                    <MetaItem>
                                      <MetaLabel>Service</MetaLabel>
                                      <MetaValue>{project.serviceCode || 'cross-service'}</MetaValue>
                                    </MetaItem>
                                    <MetaItem>
                                      <MetaLabel>Primary team</MetaLabel>
                                      <MetaValue>{formatTeamLabel(project.teamNodeId, teamLabelMap)}</MetaValue>
                                    </MetaItem>
                                  </MetaRow>
                                  <div style={{ color: '#9aa5b6', fontSize: 11, fontWeight: 700 }}>
                                    Supporting {formatTeamList(project.supportingTeamNodeIds, teamLabelMap).join(', ')}
                                  </div>
                                  <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                    {formatNumber(milestones.length)} milestone{milestones.length === 1 ? '' : 's'} | {formatNumber(milestones.reduce((sum, entry) => sum + entry.deliverables.length, 0))} deliverable{milestones.reduce((sum, entry) => sum + entry.deliverables.length, 0) === 1 ? '' : 's'} | {formatNumber(milestones.reduce((sum, entry) => sum + entry.tickets.length, 0))} ticket{milestones.reduce((sum, entry) => sum + entry.tickets.length, 0) === 1 ? '' : 's'}
                                  </div>
                                </div>
                              )) : <EmptyText>No projects returned for this planner view.</EmptyText>}
                            </div>
                          </>
                        ) : null}

                        {selectedPlanningGroup.key === 'milestones' ? (
                          <>
                            <MetaRow>
                              <MetaItem>
                                <MetaLabel>Milestones</MetaLabel>
                                <MetaValue>{formatNumber(milestoneCards.length)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Deliverables</MetaLabel>
                                <MetaValue>{formatNumber(plannerResult?.planGraph.deliverables.length ?? 0)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Tickets</MetaLabel>
                                <MetaValue>{formatNumber(selectedRunSummary?.counts.tickets ?? 0)}</MetaValue>
                              </MetaItem>
                            </MetaRow>
                            <div style={{ display: 'grid', gap: 14 }}>
                              {milestoneCards.length ? milestoneCards.map(({ project, milestone, deliverables, tickets }, milestoneIndex) => (
                                <div
                                  key={milestone.key}
                                  style={{
                                    display: 'grid',
                                    gap: 8,
                                    paddingTop: milestoneIndex ? 14 : 0,
                                    borderTop: milestoneIndex ? '1px dashed rgba(255,255,255,.12)' : 'none',
                                  }}
                                >
                                  <div style={{ display: 'grid', gap: 3 }}>
                                    <div style={{ color: '#8f9aae', fontSize: 10, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                      {project.name}
                                    </div>
                                    <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{milestone.title}</div>
                                    {milestone.outcome ? (
                                      <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>{milestone.outcome}</div>
                                    ) : null}
                                  </div>
                                  <Grid $cols="repeat(2, minmax(0, 1fr))">
                                    <div style={{ display: 'grid', gap: 6 }}>
                                      <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                        Deliverables
                                      </div>
                                      {deliverables.length ? (
                                        <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, color: '#d0d7e2', fontSize: 12, lineHeight: 1.5, fontWeight: 700 }}>
                                          {deliverables.map((deliverable) => (
                                            <li key={deliverable.key}>
                                              {deliverable.title}
                                              {deliverable.description ? ` | ${truncate(deliverable.description, 120)}` : ''}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No deliverables returned.</div>
                                      )}
                                    </div>
                                    <div style={{ display: 'grid', gap: 6 }}>
                                      <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                        Tickets
                                      </div>
                                      {tickets.length ? (
                                        <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, color: '#d0d7e2', fontSize: 12, lineHeight: 1.5, fontWeight: 700 }}>
                                          {tickets.map((ticket) => (
                                            <li key={ticket.key}>
                                              {ticket.title}
                                              {ticket.proposedExecutionTeamNodeId ? ` | ${formatTeamLabel(ticket.proposedExecutionTeamNodeId, teamLabelMap)}` : ''}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No tickets returned.</div>
                                      )}
                                    </div>
                                  </Grid>
                                </div>
                              )) : <EmptyText>No milestones returned for this planner view.</EmptyText>}
                            </div>
                          </>
                        ) : null}

                        {selectedPlanningGroup.key === 'taxonomy_kbase' ? (
                          <>
                            <MetaRow>
                              <MetaItem>
                                <MetaLabel>Subcategory decisions</MetaLabel>
                                <MetaValue>{formatNumber(subcategoryDecisions.length)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>KBase decisions</MetaLabel>
                                <MetaValue>{formatNumber(kbProcessDecisions.length)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Review required</MetaLabel>
                                <MetaValue $tone={reviewRequiredExpansionCount ? 'warn' : 'good'}>
                                  {formatNumber(reviewRequiredExpansionCount)}
                                </MetaValue>
                              </MetaItem>
                            </MetaRow>
                            {(subcategoryDecisions.length || kbProcessDecisions.length) ? (
                              <Grid $cols="repeat(2, minmax(0, 1fr))">
                                <div style={{ display: 'grid', gap: 8 }}>
                                  <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                    Taxonomy
                                  </div>
                                  {subcategoryDecisions.length ? subcategoryDecisions.map((entry) => (
                                    <div key={entry.decisionKey} style={{ display: 'grid', gap: 3 }}>
                                      <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{entry.inferredSubcategoryName}</div>
                                      <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                        {humanizeToken(entry.status)} | {entry.rationale}
                                      </div>
                                    </div>
                                  )) : <EmptyText>No taxonomy decisions returned.</EmptyText>}
                                </div>
                                <div style={{ display: 'grid', gap: 8 }}>
                                  <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                    KBase
                                  </div>
                                  {kbProcessDecisions.length ? kbProcessDecisions.map((entry) => (
                                    <div key={entry.decisionKey} style={{ display: 'grid', gap: 3 }}>
                                      <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{entry.processTitle}</div>
                                      <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                        {humanizeToken(entry.status)} | {entry.summary || entry.rationale}
                                      </div>
                                    </div>
                                  )) : <EmptyText>No KBase decisions returned.</EmptyText>}
                                </div>
                              </Grid>
                            ) : (
                              <EmptyText>No taxonomy or KBase planning detail was returned for this version.</EmptyText>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              <Button
                                type="button"
                                onClick={() => {
                                  setDrawerTab('taxonomy');
                                  setDrawerOpen(true);
                                }}
                              >
                                Open taxonomy + KBase details
                              </Button>
                            </div>
                          </>
                        ) : null}

                        {selectedPlanningGroup.key === 'collaborations' ? (
                          <>
                            <MetaRow>
                              <MetaItem>
                                <MetaLabel>Routed lanes</MetaLabel>
                                <MetaValue>{formatNumber(executionLanes.length)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Review items</MetaLabel>
                                <MetaValue $tone={assignmentIssues.length ? 'warn' : 'good'}>
                                  {formatNumber(assignmentIssues.length)}
                                </MetaValue>
                              </MetaItem>
                            </MetaRow>
                            <div style={{ display: 'grid', gap: 12 }}>
                              {projectCards.length ? projectCards.map(({ project }, index) => (
                                <div
                                  key={`collab:${project.key}`}
                                  style={{
                                    display: 'grid',
                                    gap: 4,
                                    paddingTop: index ? 12 : 0,
                                    borderTop: index ? '1px dashed rgba(255,255,255,.12)' : 'none',
                                  }}
                                >
                                  <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{project.name}</div>
                                  <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                    Primary {formatTeamLabel(project.teamNodeId, teamLabelMap)} | Supporting {formatTeamList(project.supportingTeamNodeIds, teamLabelMap).join(', ')}
                                  </div>
                                </div>
                              )) : <EmptyText>No project collaboration detail was returned.</EmptyText>}

                              {executionLanes.length ? (
                                <ExplorerDivider>
                                  <div style={{ display: 'grid', gap: 8 }}>
                                    <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                      Routing detail
                                    </div>
                                    {executionLanes.map((lane) => (
                                      <div key={lane.laneKey} style={{ display: 'grid', gap: 3 }}>
                                        <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{lane.serviceCode}</div>
                                        <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                          Lead {formatTeamLabel(lane.primaryTeamNodeId, teamLabelMap)} | Supporting {formatTeamList(lane.supportingTeamNodeIds, teamLabelMap).join(', ')}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </ExplorerDivider>
                              ) : null}

                              {assignmentIssues.length ? (
                                <ExplorerDivider>
                                  <div style={{ display: 'grid', gap: 8 }}>
                                    <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                      Collaboration review items
                                    </div>
                                    {assignmentIssues.map((entry) => (
                                      <div key={`${entry.entityType}:${entry.entityKey}`} style={{ display: 'grid', gap: 3 }}>
                                        <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{entry.entityType} | {entry.entityKey}</div>
                                        <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                          Primary {formatTeamLabel(entry.primaryTeamNodeId, teamLabelMap)} | Supporting {formatTeamList(entry.supportingTeamNodeIds, teamLabelMap).join(', ')}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </ExplorerDivider>
                              ) : null}
                            </div>
                          </>
                        ) : null}

                        <ExplorerDivider>
                          <div style={{ display: 'grid', gap: 10 }}>
                            <div style={{ display: 'grid', gap: 4 }}>
                              <div style={{ color: '#8f9aae', fontSize: 10, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                Execution updates
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, color: '#f5f7fb', fontSize: 12, fontWeight: 800 }}>
                                <span style={{ color: selectedPlanningExecutionStage === 'planning' ? '#f5f7fb' : '#778195' }}>Planning</span>
                                <span style={{ color: '#778195' }}>&gt;</span>
                                <span style={{ color: selectedPlanningExecutionStage === 'in_progress' ? '#e0a15e' : '#778195' }}>In progress</span>
                                <span style={{ color: '#778195' }}>&gt;</span>
                                <span
                                  style={{
                                    color:
                                      selectedPlanningExecutionStage === 'build'
                                        ? (
                                            selectedPlanningExecutionTone === 'good'
                                              ? '#87c27a'
                                              : selectedPlanningExecutionTone === 'warn'
                                                ? '#e0a15e'
                                                : selectedPlanningExecutionTone === 'danger'
                                                  ? '#ff8b7a'
                                                  : '#9fb6d9'
                                          )
                                        : '#778195',
                                  }}
                                >
                                  Build
                                </span>
                              </div>
                              <div style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                {selectedPlanningExecutionSummary}
                              </div>
                            </div>

                            <MetaRow>
                              <MetaItem>
                                <MetaLabel>Latest state</MetaLabel>
                                <MetaValue $tone={selectedPlanningExecutionTone}>
                                  {selectedPlanningExecutionStage === 'planning'
                                    ? 'Planned'
                                    : selectedPlanningExecutionStage === 'in_progress'
                                      ? 'In progress'
                                      : latestApplyExecution
                                        ? formatApplyStatusLabel(latestApplyExecution.status)
                                        : 'Built'}
                                </MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Relevant ops</MetaLabel>
                                <MetaValue>{formatNumber(selectedPlanningExecutionOperationCount)}</MetaValue>
                              </MetaItem>
                              <MetaItem>
                                <MetaLabel>Apply path</MetaLabel>
                                <MetaValue $tone={applyEligibilityState.tone}>
                                  {applyEligibilityState.label}
                                </MetaValue>
                              </MetaItem>
                            </MetaRow>

                            {selectedPlanningGroup.key === 'collaborations' ? (
                              <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                Collaboration updates affect routing and ownership, not direct write operations.
                              </div>
                            ) : selectedPlanningApplyOperations.length ? (
                              <div style={{ display: 'grid', gap: 6 }}>
                                <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                  Latest build activity
                                </div>
                                <ScrollSurface>
                                  <div style={{ display: 'grid', gap: 10 }}>
                                    {selectedPlanningApplyOperations.map((operation, operationIndex) => (
                                      <div
                                        key={operation.applyOperationId}
                                        style={{
                                          display: 'grid',
                                          gap: 4,
                                          paddingTop: operationIndex ? 10 : 0,
                                          borderTop: operationIndex ? '1px dashed rgba(255,255,255,.12)' : 'none',
                                        }}
                                      >
                                        <MetaRow>
                                          <MetaItem>
                                            <MetaLabel>Status</MetaLabel>
                                            <MetaValue $tone={operation.status === 'succeeded' ? 'good' : operation.status === 'failed' ? 'danger' : operation.status === 'running' || operation.status === 'pending' ? 'warn' : 'info'}>
                                              {humanizeToken(operation.status)}
                                            </MetaValue>
                                          </MetaItem>
                                          <MetaItem>
                                            <MetaLabel>Action</MetaLabel>
                                            <MetaValue>{humanizeToken(operation.action)}</MetaValue>
                                          </MetaItem>
                                        </MetaRow>
                                        <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{operation.entityKey}</div>
                                        <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                          {operation.notes[0] || `Targets ${operation.targetSystem}.`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </ScrollSurface>
                              </div>
                            ) : selectedPlanningWriteOperations.length ? (
                              <div style={{ display: 'grid', gap: 6 }}>
                                <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                  Planned write preview
                                </div>
                                <ScrollSurface>
                                  <div style={{ display: 'grid', gap: 10 }}>
                                    {selectedPlanningWriteOperations.map((operation, operationIndex) => (
                                      <div
                                        key={operation.operationId}
                                        style={{
                                          display: 'grid',
                                          gap: 4,
                                          paddingTop: operationIndex ? 10 : 0,
                                          borderTop: operationIndex ? '1px dashed rgba(255,255,255,.12)' : 'none',
                                        }}
                                      >
                                        <MetaRow>
                                          <MetaItem>
                                            <MetaLabel>State</MetaLabel>
                                            <MetaValue $tone="info">Planned</MetaValue>
                                          </MetaItem>
                                          <MetaItem>
                                            <MetaLabel>Action</MetaLabel>
                                            <MetaValue>{humanizeToken(operation.action)}</MetaValue>
                                          </MetaItem>
                                        </MetaRow>
                                        <div style={{ color: '#f5f7fb', fontSize: 12, fontWeight: 900 }}>{operation.entityKey}</div>
                                        <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                          {operation.notes[0] || `Targets ${operation.targetSystem}.`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </ScrollSurface>
                              </div>
                            ) : (
                              <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                No direct build updates are currently attached to this planning group.
                              </div>
                            )}
                          </div>
                        </ExplorerDivider>
                      </ExplorerDetailScroll>
                    ) : (
                      <EmptyText>No planning group is available for this version.</EmptyText>
                    )}
                  </ExplorerDetail>
                </ExplorerShell>
              ) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No planning groups are available for this planner view.</div>}
            </Section>

            <Section>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShieldAlert size={16} />
                <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Validation signals</div>
              </div>
              <Eyebrow $tone={validationTone(plannerResult.validationResults)}>
                {humanizeToken(plannerResult.approvalGate.status)}
              </Eyebrow>
              <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                Validation shows the planner's warnings, failures, and gate reasons for this version. Human approval and apply actions are handled separately.
              </div>
              <Grid $cols="repeat(2, minmax(0, 1fr))">
                <Card>
                  <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Approval gate reasons</div>
                  {renderTextList(plannerResult.approvalGate.reasons, 'No approval gate reasons were returned.')}
                </Card>
                <Card>
                  <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Validation findings</div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {[...validationFailures, ...validationWarnings].length ? [...validationFailures, ...validationWarnings].slice(0, 6).map((entry) => (
                      <div key={`${entry.ruleName}:${entry.message}`} style={{ color: '#d0d7e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                        {entry.ruleName}: {entry.message}
                      </div>
                    )) : <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No warnings or failures returned.</div>}
                  </div>
                </Card>
              </Grid>
            </Section>

              */}
              {selectedRunSummary && !detailLoading && !plannerResult ? (
                <Section>
                  <div style={{ color: '#f5f7fb', fontSize: 16, fontWeight: 900 }}>Version detail unavailable</div>
                  <div style={{ color: '#cdd5e0', fontSize: 13, lineHeight: 1.6, fontWeight: 700 }}>
                    The selected version summary is loaded, but the full version detail could not be opened yet.
                  </div>
                </Section>
              ) : null}
                </>
              </motion.div>
            ) : null}
      </AnimatePresence>
      </Content>

      {typeof document !== 'undefined'
        ? createPortal(
            <AnimatePresence>
              {programStarterOpen ? (
                <>
                  <ProgramStarterBackdrop
                    onClick={() => setProgramStarterOpen(false)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  />
                  <ProgramStarterShell>
                    <ProgramStarterDialog
                      role="dialog"
                      aria-modal="true"
                      aria-label="Start Program"
                      initial={{ opacity: 0, y: 18, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 12, scale: 0.98 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <ProgramStarterHeader>
                        <div style={{ display: 'grid', gap: 4 }}>
                          <div style={{ color: '#f5f7fb', fontSize: 18, fontWeight: 900 }}>Start Program</div>
                          <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                            Choose one purchased offering. Programs are created and managed per offering.
                          </div>
                        </div>
                        
                      </ProgramStarterHeader>
                      <ProgramStarterBody>
                        <ProgramStarterRail>
                          {availableProgramStarterOfferings.map((entry) => {
                            const existingCount = programCardsByOfferingCode.get(entry.offeringCode)?.length ?? 0;
                            return (
                              <ProgramStarterOfferingButton
                                key={`${entry.serviceContractId}:${entry.offeringCode}`}
                                type="button"
                                $active={entry.offeringCode === selectedProgramStarterOffering?.offeringCode}
                                onClick={() => setProgramStarterOfferingCode(entry.offeringCode)}
                              >
                                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{entry.offeringName}</div>
                                <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.5, fontWeight: 700 }}>
                                  {existingCount ? `${existingCount} existing program${existingCount === 1 ? '' : 's'}` : 'No existing program'}
                                </div>
                              </ProgramStarterOfferingButton>
                            );
                          })}
                        </ProgramStarterRail>
                        <ProgramStarterDetail>
                          {selectedProgramStarterOffering ? (
                            <>
                              <div style={{ display: 'grid', gap: 4 }}>
                                <Eyebrow>{selectedProgramStarterOffering.serviceName || SERVICE_LABELS[serviceTab]}</Eyebrow>
                                <div style={{ color: '#f5f7fb', fontSize: 20, fontWeight: 900 }}>
                                  {selectedProgramStarterOffering.offeringName}
                                </div>
                                <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                  Offering code {selectedProgramStarterOffering.offeringCode}
                                </div>
                              </div>
                              <div style={{ display: 'grid', gap: 10 }}>
                                {offeringPlaybooksLoading ? (
                                  <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                    Loading offering guidance...
                                  </div>
                                ) : offeringPlaybooksError ? (
                                  <div style={{ color: '#d7b38a', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                    {offeringPlaybooksError}
                                  </div>
                                ) : selectedProgramStarterPlaybook ? (
                                  <div style={{ display: 'grid', gap: 10 }}>
                                    <div style={{ display: 'grid', gap: 4 }}>
                                      <div style={{ color: '#f5f7fb', fontSize: 14, fontWeight: 900 }}>
                                        {selectedProgramStarterPlaybook.title}
                                      </div>
                                      {selectedProgramStarterPlaybook.summary ? (
                                        <div style={{ color: '#cfd6e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                          {selectedProgramStarterPlaybook.summary}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div style={{ display: 'grid', gap: 10 }}>
                                      <div style={{ display: 'grid', gap: 4 }}>
                                        <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                          Typical Projects
                                        </div>
                                        {selectedProgramStarterPlaybook.exampleProjects.length ? (
                                          <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                            {selectedProgramStarterPlaybook.exampleProjects.join(' | ')}
                                          </div>
                                        ) : (
                                          <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                            No example projects are documented yet.
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ display: 'grid', gap: 4 }}>
                                        <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                          Industry Standards
                                        </div>
                                        {selectedProgramStarterPlaybook.industryStandards.length ? (
                                          <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                            {selectedProgramStarterPlaybook.industryStandards.join(' | ')}
                                          </div>
                                        ) : (
                                          <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                            No standards guidance is documented yet.
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ display: 'grid', gap: 4 }}>
                                        <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                          Typical Collaborators
                                        </div>
                                        {selectedProgramStarterPlaybook.collaborators.length ? (
                                          <div style={{ display: 'grid', gap: 8 }}>
                                            {selectedProgramStarterPlaybook.collaborators.map((collaborator, index) => (
                                              <div
                                                key={`offering-playbook-collaborator:${collaborator.serviceCode ?? collaborator.teamLabel ?? index}`}
                                                style={{ display: 'grid', gap: 2 }}
                                              >
                                                <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.5, fontWeight: 800 }}>
                                                  {collaborator.teamLabel || collaborator.serviceCode || 'Collaborating team'}
                                                </div>
                                                {collaborator.rationale ? (
                                                  <div style={{ color: '#cfd6e2', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                                    {collaborator.rationale}
                                                  </div>
                                                ) : null}
                                                {collaborator.exampleWork.length ? (
                                                  <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                                    Example work: {collaborator.exampleWork.join(' | ')}
                                                  </div>
                                                ) : null}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                            Primary team only.
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ display: 'grid', gap: 4 }}>
                                        <div style={{ color: '#8f9aae', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                                          Recommended Specialists
                                        </div>
                                        <div style={{ color: '#f5f7fb', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                          {selectedProgramStarterPlaybook.recommendedSpecialistLabels.length
                                            ? selectedProgramStarterPlaybook.recommendedSpecialistLabels.join(' | ')
                                            : 'No specialist guidance is documented yet.'}
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                      Updated {formatCompactDate(selectedProgramStarterPlaybook.updatedAt)}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                    No offering-level KBase process is available for this offering yet.
                                  </div>
                                )}
                              </div>
                              {selectedProgramStarterPrograms.length ? (
                                <div style={{ display: 'grid', gap: 10 }}>
                                  <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Existing Programs</div>
                                  <div style={{ display: 'grid', gap: 8 }}>
                                    {selectedProgramStarterPrograms.map((entry) => {
                                      const anchorRun = entry.currentDraftRun ?? entry.latestRun ?? entry.latestApprovedRun;
                                      const serviceFamilyLabel = getProgramServiceFamilyLabel(entry.program);
                                      const title = getProgramDisplayTitle(entry.program, serviceFamilyLabel, anchorRun);
                                      return (
                                        <Card key={`program-start:${entry.program.programId}`}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                            <div style={{ display: 'grid', gap: 4 }}>
                                              <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>{title}</div>
                                              <div style={{ color: '#9aa5b6', fontSize: 11, lineHeight: 1.6, fontWeight: 700 }}>
                                                {formatProgramTypeLabel(entry.program.programType)} | Updated {formatCompactDate(entry.program.updatedAt)}
                                              </div>
                                            </div>
                                            <Button
                                              type="button"
                                              onClick={() => {
                                                setProgramStarterOpen(false);
                                                openInitiative(
                                                  entry.program.programId,
                                                  entry.currentDraftRun?.runId
                                                    ?? entry.latestRun?.runId
                                                    ?? entry.latestApprovedRun?.runId
                                                    ?? null,
                                                );
                                              }}
                                            >
                                              Open Program
                                            </Button>
                                          </div>
                                        </Card>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : null}
                              {/* <div style={{ display: 'grid', gap: 6 }}>
                                <div style={{ color: '#f5f7fb', fontSize: 13, fontWeight: 900 }}>Start new Program</div>
                                <div style={{ color: '#9aa5b6', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                  {listViewStartAction
                                    ? 'The planner will use the current meeting context and create a new Program anchored to this offering.'
                                    : 'A kickoff or recurring meeting note is required before a new Program can be started from this offering.'}
                                </div>
                              </div> */}
                            </>
                          ) : (
                            <div style={{ color: '#9aa5b6', fontSize: 12, fontWeight: 700 }}>No purchased offering is available for this service contract.</div>
                          )}
                        </ProgramStarterDetail>
                      </ProgramStarterBody>
                      <ProgramStarterFooter>
                        <Button type="button" onClick={() => setProgramStarterOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          $primary
                          onClick={() => void handleProgramStarterRun()}
                          disabled={!selectedProgramStarterOffering || !listViewStartAction || running}
                        >
                          {running ? getPlanningActionProgressLabel('Start Program') : 'Start new Program'}
                        </Button>
                      </ProgramStarterFooter>
                    </ProgramStarterDialog>
                  </ProgramStarterShell>
                </>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}

      <PlannerDrawer
        open={drawerOpen}
        tab={drawerTab}
        onClose={() => setDrawerOpen(false)}
        onSelectTab={setDrawerTab}
        result={plannerResult}
        reviewHistory={reviewHistory}
        latestApply={latestApplyExecution}
        applyHistory={applyHistory}
        trigger={selectedRunTrigger}
        triggerHistory={triggerHistory}
        triggerEvents={selectedTriggerEvents}
        triggerEventsLoading={triggerEventsLoading}
        attentionItems={selectedRunAttentionItems}
        attentionEventsByItemId={attentionEventsByItemId}
        attentionEventsLoading={attentionEventsLoading}
        attentionActionLoading={attentionActionLoading}
        onAttentionAction={handleAttentionAction}
        teamLabelMap={teamLabelMap}
        serviceTab={serviceTab}
      />
    </Root>
  );
}
